/**
 * Central editor state with Zustand.
 * Manages: loaded beatmap, playback, tool mode, selection, undo/redo.
 * Persists user preferences (camera, speed, volumes, etc.) to localStorage.
 */

import { create } from 'zustand';
import type {
  Beatmap, Difficulty, Note, Slide, Obstacle,
  ToolMode, SnapDivision, PlaybackState, CameraView,
  FlashEvent, BookmarkEntry,
} from '../types/beatmap';
import { GRID_SCALE, DEFAULT_VR_CAM_NAMES } from '../types/beatmap';
import { newBeatProblem, placementProblem, SAME_BEAT_MS } from './noteBeatRules';
import { hasTimedObjects, rebeatDifficulties, rescaleDifficulties } from './bpmRescale';
import { onBeatTolerance } from '../components/three/halo';
import type { VrCamSlot } from '../types/beatmap';
import { clampToPlacementLimits, snapToGridLines, snapToGridLinesUnbounded, DEFAULT_GRID, PLACEMENT_LIMITS } from '../components/three/placementGeometry';
import { gameRailPoints } from '../components/three/gameRailLine';
import { newWallPosition, wallFileXY, normalizeWallRotation, CROUCH_MATCH_MS } from '../components/three/wallGeometry';
import type { WallKind } from '../components/three/tunnelToolModes';
import type { IntensityData } from '../heatmap/intensityEngine';
import type { PatternDifficultyReport } from '../api/patternDifficulty';
import { fetchPatternDifficulty } from '../api/patternDifficulty';
import defaultPrefs from '../config/defaultPrefs.json';
import type { DesignShape, DesignMesh, DesignPathNode, SavedShape } from '../types/designShape';
import { makeDesignShape, makeDefaultCustomPath, makeSavedShape, instantiateSavedShape, randomNeonColor, PRIMARY_MESH_ID } from '../types/designShape';
import type { KeyBinding, KeyCombo } from '../config/keybindings';
import { DEFAULT_KEYBINDINGS } from '../config/keybindings';
import { getApiBase } from '../api/apiBase';
import { listShapes, saveShape, deleteShape, setShapeVisibility } from '../api/shapes';


// Settings types + defaults now live in editorSettings.ts (extracted for
// modularity). Imported for local use here and re-exported so existing
// 'import { ... } from store/editorStore' calls keep working.
import {
  GridGuideType,
  ActiveView,
  FigureMode,
  RenderMode,
  MannequinSettings,
  ObstacleConfig,
  OBSTACLE_CONFIG_KEYS,
  ObstacleConfigKey,
  DEFAULT_OBSTACLE_CONFIGS,
  GameplaySettings,
  DEFAULT_MANNEQUIN,
  DEFAULT_GAMEPLAY,
  ParticleSettings,
  DEFAULT_PARTICLES,
  InterfaceSettings,
  DEFAULT_INTERFACE,
  migrateTypographyColors,
  PerfMode,
  PerformanceSettings,
  PerfPresetConfig,
  DEFAULT_PERFORMANCE,
  isSynthVrView,
} from './editorSettings';
// Synthesised editor SFX — reuse the Flow sound module (no duplication) so
// placing / deleting notes plays the same voices across every editor.
import { playPlaceSound, playDeleteSound } from '../editors/flow/flowSounds';
import type { AreaRange, AreaGesture, AreaFilters, AreaFilterKind, NoteFilterKind, AreaClipboard, AreaObjects, FlipAxis, PasteMode, RangeContents } from './areaSelection';
import {
  DEFAULT_AREA_FILTERS, effectiveAreaRange, nearBeat, objectsInRange, isEmptyContents, copyContents,
  removeContents, retimeObjects, objectsUpTo, insertObjects, flipObjects, countObjects, pasteObjects, PASTE_MODES,
  lightsInRange, removeLights, pasteLights, effectsInRange, removeEffects, pasteEffects,
  fillConflicts, fillBeats, noteFilterKind, inRange, msToBeat, flipNote, withCounts, resolveNoteConflicts,
} from './areaSelection';
import { rotateObjects, scaleObjects, pickRail, toolNoteKind, railPoints, withRailPoints } from './vrTools';
import { joinKind, playheadJoin, areaJoin } from './railJoin';
import { splitAtPlayhead } from './railSplit';
import { smoothRail, type SmoothingOptions } from './railSmoothing';
// Delete "explosion" particle burst + place "pop" stamps — rendered by TunnelScene.
import { emitNoteBurst, markPlaced, noteKey } from '../components/three/srBurst';
export * from './editorSettings';

/** Snap-grid subdivision family. `binary` = straight (…1/8, 1/4, 1/2),
 *  `ternary` = triplets (…1/6, 1/3, 2/3), `all` = both merged. Cycled with Tab. */
export type StepType = 'binary' | 'ternary' | 'all';

/** One wall's new X/Y for `moveObstacles`, in note space. Slides, squares and triangles are addressed
 *  by index in their own list; a crouch by its time, since the format stores crouches as times. */
export type ObstacleMove =
  | { kind: 'slide' | 'square' | 'triangle'; index: number; x: number; y: number }
  | { kind: 'crouch'; timeMs: number; x: number; y: number };
// No family steps coarser than one beat (SRVR-0094 AC8): mappers step through a chart by beat or
// finer, and a 2–8 beat step only skipped past objects.
const BINARY_STEPS = [0.015625, 0.03125, 0.0625, 0.125, 0.25, 0.5, 1];
// Triplet subdivisions of a beat: 1/48, 1/24, 1/12, 1/6, 1/3, 2/3, then the whole beat.
const TERNARY_STEPS = [1 / 48, 1 / 24, 1 / 12, 1 / 6, 1 / 3, 2 / 3, 1];
/** The coarsest step any family offers, in beats. */
export const MAX_SCROLL_STEP = 1;
export function stepsForType(type: StepType): number[] {
  if (type === 'ternary') return TERNARY_STEPS;
  if (type === 'all') return Array.from(new Set([...BINARY_STEPS, ...TERNARY_STEPS])).sort((a, b) => a - b);
  return BINARY_STEPS;
}

/** Performance mode: `auto` = the adaptive governor tunes draw distance by FPS;
 *  `performance`/`quality` force a short/long horizon (fewer/more objects);
 *  `potato` is the everything-off tier for weak machines (integrated/software
 *  GPUs). The type lives in editorSettings.ts next to the per-preset
 *  PerformanceSettings, whose DEFAULT_PERFORMANCE carries the values that used
 *  to be the PERF_MODE_BEATS / MODE_RENDER_SCALE / POTATO constants. */
export type { PerfMode } from './editorSettings';
function loadPerfMode(): PerfMode {
  try {
    const v = localStorage.getItem('synapse_perf_mode');
    if (v === 'auto' || v === 'performance' || v === 'quality' || v === 'potato') return v;
  } catch { /* ignore */ }
  return 'auto';
}

/** Which rail line a note is snapped onto. `smooth` and `game` are the two Rail Rendering shapes the scene
 *  draws, so a snapped note sits on the line the mapper sees; `legacy` is the older estimate Console keeps. */
export type RailShape = 'legacy' | 'smooth' | 'game';

/** The web editor's own rail line: what `RailLine` draws, a three.js `CatmullRomCurve3` (uniform, tension
 *  0.5, ends extended by reflection) through the head and every node. x, y and the beat are all cubic in the
 *  curve's parameter, so the parameter is found from the beat first, then x and y read at it. */
function _smoothTubeXYAtBeat(note: Note, beat: number, dec: (enc: number) => number): [number, number] | null {
  const P = [
    { b: note.time_beats, x: note.position[0], y: note.position[1] },
    ...note.rail_nodes!.map(n => ({ b: dec(n[2]), x: n[0], y: n[1] })),
  ];
  const n = P.length;
  if (n < 2 || beat < P[0].b - 1e-6 || beat > P[n - 1].b + 1e-6) return null;
  const hermite = (a: number, b: number, t0: number, t1: number, t: number) => {
    const c2 = -3 * a + 3 * b - 2 * t0 - t1, c3 = 2 * a - 2 * b + t0 + t1;
    return a + t0 * t + c2 * t * t + c3 * t * t * t;
  };
  const at = (i: number, k: 'b' | 'x' | 'y', t: number) => {
    const p1 = P[i][k], p2 = P[i + 1][k];
    const p0 = i === 0 ? 2 * p1 - p2 : P[i - 1][k];
    const p3 = i + 2 >= n ? 2 * p2 - p1 : P[i + 2][k];
    return hermite(p1, p2, 0.5 * (p2 - p0), 0.5 * (p3 - p1), t);
  };
  let i = 0;
  while (i < n - 2 && P[i + 1].b < beat) i++;
  let lo = 0, hi = 1;
  for (let it = 0; it < 40; it++) {                    // the beat rises along the curve: bisect on it
    const mid = (lo + hi) / 2;
    if (at(i, 'b', mid) < beat) lo = mid; else hi = mid;
  }
  const t = (lo + hi) / 2;
  return [at(i, 'x', t), at(i, 'y', t)];
}

/** The rail line the game and the desktop editor draw (Rail Rendering on, `gameRailPoints`): straight pieces
 *  between the points its smoother samples, which is the line Track.cs `GetSnapToRailPosition` reads. */
function _gameRailXYAtBeat(note: Note, beat: number, dec: (enc: number) => number): [number, number] | null {
  const pts = gameRailPoints(note);
  // The head sits on its own beat, as the scene places it.
  const bs = pts.map((p, i) => (i === 0 ? note.time_beats : dec(p[2])));
  if (pts.length < 2 || beat < bs[0] - 1e-6 || beat > bs[bs.length - 1] + 1e-6) return null;
  for (let i = 0; i + 1 < pts.length; i++) {
    if (beat <= bs[i + 1] + 1e-9) {
      const f = bs[i + 1] - bs[i] > 1e-9 ? Math.min(1, Math.max(0, (beat - bs[i]) / (bs[i + 1] - bs[i]))) : 0;
      return [pts[i][0] + f * (pts[i + 1][0] - pts[i][0]), pts[i][1] + f * (pts[i + 1][1] - pts[i][1])];
    }
  }
  const last = pts[pts.length - 1];
  return [last[0], last[1]];
}

/** The rail's interpolated [x, y] at a given chart beat (uniform Catmull-Rom over
 *  the head + nodes), or null if the note has no rail or the beat is outside its
 *  span. rail_nodes[2] is encoded time (timeMs * 0.02). `shape` picks the line (see RailShape). */
function _railXYAtBeat(note: Note, beat: number, bpm: number, shape: RailShape = 'legacy'): [number, number] | null {
  if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;
  const dec = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
  if (shape === 'smooth') return _smoothTubeXYAtBeat(note, beat, dec);
  if (shape === 'game') return _gameRailXYAtBeat(note, beat, dec);
  const ctrl = [
    { b: note.time_beats, x: note.position[0], y: note.position[1] },
    ...note.rail_nodes.map(n => ({ b: dec(n[2]), x: n[0], y: n[1] })),
  ].sort((a, b) => a.b - b.b);
  const start = ctrl[0].b, end = ctrl[ctrl.length - 1].b;
  if (beat < start - 1e-6 || beat > end + 1e-6) return null;
  const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
    const t2 = t * t, t3 = t2 * t;
    return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
  };
  let i = 0;
  while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
  const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
  const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
  const seg = Math.max(1e-6, p2.b - p1.b);
  const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
  return [cr(p0.x, p1.x, p2.x, p3.x, t), cr(p0.y, p1.y, p2.y, p3.y, t)];
}

const RAIL_SNAP_EPS = 1e-4;

/** The line snapping uses follows the Rail Rendering toggle: the game's shape when it is on, else the one the web draws. */
const _snapShape = (s: { gameRailRendering: boolean }): RailShape => (s.gameRailRendering ? 'game' : 'smooth');

/** The beat a rail's last node falls on (its head's beat when it has no nodes). */
function _railEndBeat(note: Note, bpm: number): number {
  let end = note.time_beats;
  for (const n of note.rail_nodes ?? []) end = Math.max(end, (n[2] / 0.02 / 1000) * (bpm / 60));
  return end;
}

/**
 * Where the rail concurrent with a note of `noteType` at `beat` passes, or null when none is — Track.cs
 * `GetSnapToRailPosition`. A rail of the same type is concurrent when its head is before the beat and
 * its last node at or after it; where rails overlap, the one that ends first wins, as the desktop
 * editor takes the first node after the note. `skipIndex` leaves out the note being moved.
 */
export function railSnapXY(
  notes: readonly Note[], noteType: string, beat: number, bpm: number, skipIndex = -1, shape: RailShape = 'smooth',
): [number, number] | null {
  let best: { end: number; xy: [number, number] } | null = null;
  notes.forEach((r, i) => {
    if (i === skipIndex || r.note_type !== noteType || !r.has_rail || !r.rail_nodes?.length) return;
    if (beat <= r.time_beats + RAIL_SNAP_EPS) return;
    const xy = _railXYAtBeat(r, beat, bpm, shape);
    if (!xy) return;
    const end = _railEndBeat(r, bpm);
    if (!best || end < best.end) best = { end, xy };
  });
  return best ? (best as { end: number; xy: [number, number] }).xy : null;
}

/**
 * Shift+R, Track.cs `SnapToSelectedRails`: every rail whose head is in the range (the playhead's beat when
 * there is no range) and whose note type is enabled pulls the notes of its type that fall on it onto its
 * line, keeping each note's beat. A rail head is never moved, and a rail that starts inside another rail of
 * its type is left to that one. Returns null when nothing moved.
 */
export function snapConcurrentNotesToRails(
  notes: readonly Note[], bpm: number, range: AreaRange, filters: AreaFilters, shape: RailShape = 'smooth',
): Note[] | null {
  const out = [...notes];
  const skipped = new Set<number>();
  let changed = false;
  const heads = notes.map((n, i) => i)
    .filter(i => notes[i].has_rail && !!notes[i].rail_nodes?.length && inRange(notes[i].time_beats, range) && filters[noteFilterKind(notes[i])])
    .sort((a, b) => notes[a].time_beats - notes[b].time_beats);
  for (const ri of heads) {
    if (skipped.has(ri)) continue;
    if (_snapConcurrentToRail(notes, out, ri, bpm, shape, skipped)) changed = true;
  }
  return changed ? out : null;
}

/** Moves the plain notes of rail `ri`'s type that fall on it (after its head, up to its last node) onto its
 *  line, writing into `out` and reading the rail from `src`. A rail head met on the way goes into `skipped`
 *  and stays put; `exclude` names notes to leave where they are. True when any note moved. */
function _snapConcurrentToRail(
  src: readonly Note[], out: Note[], ri: number, bpm: number, shape: RailShape, skipped?: Set<number>, exclude?: ReadonlySet<number>,
): boolean {
  const rail = src[ri];
  if (!rail?.has_rail || !rail.rail_nodes?.length) return false;
  const end = _railEndBeat(rail, bpm);
  let moved = false;
  src.forEach((n, i) => {
    if (i === ri || n.note_type !== rail.note_type || exclude?.has(i)) return;
    if (n.time_beats <= rail.time_beats + RAIL_SNAP_EPS || n.time_beats > end + RAIL_SNAP_EPS) return;
    if (n.has_rail) { skipped?.add(i); return; }
    const xy = _railXYAtBeat(rail, n.time_beats, bpm, shape);
    if (!xy) return;
    // Only a note that actually moves counts, so a second Shift+R over a snapped passage changes nothing.
    if (Math.abs(xy[0] - n.position[0]) < 1e-9 && Math.abs(xy[1] - n.position[1]) < 1e-9) return;
    out[i] = { ...out[i], position: [xy[0], xy[1], n.position[2]] };
    moved = true;
  });
  return moved;
}

/** After a rail head, node or whole rail has moved: with Snap to Rail on in Synth VR, the notes concurrent
 *  with each moved rail follow its new form (Track.cs `RenderLine` re-snaps them). Part of the move's own
 *  history entry, so it pushes none. The rails and the notes to leave alone are given as the note objects the
 *  edit wrote, not as indices: `setDiffNotes` re-sorts by time, so an index taken before it can name another
 *  note afterwards, while an object keeps its identity through the sort. */
function _resnapToRails(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void, get: () => EditorState,
  rails: Iterable<Note>, exclude?: ReadonlySet<Note>,
): void {
  const s = get();
  if (!s.railSnapping || !isSynthVrView(s.activeView) || !s.beatmap) return;
  const diff = s.beatmap.difficulties[s.activeDifficulty];
  if (!diff) return;
  const bpm = s.beatmap.bpm || 120;
  const out = [...diff.notes];
  const excluded = new Set<number>();
  if (exclude) diff.notes.forEach((n, i) => { if (exclude.has(n)) excluded.add(i); });
  let changed = false;
  const shape = _snapShape(s);
  for (const rail of rails) {
    const ri = diff.notes.indexOf(rail);
    if (ri >= 0 && _snapConcurrentToRail(diff.notes, out, ri, bpm, shape, undefined, excluded)) changed = true;
  }
  if (changed) setDiffNotes(set, get, out);
}

/** Console's Shift+R, unchanged: each selected note moves onto the nearest other rail covering its beat. */
function _snapSelectionToNearestRail(set: (fn: (s: EditorState) => Partial<EditorState>) => void, get: () => EditorState): void {
  const state = get();
  if (!state.beatmap || state.selectedNoteIds.length === 0) return;
  const diff = state.beatmap.difficulties[state.activeDifficulty];
  if (!diff) return;
  const bpm = state.beatmap.bpm || 120;
  const selSet = new Set(state.selectedNoteIds);
  let changed = false;
  const newNotes = diff.notes.map((n, idx) => {
    if (!selSet.has(idx)) return n;
    let best: { x: number; y: number; d: number } | null = null;
    diff.notes.forEach((r, ri) => {
      if (ri === idx || !r.has_rail) return;
      const xy = _railXYAtBeat(r, n.time_beats, bpm);
      if (!xy) return;
      const d = Math.hypot(xy[0] - n.position[0], xy[1] - n.position[1]);
      if (!best || d < best.d) best = { x: xy[0], y: xy[1], d };
    });
    if (!best) return n;
    changed = true;
    const b = best as { x: number; y: number; d: number };
    return { ...n, position: [b.x, b.y, n.position[2]] as [number, number, number] };
  });
  if (!changed) return;
  pushUndo(state, 'Snap to rail');
  setDiffNotes(set, get, newNotes);
}

// ── LocalStorage Persistence ─────────────────────────────────────────────────

const STORAGE_KEY = 'abm-editor-prefs';

interface PersistedPrefs {
  cameraView?: CameraView;
  minimapCam?: { height: number; posZ: number; tilt: number };
  /** Minimap beat-spacing multiplier (0.5–2) — higher spreads packed notes. */
  minimapZoom?: number;
  /** Last scrub position (seconds) — restored on refresh so you return to where
   *  you were editing. Seconds (not beats) so it's bpm/offset-independent. */
  lastTime?: number;
  playbackSpeed?: number;
  scrollStep?: number;
  snapDivision?: SnapDivision;
  musicVolume?: number;
  sfxVolume?: number;
  activeDifficulty?: string;
  timelineZoom?: number;
  noteJumpSpeed?: number;
  mapLocked?: boolean;
  leftSidebarOpen?: boolean;
  leftSidebarWidth?: number;
  rightSidebarOpen?: boolean;
  rightSidebarWidth?: number;
  bottomPanelOpen?: boolean;
  bottomPanelHeight?: number;
  showHumanFigure?: boolean;
  figureMode?: FigureMode;
  renderMode?: RenderMode;
  heatmapEnabled?: boolean;
  gameRailRendering?: boolean;
  patternDifficultyVisible?: boolean;
  fovPanelOpen?: boolean;
  customCameraPosition?: [number, number, number];
  customCameraRotation?: [number, number, number];
  vrCams?: VrCamSlot[];
  defaultCameraPosition?: [number, number, number];
  defaultCameraRotation?: [number, number, number];
  leftCameraPosition?: [number, number, number];
  leftCameraRotation?: [number, number, number];
  rightCameraPosition?: [number, number, number];
  rightCameraRotation?: [number, number, number];
  mannequinSettings?: MannequinSettings;
  gameplaySettings?: GameplaySettings;
  interfaceSettings?: InterfaceSettings;
  particleSettings?: ParticleSettings;
  /** Per-preset performance knobs (Potato/Perf/Auto/Quality) — user-editable
   *  from the Performance settings tab; defaults in DEFAULT_PERFORMANCE. */
  performanceSettings?: PerformanceSettings;
  lastBeatmapId?: string;
  // Per-editor last-opened map (VR vs Console are independent).
  lastBeatmapIdByEditor?: Partial<Record<'synth-rider' | 'console', string>>;
  // Grid settings
  gridVisible?: boolean;
  gridSize?: number;
  snapToGrid?: boolean;
  /** Snap to Rail (Alt+F3): notes placed or moved onto a concurrent rail land on its line. */
  railSnapping?: boolean;
  gridGuideType?: string;
  gridGuideVisible?: boolean;
  /** Halo strength on the objects on the grid while paused (SRVR-0314): 0 = off, 1–3 = level. */
  highlightLevel?: number;
  /** Before halo levels: the on/off flag. Read once, as level 0 or 1, when there is no level. */
  highlightingEnabled?: boolean;
  gridWidth?: number;
  gridHeight?: number;
  gridDivisionsH?: number;
  gridDivisionsV?: number;
  gridColor?: string;
  gridOpacity?: number;
  notesGridYOffset?: number;
  /** Set once the stored record has been through the grid-geometry migration (see loadPrefs). */
  gridGeometryVersion?: number;
  activeView?: ActiveView;
  // Last-write-wins clock for cross-browser server sync (epoch ms). Set by
  // savePrefs; compared on boot to decide whether the server copy is newer.
  _savedAt?: number;
}

// Shipped defaults (camera, colors, gameplay, particles, mannequin, interface,
// grid, …) captured from a configured editor. They're the BASELINE: anything
// the user has saved in this browser's localStorage overrides them. On a fresh
// origin (e.g. the deployed site, with no localStorage and no backend) these
// become the visible defaults instead of the bare built-ins.
function loadPrefs(): PersistedPrefs {
  const base = defaultPrefs as unknown as PersistedPrefs;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return migrateGridGeometry({ ...base, ...(JSON.parse(raw) as PersistedPrefs) });
  } catch { /* ignore parse errors */ }
  return { ...base, gridGeometryVersion: GRID_GEOMETRY_VERSION };
}

/**
 * One-time reset of the grid to the desktop editor's. Earlier builds shipped a 3 × 2.25 grid drawn
 * `notesGridYOffset: 0.2` above the notes' origin; a stored record keeps those values forever, so
 * every existing user would keep a grid whose centre is off and whose cells (0.1374 note units, not
 * the desktop's 0.1365) drift away from the intersections a desktop map's notes sit on.
 * A record without the current marker gets width, height, divisions and offset reset once, and the
 * marker written back immediately; a record with the marker is left alone, so a value the user sets
 * afterwards survives. A server copy adopted on boot lands in local storage and comes through here on
 * the reload (SRVR-0307).
 *
 * The steps run in order, each once: below version 2 the grid is reset (version 1 reset only the
 * offset); below version 3 each wall type's Pos X / Pos Y, Scale X / Y and Rotation are reset to the
 * desktop prefab's, because Pos X / Pos Y changed meaning — they used to replace a wall's position and
 * are now an offset added to it — so a saved value no longer puts the wall where it was meant to go.
 * Colours and Scale Z are the user's and are never touched.
 */
const GRID_GEOMETRY_VERSION = 3;
const GRID_RESET: Partial<PersistedPrefs> = {
  gridWidth: DEFAULT_GRID.gridWidth,
  gridHeight: DEFAULT_GRID.gridHeight,
  gridDivisionsH: DEFAULT_GRID.gridDivisionsH,
  gridDivisionsV: DEFAULT_GRID.gridDivisionsV,
  notesGridYOffset: DEFAULT_GRID.notesGridYOffset,
};
const WALL_GEOMETRY_FIELDS = ['defaultX', 'defaultY', 'scaleX', 'scaleY', 'rotation'] as const;

/** Reset the wall-geometry fields of every stored obstacle configuration; nothing else in them. */
function resetWallGeometry(gp: PersistedPrefs['gameplaySettings']): PersistedPrefs['gameplaySettings'] {
  if (!gp || !gp.obstacleConfigs) return gp;
  const configs: Partial<Record<ObstacleConfigKey, ObstacleConfig>> = { ...gp.obstacleConfigs };
  for (const key of Object.keys(DEFAULT_OBSTACLE_CONFIGS) as ObstacleConfigKey[]) {
    const saved = configs[key];
    if (!saved) continue;
    const next: ObstacleConfig = { ...saved };
    for (const f of WALL_GEOMETRY_FIELDS) next[f] = DEFAULT_OBSTACLE_CONFIGS[key][f] as never;
    configs[key] = next;
  }
  return { ...gp, obstacleConfigs: configs as typeof gp.obstacleConfigs };
}

function applyGeometrySteps(record: PersistedPrefs, from: number): PersistedPrefs {
  let next: PersistedPrefs = { ...record };
  if (from < 2) next = { ...next, ...GRID_RESET };
  if (from < 3 && next.gameplaySettings) next = { ...next, gameplaySettings: resetWallGeometry(next.gameplaySettings) };
  return { ...next, gridGeometryVersion: GRID_GEOMETRY_VERSION };
}

function migrateGridGeometry(prefs: PersistedPrefs): PersistedPrefs {
  const from = prefs.gridGeometryVersion ?? 0;
  if (from >= GRID_GEOMETRY_VERSION) return prefs;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as PersistedPrefs) : {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify(applyGeometrySteps(stored, from)));
  } catch { /* storage unavailable: the reset still applies to this session */ }
  return applyGeometrySteps(prefs, from);
}

// ── Server-side prefs sync ──────────────────────────────────────────────────
//
// We persist a copy of the localStorage prefs on the backend so they survive:
//   - the frontend running on a different port (each origin has its own LS)
//   - the user switching browsers / clearing site data
//   - first-time setup on a new machine that already runs the backend
//
// Strategy:
//   1. On boot, if localStorage is EMPTY, fetch from server and hydrate.
//      The fetch is async — we trigger a one-time reload after writing so
//      the synchronous store init picks up the new values.
//   2. On every savePrefs call, debounce and POST to /api/prefs.

const API_BASE = getApiBase();
const SERVER_SYNC_DEBOUNCE_MS = 1500;
let _serverSyncTimer: ReturnType<typeof setTimeout> | null = null;
let _lastSentJson = ''; // avoid pushing identical bodies repeatedly

function pushPrefsToServer(serialized: string) {
  if (_serverSyncTimer) clearTimeout(_serverSyncTimer);
  _serverSyncTimer = setTimeout(() => {
    if (serialized === _lastSentJson) return;
    fetch(`${API_BASE}/api/prefs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prefs: serialized }),
    }).then(res => {
      if (res.ok) _lastSentJson = serialized;
    }).catch(() => { /* offline / backend down — try again next save */ });
  }, SERVER_SYNC_DEBOUNCE_MS);
}

function savePrefs(prefs: PersistedPrefs) {
  let serialized = '';
  try {
    // Stamp a save clock so cross-browser sync can pick the most recent copy.
    serialized = JSON.stringify({ ...prefs, _savedAt: Date.now() });
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch { /* ignore quota errors */ }
  if (serialized) pushPrefsToServer(serialized);
}

// ── Flash-event draft (survives a browser refresh) ──────────────────────────
// SynthVR edits otherwise live only in memory: on refresh the map reloads from
// the backend (the original .synth, where a light has no strobe fields), so the
// frequency/repeat/duration would be lost. Mirror the per-map flash events to
// localStorage and restore them on load.
const FLASH_DRAFT_KEY = 'synapse_flash_drafts_v1';

function loadFlashDrafts(): Record<string, FlashEvent[]> {
  try { return JSON.parse(localStorage.getItem(FLASH_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadFlashDraft(id: string | null | undefined): FlashEvent[] | null {
  if (!id) return null;
  const all = loadFlashDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveFlashDraft(id: string | null | undefined, events: FlashEvent[]) {
  if (!id) return;
  try {
    const all = loadFlashDrafts();
    all[id] = events;
    localStorage.setItem(FLASH_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// ── Flash-effect draft ──────────────────────────────────────────────────────
// Same reason as the flash-event draft: the map reloads from the backend on a refresh, so the effects
// edited here are mirrored per map and restored on load. The value is the whole {difficulty: beats} object.
const EFFECT_DRAFT_KEY = 'synapse_effect_drafts_v1';

function loadEffectDraft(id: string | null | undefined): Record<string, number[]> | null {
  if (!id) return null;
  try {
    const all = JSON.parse(localStorage.getItem(EFFECT_DRAFT_KEY) || '{}');
    return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
  } catch { return null; }
}
function saveEffectDraft(id: string | null | undefined, effects: Record<string, number[]>) {
  if (!id) return;
  try {
    const all = JSON.parse(localStorage.getItem(EFFECT_DRAFT_KEY) || '{}');
    all[id] = effects;
    localStorage.setItem(EFFECT_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// Earliest beat across a copied notes+walls pair — the paste anchor. Kept as one
// helper so plain paste and mirror paste can never drift apart.
function _clipboardMinBeat(notes: Note[], slides: Slide[]): number {
  let min = Infinity;
  for (const n of notes) if (n.time_beats < min) min = n.time_beats;
  for (const s of slides) if (s.time_beats < min) min = s.time_beats;
  return Number.isFinite(min) ? min : 0;
}

// Clearing an area range keeps it for Shift+Z to recall (SRVR-0316 AC6).
function _areaCleared(s: { areaRange: AreaRange | null }): { areaRange: null; areaGesture: null; areaRecall?: AreaRange } {
  return { areaRange: null, areaGesture: null, ...(s.areaRange ? { areaRecall: s.areaRange } : {}) };
}

// The range an area operation acts on: the range in force, else the playhead's beat (Track.cs treats
// "no selection" as the current beat).
function _areaTarget(state: EditorState) {
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  if (!state.beatmap || !diff) return null;
  const p = state.playback.currentBeat;
  const range = effectiveAreaRange(state.areaRange, state.areaGesture, p) ?? { startBeat: p, endBeat: p };
  return { state, diff, range, bpm: state.beatmap.bpm || 120 };
}

// ── Tools box helpers (SRVR-0352 – SRVR-0354) ──

/** The parameters Rail Smoothing's controls give; the store adds the map's BPM. */
export type RailSmoothingParams = Omit<SmoothingOptions, 'bpm'>;

/** An open preview: the difficulty it started from, and the undo entry it pushed for itself. */
interface ToolSession {
  kind: 'rotate' | 'scale' | 'smooth';
  base: Difficulty;
  beatmapId: string;
  diffName: string;
  beat: number;
  entry: HistoryEntry | null;
  prevRedo: HistoryEntry[];
  railIndex?: number;
}
let _toolSession: ToolSession | null = null;

/** The tools are Synth VR's; another view, or no map, leaves them inert. Playing is refused by the
 *  map-edit guard (MAP_EDIT_ACTIONS). */
function _toolsAllowed(s: EditorState): boolean {
  return !!s.beatmap && isSynthVrView(s.activeView) && !!s.beatmap.difficulties[s.activeDifficulty];
}

function _openToolSession(kind: ToolSession['kind'], s: EditorState, base: Difficulty, label: string, railIndex?: number) {
  const prevRedo = s.redoStack;
  pushUndo(s, label);
  _toolSession = {
    kind, base, beatmapId: s.beatmap!.id, diffName: s.activeDifficulty, beat: s.playback.currentBeat,
    entry: _lastUndoPush?.entry ?? null, prevRedo, railIndex,
  };
}

/** Synth VR's split (`snip` false, SRVR-0361) and snip (`snip` true, SRVR-0362) at the playhead. */
function _vrSplit(
  state: EditorState, set: (fn: (s: EditorState) => Partial<EditorState>) => void, get: () => EditorState, snip: boolean,
): void {
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  if (!state.beatmap || !diff) return;
  const bpm = state.beatmap.bpm || 120;
  const tolMs = onBeatTolerance(state.activeView, state.scrollStep, bpm) * (60000 / bpm);
  const result = splitAtPlayhead(diff.notes, state.beatToTimeMs(state.playback.currentBeat), tolMs,
    joinKind(state.toolMode, state.lastNoteKind), bpm, snip);
  if ('refused' in result) { if (result.refused) _noteRuleHandler?.(result.refused); return; }
  pushUndo(state, snip ? 'Snip rail' : 'Split rail');
  if (!setDiffNotes(set, get, result.notes)) return;
  // The new rail shifts array indices; nothing should keep pointing at a note that moved.
  set(() => ({ selectedRailNode: null, ...(snip ? {} : { selectedNoteIds: [] }) }));
}

/** Rotation turns notes and rails only (walls keep their own rotate controls). */
function _notesOnly(c: RangeContents): RangeContents {
  return { notes: c.notes, slides: [], crouches: [], squares: [], triangles: [] };
}

/** The rail Rail Smoothing works on: its head on the playhead beat, preferring the current tool's hand. */
function _railOnPlayhead(s: EditorState, diff: Difficulty): number | null {
  const bpm = s.beatmap?.bpm || 120;
  return pickRail(diff.notes, s.playback.currentBeat, onBeatTolerance(s.activeView, s.scrollStep, bpm), toolNoteKind(s.toolMode));
}

function _smoothedDiff(diff: Difficulty, index: number, opts: RailSmoothingParams, bpm: number): Difficulty {
  const notes = diff.notes.slice();
  notes[index] = withRailPoints(diff.notes[index], smoothRail(railPoints(diff.notes[index]), { ...opts, bpm }));
  return { ...diff, notes };
}

function _areaClip(ctx: { range: AreaRange }, objs: AreaObjects, lights: FlashEvent[], effects: number[]): AreaClipboard {
  return { ...objs, lights: JSON.parse(JSON.stringify(lights)), effects: [...effects], anchorBeat: ctx.range.startBeat, lengthBeats: ctx.range.endBeat - ctx.range.startBeat };
}

// The lights an area operation writes: the shared list (every difficulty sees it), saved to the
// per-map draft as every other light edit is.
function _areaLights(state: EditorState, next: FlashEvent[]): Partial<EditorState> {
  saveFlashDraft(state.beatmap?.id, next);
  return { flashEvents: next };
}

// The active difficulty's effects an area operation writes, saved to the per-map draft like every other effect edit.
function _areaEffects(state: EditorState, next: number[]): Partial<EditorState> {
  const effects = { ...state.effects, [state.activeDifficulty]: next };
  saveEffectDraft(state.beatmap?.id, effects);
  return { effects };
}
const _activeEffects = (s: EditorState): number[] => s.effects[s.activeDifficulty] ?? [];

// Write a whole difficulty from an area operation. The arrays are rebuilt, so every index-based
// reference into them is dropped rather than left pointing at another object (SRVR-0081).
/**
 * A slide array rebuilt without an index remap leaves a slide selection pointing at the wrong walls, so
 * the selection is dropped. Under an area range it is not stale: the range subscription has just
 * rewritten it from the new array, and clearing it again would deselect the very walls the range
 * holds (a wall placed or deleted inside a range would unselect the rest).
 */
function _staleSlideSelectionCleared(s: EditorState): Partial<EditorState> {
  // Synth VR only: a range left set when the view changes must not change how another view clears.
  if (isSynthVrView(s.activeView) && effectiveAreaRange(s.areaRange, s.areaGesture, s.playback.currentBeat)) return {};
  return s.selectedSlideIds.length ? { selectedSlideIds: [] } : {};
}

function _writeAreaDiff(
  set: (p: Partial<EditorState>) => void, get: () => EditorState, next: Difficulty, extra: Partial<EditorState>,
) {
  const state = get();
  if (!state.beatmap) return;
  set({
    beatmap: { ...state.beatmap, difficulties: { ...state.beatmap.difficulties, [state.activeDifficulty]: next } },
    selectedNoteIds: [], selectedSlideIds: [], selectedRailNode: null, hoveredNoteIndex: null,
    ...(state.railPlacementActive ? { railPlacementActive: false, railPlacementNoteIndex: null } : {}),
    ...extra,
  });
}

// The song's last beat in the chart frame: from the audio's duration when it is known, else from the
// difficulty's own duration. Select-all's range ends here and a paste never lands past it.
function _songEndBeat(s: { playback: { duration: number }; beatmap: { bpm: number; offset?: number } | null }, diff: Difficulty): number {
  const bpm = s.beatmap?.bpm || 120;
  if (s.playback.duration > 0) return Math.max(0, ((s.playback.duration - (s.beatmap?.offset || 0) / 1000) / 60) * bpm);
  return diff.duration_beats || 0;
}

// Select-all's range end: the song's end, or the last object's beat if that is later — the song's
// length is unknown until its audio loads, and a range that stopped short would deselect what
// select-all just selected (the range selects its contents, SRVR-0315 AC12; SRVR-0316 AC7).
function _selectAllEndBeat(s: EditorState, diff: Difficulty): number {
  const bpm = s.beatmap?.bpm || 120;
  let last = _songEndBeat(s, diff);
  for (const n of diff.notes) last = Math.max(last, n.time_beats);
  for (const sl of diff.slides ?? []) last = Math.max(last, sl.time_beats);
  for (const o of [...(diff.squares ?? []), ...(diff.triangles ?? [])]) last = Math.max(last, o.time_beats ?? 0);
  for (const ms of diff.crouches ?? []) last = Math.max(last, (ms / 60000) * bpm);
  for (const e of s.flashEvents) last = Math.max(last, e.time_beats);
  return last;
}

// The crouch_obstacles entry (the crouch's file position) that belongs to the crouch at `timeMs`,
// or -1. The crouch list holds times only; the positions ride alongside (see wallGeometry).
function _crouchObstacleIndex(obstacles: Obstacle[] | undefined, timeMs: number): number {
  if (!obstacles) return -1;
  return obstacles.findIndex((o) => Math.abs(o.time_ms - timeMs) <= CROUCH_MATCH_MS);
}

// Two walls are on the same beat when their chart times are this close — the window addPlacedSlide
// already uses. The desktop editor compares beats exactly; the web stores walls by time_ms.
const WALL_BEAT_MS = 2;

// Whether a wall's file position is the format centre a new wall is given (SRVR-0312). No position
// counts as the centre: it is where such a wall is drawn.
function _atCentre(position: number[] | null | undefined): boolean {
  if (!position || position.length < 2) return true;
  const [cx, cy] = newWallPosition(0);
  return Math.abs(position[0] - cx) < 1e-4 && Math.abs(position[1] - cy) < 1e-4;
}

// Side-locked wall types swap when mirrored; centre/crouch are symmetric.
const _MIRROR_WALL_TYPE: Record<string, string> = {
  wall_left: 'wall_right', wall_right: 'wall_left',
  angle_left: 'angle_right', angle_right: 'angle_left',
};

// Keybinding default migrations. Saved bindings normally win over defaults, but
// when a default changed *because the old one was the bug*, keeping the saved copy
// would ship the bug back to the people who reported it. Bump the version and list
// the affected ids together; only those are reset, all other customisations stay.
const KEYBINDINGS_VERSION_KEY = 'abm-keybindings-version';
// v3: A/D beat-step direction swapped per reviewer confirmation — D = finer
// subdivision, A = coarser (toward a whole step). Same ids as v2, so the reset
// list already covers them; only the version needs the bump.
const KEYBINDINGS_VERSION = 3;
const KEYBINDING_MIGRATED_IDS = new Set<string>([
  // v2: A/D now step the snap grid, and playback speed moved to Shift+Arrow so it
  // stops being shadowed by the identical beat-step arrow binding. paste_mirror
  // moved off Alt+V, which Windows browsers swallow into the menu bar.
  'beat_step_down', 'beat_step_up', 'playback_speed_down', 'playback_speed_up',
  'paste_mirror',
]);

// Design walls are a purely-decorative layer with no backend representation, so
// (like flash events) they live entirely in a per-map localStorage draft.
const DESIGN_DRAFT_KEY = 'synapse_decor_drafts_v1';
function loadDesignDrafts(): Record<string, DesignShape[]> {
  try { return JSON.parse(localStorage.getItem(DESIGN_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadDesignDraft(id: string | null | undefined): DesignShape[] | null {
  if (!id) return null;
  const all = loadDesignDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveDesignDraft(id: string | null | undefined, groups: DesignShape[]) {
  if (!id) return;
  try {
    const all = loadDesignDrafts();
    all[id] = groups;
    localStorage.setItem(DESIGN_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// ── Wall art LIBRARY (cross-project, like the old pattern library) ─────────────
// Saved wall arts the user liked — reusable in ANY project. A flat global list in
// localStorage holds only the PRE-FEATURE local library, read once as the
// migration source. The live library is server-backed and never written back
// to localStorage (a shared browser must not cache one user's shapes).
const DESIGN_LIB_KEY = 'synapse_decor_library_v1';
function loadDesignLibrary(): SavedShape[] {
  try {
    const arr = JSON.parse(localStorage.getItem(DESIGN_LIB_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
// One-time migration flag: once the localStorage library has been uploaded to
// the server (as the user's private shapes), we never re-run the upload.
const DESIGN_LIB_MIGRATED_KEY = 'synapse_decor_migrated_v1';

// Bookmarks have the same problem as flash events: a refresh reloads the backend
// .synth (which may not have the new/renamed bookmarks), so mirror them to
// localStorage per map and restore on load.
const BOOKMARK_DRAFT_KEY = 'synapse_bookmark_drafts_v1';
function loadBookmarkDrafts(): Record<string, BookmarkEntry[]> {
  try { return JSON.parse(localStorage.getItem(BOOKMARK_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadBookmarkDraft(id: string | null | undefined): BookmarkEntry[] | null {
  if (!id) return null;
  const all = loadBookmarkDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveBookmarkDraft(id: string | null | undefined, bookmarks: BookmarkEntry[]) {
  if (!id) return;
  try {
    const all = loadBookmarkDrafts();
    all[id] = bookmarks;
    localStorage.setItem(BOOKMARK_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// Song info the mapper sets by hand (BPM, offset, name, author) has the same problem: `updateMeta` only
// changed memory, so a refresh reloaded the backend .synth and the BPM went back to the one detected when
// the map was created. Mirror exactly what the mapper set to a per-map draft and restore it on load, so the
// BPM only ever changes when the mapper changes it. Synth VR only.
type MetaDraft = Partial<Pick<Beatmap, 'name' | 'author' | 'beatmapper' | 'bpm' | 'offset' | 'draft_mode' | 'custom_difficulty_name' | 'custom_difficulty_speed'>>;
const META_DRAFT_KEY = 'synapse_meta_drafts_v1';
function loadMetaDrafts(): Record<string, MetaDraft> {
  try { return JSON.parse(localStorage.getItem(META_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadMetaDraft(id: string | null | undefined): MetaDraft | null {
  if (!id) return null;
  const all = loadMetaDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveMetaDraft(id: string | null | undefined, meta: MetaDraft) {
  if (!id) return;
  try {
    const all = loadMetaDrafts();
    all[id] = { ...all[id], ...meta };
    localStorage.setItem(META_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

// Note edits (add/delete/move/paste, clearing a difficulty, undo/redo, …) live
// only in memory; a refresh reloads the backend .synth (the original), so the
// edits — including *deletions* — would be lost. Mirror the edited difficulties
// to a per-map localStorage draft and restore them on load, exactly like flash
// events / decor / bookmarks. Keyed by the (content-derived) beatmap id.
const DIFFICULTY_DRAFT_KEY = 'synapse_difficulty_drafts_v1';
function loadDifficultyDrafts(): Record<string, Record<string, Difficulty>> {
  try { return JSON.parse(localStorage.getItem(DIFFICULTY_DRAFT_KEY) || '{}'); }
  catch { return {}; }
}
function loadDifficultyDraft(id: string | null | undefined): Record<string, Difficulty> | null {
  if (!id) return null;
  const all = loadDifficultyDrafts();
  return Object.prototype.hasOwnProperty.call(all, id) ? all[id] : null;
}
function saveDifficultyDraft(id: string | null | undefined, difficulties: Record<string, Difficulty>) {
  if (!id) return;
  try {
    const all = loadDifficultyDrafts();
    all[id] = difficulties;
    localStorage.setItem(DIFFICULTY_DRAFT_KEY, JSON.stringify(all));
  } catch { /* quota — ignore */ }
}

/**
 * Server prefs sync on boot — DELIBERATELY non-destructive.
 *
 * If THIS browser already has a local prefs copy, it is the source of truth and
 * we NEVER overwrite it from the server; we only back our copy up. This prevents
 * a concurrent context that shares the same backend (a second tab, another
 * browser left open, a dev preview on a different port) from clobbering your
 * settings on reload — which manifested as "my settings keep reverting".
 *
 * Only when localStorage is EMPTY (first run in this browser / new machine /
 * different port) do we hydrate from the server and reload once so the
 * synchronous store init picks the values up.
 */
function syncPrefsWithServer() {
  let localRaw: string | null = null;
  try { localRaw = localStorage.getItem(STORAGE_KEY); } catch { return; }

  // Have local prefs → we own them. Just push a backup; don't ever adopt.
  if (localRaw) {
    pushPrefsToServer(localRaw);
    return;
  }

  // Empty localStorage → first run here: hydrate from the server if it has a copy.
  fetch(`${API_BASE}/api/prefs`)
    .then(res => res.ok ? res.json() : null)
    .then(payload => {
      const serverStr = payload && typeof payload.prefs === 'string' ? payload.prefs as string : null;
      if (!serverStr) return;
      try {
        localStorage.setItem(STORAGE_KEY, serverStr);
        // eslint-disable-next-line no-console
        console.info('[prefs] Hydrated settings from backend (first run), reloading…');
        window.location.reload();
      } catch { /* quota — ignore */ }
    })
    .catch(() => { /* backend offline — keep local */ });
}

if (typeof window !== 'undefined') {
  syncPrefsWithServer();
}

const savedPrefs = loadPrefs();

// ── Sanitize persisted gameplaySettings (fill missing fields in nested obstacleConfigs) ──

function sanitizeGameplaySettings(saved?: Partial<GameplaySettings>): GameplaySettings {
  const merged = { ...DEFAULT_GAMEPLAY, ...saved };
  // Deep-merge each obstacle config so new fields (e.g. scaleZ) get defaults
  if (saved?.obstacleConfigs) {
    const sanitized: Record<ObstacleConfigKey, ObstacleConfig> = { ...DEFAULT_OBSTACLE_CONFIGS };
    for (const key of OBSTACLE_CONFIG_KEYS) {
      sanitized[key] = { ...DEFAULT_OBSTACLE_CONFIGS[key], ...saved.obstacleConfigs[key] };
    }
    merged.obstacleConfigs = sanitized;
  }
  return merged;
}

/** Deep-merge saved per-preset performance settings over the defaults, per
 *  preset AND per flowFx, so a blob from an older build gets new knobs filled
 *  in. Explicit nulls are meaningful ("adaptive") and survive; a WRONG-TYPED
 *  value falls back to the default — these numbers feed the render budget
 *  directly (setManualNoteView etc.), so garbage must not pass. */
function sanitizePerformanceSettings(saved?: Partial<PerformanceSettings>): PerformanceSettings {
  const num = (v: unknown, def: number | null): number | null =>
    v === null ? null : (typeof v === 'number' && Number.isFinite(v) ? v : def);
  const bool = (v: unknown, def: boolean): boolean => (typeof v === 'boolean' ? v : def);
  const out = {} as PerformanceSettings;
  for (const mode of Object.keys(DEFAULT_PERFORMANCE) as PerfMode[]) {
    const def = DEFAULT_PERFORMANCE[mode];
    const s: Partial<PerfPresetConfig> = (saved?.[mode] && typeof saved[mode] === 'object') ? saved[mode]! : {};
    const fx = (s.flowFx && typeof s.flowFx === 'object') ? s.flowFx : ({} as Partial<typeof def.flowFx>);
    out[mode] = {
      drawDistanceBeats: num(s.drawDistanceBeats, def.drawDistanceBeats),
      renderScale: num(s.renderScale, def.renderScale),
      ladderSeed: num(s.ladderSeed, def.ladderSeed),
      fpsCap: num(s.fpsCap, def.fpsCap) ?? def.fpsCap,
      dprCap: num(s.dprCap, def.dprCap),
      antialias: bool(s.antialias, def.antialias),
      flowFx: {
        sparks: bool(fx.sparks, def.flowFx.sparks),
        waveformSprite: bool(fx.waveformSprite, def.flowFx.waveformSprite),
        railSweep: bool(fx.railSweep, def.flowFx.railSweep),
        beatChipsAllBeats: bool(fx.beatChipsAllBeats, def.flowFx.beatChipsAllBeats),
        minimapLiveDuringPlay: bool(fx.minimapLiveDuringPlay, def.flowFx.minimapLiveDuringPlay),
      },
    };
  }
  return out;
}

// ── Undo/Redo ───────────────────────────────────────────────────────────────

interface HistoryEntry {
  label: string;
  difficulties: Record<string, Difficulty>;
  /** The lights. Synth VR records them on every entry (light and effect edits are entries of their own);
   *  the other views only on an entry whose action changed them (area operations, SRVR-0319 AC12), since
   *  there lights are placed without an entry and restoring them on every undo would drop those. */
  flashEvents?: FlashEvent[];
  /** The flash effects, per difficulty, present exactly when `flashEvents` is. */
  effects?: Record<string, number[]>;
}

// ── Store Shape ─────────────────────────────────────────────────────────────

// A per-editor snapshot of the loaded map, so VR and Console keep independent
// maps even though they share this store + the SynthRiderApp shell.
type EditorKey = 'synth-rider' | 'console';
interface MapSlot {
  beatmap: Beatmap | null;
  lastBeatmapId: string | null;
  activeDifficulty: string;
  bookmarks: BookmarkEntry[];
  flashEvents: FlashEvent[];
  effects: Record<string, number[]>;
}

interface EditorState {
  beatmap: Beatmap | null;
  lastBeatmapId: string | null;
  activeDifficulty: string;
  // ── Shapes (decorative wall layer — see types/designShape.ts) ──
  designShapes: DesignShape[];
  selectedDesignShapeIds: string[];   // multi-select (box-select marks many; click marks one)
  // Which shape (primary or a part, by id — see PRIMARY_MESH_ID) is being
  // edited right now, so the 3D scene knows where to put the move-gizmo.
  activeDesignMeshId: string | null;
  // Which control node of a `custom` curve is selected (its drag-gizmo shows in
  // the scene); null = none. Reset whenever the selection/active shape changes.
  activeDesignNodeIndex: number | null;
  shapeDesignerOpen: boolean;
  designClipboard: DesignShape | null;
  designUndoStack: DesignShape[][];
  designRedoStack: DesignShape[][];
  // Cross-project saved wall arts ("My Library" tab) — persisted globally.
  designLibrary: SavedShape[];
  loadDesignLibraryFromServer: () => Promise<void>;
  addToDesignLibrary: (groupId: string) => void;
  removeFromDesignLibrary: (id: string) => void;
  setLibraryShapeVisibility: (id: string, isPublic: boolean) => Promise<void>;
  addLibraryArtToMap: (id: string) => void;
  addDesignShape: () => void;
  updateDesignShape: (id: string, patch: Partial<DesignShape>, force?: boolean) => void;
  updateDesignShapes: (ids: string[], patch: Partial<DesignShape>, force?: boolean) => void;
  updateDesignMesh: (groupId: string, partId: string, patch: Partial<DesignMesh>, force?: boolean) => void;
  // Custom-curve control nodes (edited via the scene gizmo + the panel node list).
  updateDesignNode: (groupId: string, index: number, patch: Partial<DesignPathNode>, force?: boolean) => void;
  addDesignNode: (groupId: string) => void;
  removeDesignNode: (groupId: string, index: number) => void;
  setActiveDesignNodeIndex: (index: number | null) => void;
  removeDesignShape: (id: string) => void;
  removeDesignShapes: (ids: string[]) => void;
  duplicateDesignShape: (id: string, name?: string) => void;
  selectDesignShape: (id: string | null) => void;
  setSelectedDesignShapeIds: (ids: string[]) => void;
  setActiveDesignMeshId: (id: string | null) => void;
  setShapeDesignerOpen: (open: boolean) => void;
  copyDesignShape: () => void;
  pasteDesignShape: () => void;
  undoDesign: () => void;
  redoDesign: () => void;
  // Per-editor map isolation. `mapOwner` = which editor the LIVE beatmap belongs
  // to; `mapSlots` stashes each editor's map when you switch away; the per-editor
  // last id lets a refresh restore the right map for each editor.
  mapOwner: EditorKey | null;
  mapSlots: Partial<Record<EditorKey, MapSlot>>;
  lastBeatmapIdByEditor: Partial<Record<EditorKey, string>>;
  playback: PlaybackState;
  /** Scrub position (seconds) to restore once the audio loads (refresh). */
  lastEditTime: number;
  toolMode: ToolMode;
  snapDivision: SnapDivision;
  selectedNoteIds: number[];
  /** Selected obstacle (slide) indices — parallel to selectedNoteIds so notes
   *  AND regular obstacles can be box-/select-all'd and deleted together. */
  selectedSlideIds: number[];
  /** Crouches (by time_ms), squares and triangles (by index in their own list) an area range holds.
   *  They are walls like any other but live in their own lists, so the range is the only thing that
   *  selects them; clearing the range or the selection empties these. */
  selectedCrouches: number[];
  selectedSquares: number[];
  selectedTriangles: number[];
  hoveredBeat: number;
  scrollStep: number;
  stepType: StepType;
  /** Mirror Mode: when ON, every note/wall you place also drops its mirror
   *  (opposite hand, X-mirrored). `mirrorInvertY` additionally flips it in Y. */
  mirrorMode: boolean;
  mirrorInvertY: boolean;
  /** Same-Note-Overwrite: when ON, placing a note where one already sits (same
   *  cell + beat) replaces it instead of stacking a duplicate. */
  sameNoteOverwrite: boolean;
  playbackSpeed: number;
  musicVolume: number;
  sfxVolume: number;
  cameraView: CameraView;
  timelineZoom: number;
  noteJumpSpeed: number;
  mapLocked: boolean;
  loopEnabled: boolean;
  metronomeEnabled: boolean;
  waveformVisible: boolean;
  showHumanFigure: boolean;
  figureMode: FigureMode;
  renderMode: RenderMode;
  heatmapEnabled: boolean;
  /** Synth VR's Rail Rendering toggle: rails drawn in their in-game shape rather than the smooth tube (SRVR-0356). */
  gameRailRendering: boolean;
  intensityData: IntensityData | null;
  // Pattern Difficulty (hand-aware, computed in backend) ────────────────
  /** Hand-aware difficulty report for the active difficulty. */
  patternDifficulty: PatternDifficultyReport | null;
  /** True while a fetch is in flight (avoid duplicate requests). */
  patternDifficultyLoading: boolean;
  /** Player arm span in meters, measured from the loaded mannequin FBX. */
  armSpanMeters: number | null;
  customCameraPosition: [number, number, number] | null;
  customCameraRotation: [number, number, number] | null;
  /** Synth VR's three free-look cameras (Custom 1-3): name + remembered pose. */
  vrCams: VrCamSlot[];
  // Preset camera overrides (null = use built-in defaults)
  defaultCameraPosition: [number, number, number] | null;
  defaultCameraRotation: [number, number, number] | null;
  leftCameraPosition: [number, number, number] | null;
  leftCameraRotation: [number, number, number] | null;
  rightCameraPosition: [number, number, number] | null;
  rightCameraRotation: [number, number, number] | null;
  isFlyModeActive: boolean;
  saveCustomCameraRequested: boolean;
  settingsOpen: boolean;
  /** The performance benchmark overlay (own canvas + synthetic workload). While
   *  open, both editors' canvases stop rendering so the measurement is clean. */
  benchmarkOpen: boolean;
  adminUsersOpen: boolean;
  mannequinSettings: MannequinSettings;
  gameplaySettings: GameplaySettings;
  interfaceSettings: InterfaceSettings;
  particleSettings: ParticleSettings;
  fileManagerOpen: boolean;
  editInfoOpen: boolean;
  newMapOpen: boolean;
  leftSidebarOpen: boolean;
  leftSidebarWidth: number;
  rightSidebarOpen: boolean;
  rightSidebarWidth: number;
  bottomPanelOpen: boolean;
  bottomPanelHeight: number;
  // Focus mode — slide ALL chrome (both sidebars + the playback bar) off-screen
  // with an ease animation for a clean, unobstructed 3D view. Session-only (not
  // persisted, so a refresh always comes back with the panels visible).
  uiHidden: boolean;
  // "Project saved" toast — rendered by App above the playback bar (anchored to
  // its top-left) so it aligns with the playbar instead of floating centre-screen.
  projectSaved: boolean;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];

  // Pattern Difficulty meter visibility (top overlay above the 3D viewport)
  patternDifficultyVisible: boolean;
  // FOV / render panel visibility (floating popup opened from the transport).
  fovPanelOpen: boolean;
  /** Performance mode driving object density (see PerfMode). */
  perfMode: PerfMode;

  // Top-level navigation between dashboard and the per-game editors.
  activeView: ActiveView;

  // ── Keybinding System ──────────────────────────────────────────
  keybindings: KeyBinding[];
  keybindingsOpen: boolean;

  // ── Clipboard ──────────────────────────────────────────────────
  clipboard: Note[];
  /** Walls/obstacles copied alongside the notes — marquee-select picks them up and
   *  Delete removes them, so copy/paste has to carry them too. */
  clipboardSlides: Slide[];
  clipboardBpm: number;

  // ── Grid Settings ──────────────────────────────────────────────
  gridVisible: boolean;
  gridSize: number;
  snapToGrid: boolean;
  railSnapping: boolean;
  gridGuideType: GridGuideType;
  gridGuideVisible: boolean;
  /** Halo strength on the objects on the grid while paused (SRVR-0314): 0 = off, 1–3 = level;
   *  Ctrl+H cycles it (KEY-0143). */
  highlightLevel: number;
  gridWidth: number;      // total width of the trigger-area grid (world units)
  gridHeight: number;     // total height of the trigger-area grid (world units)
  gridDivisionsH: number; // horizontal (column) divisions
  gridDivisionsV: number; // vertical (row) divisions
  gridColor: string;
  gridOpacity: number;
  notesGridYOffset: number;  // Y offset for the notes placement grid

  // ── Rail Placement ─────────────────────────────────────────────
  railPlacementActive: boolean;
  railPlacementNoteIndex: number | null;

  // ── Rail Node Selection & Editing ─────────────────────────────
  selectedRailNode: { noteIndex: number; nodeIndex: number } | null;
  setSelectedRailNode: (noteIndex: number | null, nodeIndex: number | null) => void;
  /** `offGrid` skips the placement-limit clamp: a group move, which may carry a rail node off the grid. */
  updateRailNode: (noteIndex: number, nodeIndex: number, position: [number, number, number], offGrid?: boolean) => void;
  removeRailNode: (noteIndex: number, nodeIndex: number) => void;

  // ── Note Preview (cursor ghost) ───────────────────────────────
  previewPosition: [number, number] | null;  // [x, y] in note-space, null when cursor not on grid
  setPreviewPosition: (pos: [number, number] | null) => void;

  // ── Note Hover / Gizmo ──────────────────────────────────────────
  hoveredNoteIndex: number | null;
  setHoveredNoteIndex: (index: number | null) => void;
  gizmoDragAxis: 'x' | 'y' | 'z' | null;  // which axis is being dragged
  setGizmoDragAxis: (axis: 'x' | 'y' | 'z' | null) => void;

  // ── Note Dragging ──────────────────────────────────────────────
  draggingNoteIndex: number | null;
  startDrag: (index: number) => void;
  finishDrag: (newBeat: number, newX: number, newY: number) => void;
  cancelDrag: () => void;

  // ── Bookmarks (named, global across difficulties) ───────────────
  bookmarks: BookmarkEntry[];
  addBookmark: (beat: number, name: string) => void;
  /** The Bookmark button and key (SRVR-0324): remove the bookmark on the playhead's beat, or add an
   *  unnamed one there and open its rename popup. */
  toggleBookmarkAtPlayhead: () => void;
  removeBookmark: (beat: number) => void;
  updateBookmarkName: (beat: number, name: string) => void;
  /** Colour the bookmark on `beat` from the palette (a 0xRRGGBB integer). */
  setBookmarkColor: (beat: number, color: number) => void;
  /** Move a bookmark from oldBeat to newBeat (drag the marker's arrow). */
  moveBookmark: (oldBeat: number, newBeat: number) => void;
  clearBookmarks: () => void;
  /** Synth VR: true while the warning panel asks the mapper to confirm clearing every bookmark. Bookmarks are
   *  not part of the undo history, so the clear is never made without it. */
  clearBookmarksPending: boolean;
  /** Synth VR: ask to clear every bookmark (F8 and the bookmark list's Clear All): opens the warning panel when
   *  there are any. The other views clear at once, as they always did. */
  requestClearBookmarks: () => void;
  /** The mapper confirmed: close the panel and clear every bookmark. */
  confirmClearBookmarks: () => void;
  /** Close the panel and keep the bookmarks. */
  cancelClearBookmarks: () => void;
  jumpToNextBookmark: () => void;
  jumpToPrevBookmark: () => void;
  bookmarkDialogOpen: boolean;
  setBookmarkDialogOpen: (open: boolean) => void;
  /** Synth VR: the panel that renames the Custom difficulty and sets its speed (the desktop's F11). */
  customDifficultyDialogOpen: boolean;
  setCustomDifficultyDialogOpen: (open: boolean) => void;
  /** Beat of the bookmark open in the rename popup (click a marker / place one), or null. */
  editingBookmarkBeat: number | null;
  setEditingBookmarkBeat: (beat: number | null) => void;

  // ── Flash Events (global light effects) ────────────────────────
  flashEvents: FlashEvent[];
  addFlashEvent: (event: FlashEvent) => void;
  /** The Light button and key (SRVR-0324): remove the light on the playhead's beat, or add one there
   *  with the flash defaults as a single pulse. */
  toggleLightAtPlayhead: () => void;
  removeFlashEvent: (beat: number) => void;
  updateFlashEvent: (beat: number, updates: Partial<FlashEvent>) => void;
  /** Move a flash event from oldBeat to newBeat (drag the marker's arrow). */
  moveFlashEvent: (oldBeat: number, newBeat: number) => void;
  clearFlashEvents: () => void;
  flashDefaults: { color: string; intensity: number; fadeInTime: number; fadeOutTime: number; duration: number; repeat: number; frequency: number; };
  setFlashDefaults: (defaults: Partial<EditorState['flashDefaults']>) => void;
  /** Beat of the "primary" flash event whose values the editor popup shows, or null. */
  editingFlashBeat: number | null;
  setEditingFlashBeat: (beat: number | null) => void;
  /** Beats of all lights selected for editing — the popup applies edits to all. */
  selectedFlashBeats: number[];
  setSelectedFlashBeats: (beats: number[]) => void;
  /** Click a bulb: replace the selection (additive=false) or toggle it (Shift/Ctrl). */
  toggleFlashSelection: (beat: number, additive: boolean) => void;
  /** Apply the same updates to many lights at once. */
  updateFlashEvents: (beats: number[], updates: Partial<FlashEvent>) => void;

  // ── Flash Effects (the game's `Effects`: a beat list per difficulty) ──
  effects: Record<string, number[]>;
  /** The Effects button and key: remove the active difficulty's effect on the playhead's beat, or add one. */
  toggleEffectAtPlayhead: () => void;
  removeEffect: (beat: number) => void;

  loadBeatmap: (beatmap: Beatmap) => void;
  closeBeatmap: () => void;
  setActiveDifficulty: (name: string) => void;
  addDifficulty: (name: string) => void;
  updateMeta: (meta: MetaDraft) => void;
  /** The BPM a mapper asked for while the map holds objects, waiting on the dialog's choice (Synth VR). */
  pendingBpmChange: number | null;
  /** A mapper's request to change the BPM. Without objects (or outside Synth VR) it applies at once; with
   *  them it waits for `applyBpmChange`, so nothing moves until the mapper has chosen how. */
  requestBpmChange: (bpm: number) => void;
  cancelBpmChange: () => void;
  /** `beats`: every object stays on its beat, so its time moves with the tempo. `times`: every object keeps
   *  its time and so lands on a different beat. */
  applyBpmChange: (mode: 'beats' | 'times') => void;
  setPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  setCurrentBeat: (beat: number) => void;
  setDuration: (duration: number) => void;
  setToolMode: (mode: ToolMode, temporary?: boolean) => void;
  /** The tool that was active when holding Ctrl switched to Select for the duration (null otherwise), so a
   *  Ctrl + click can act for the tool the mapper was really using (SRVR-0038). */
  toolBeforeCtrl: ToolMode | null;
  /** The type of the last note or rail tool chosen, which Synth VR's J joins when the current tool
   *  places neither (SRVR-0359 AC2). */
  lastNoteKind: NoteFilterKind | null;
  setSnapDivision: (snap: SnapDivision) => void;
  selectNote: (index: number, additive?: boolean) => void;
  selectNotes: (indices: number[]) => void;
  selectSlides: (indices: number[]) => void;
  clearSelection: () => void;
  /** Move the selected notes by whole grid cells in X / Y (keyboard nudge). */
  nudgeSelection: (dxCells: number, dyCells: number) => void;
  /** Right-drag loop region, in raw beats (same frame as note.time_beats). */
  loopRegion: { startBeat: number; endBeat: number } | null;
  setLoopRegion: (r: { startBeat: number; endBeat: number } | null) => void;
  /** Area selection (SRVR-0315/0316): the committed beat range, the held key's gesture, the range
   *  Shift+Z recalls, and the Selection Options toggles (SRVR-0318). Read the range in force with
   *  `effectiveAreaRange(areaRange, areaGesture, playback.currentBeat)`. */
  areaRange: AreaRange | null;
  areaGesture: AreaGesture | null;
  areaRecall: AreaRange | null;
  areaFilters: AreaFilters;
  /** The area-selection key went down / came up (KEY-0021). */
  areaKeyDown: () => void;
  areaKeyUp: () => void;
  /** Shift+Z (KEY-0144): extend the range to the playhead, or recall the last cleared one. */
  areaExtendOrRecall: () => void;
  /** Clear the range, keeping it for recall. */
  clearAreaRange: () => void;
  setAreaFilters: (kinds: readonly AreaFilterKind[], on: boolean) => void;
  /** What the last range copy or cut stored (SRVR-0319); null after an index-selection copy. */
  areaClipboard: AreaClipboard | null;
  /** Delete, copy, cut, paste and flip on the range in force, or the playhead's beat without one
   *  (SRVR-0319, SRVR-0320, SRVR-0321). */
  areaDelete: () => void;
  areaCopy: () => void;
  areaCut: () => void;
  areaPaste: (mirrored: boolean) => void;
  areaFlip: (axis: FlipAxis) => void;
  /** The Tools box (Synth VR). A live preview open from a rotation slider or Rail Smoothing: the
   *  operation's base is the difficulty as it was when the preview opened (SRVR-0352, SRVR-0354). */
  toolPreview: 'rotate' | 'scale' | 'smooth' | null;
  /** Note Rotation (SRVR-0352): turn the range's notes and rails `deg` degrees anticlockwise. */
  rotateArea: (deg: number) => void;
  /** The rotation slider while dragged: the range rotated `deg` from where it was when the drag began. */
  previewRotateArea: (deg: number) => void;
  /** Scaling (SRVR-0353): scale the range's notes, rails and walls by `xPct`/`yPct` percent. */
  scaleArea: (xPct: number, yPct: number) => void;
  /** Scaling's controls as they change: the range scaled from where it was when the preview opened. */
  previewScaleArea: (xPct: number, yPct: number) => void;
  /** Rail Smoothing (SRVR-0354): preview the smoothing of the rail on the playhead beat. */
  previewRailSmoothing: (opts: RailSmoothingParams) => 'ok' | 'no-rail';
  /** Keep the previewed smoothing, or with none open, smooth once with `opts`. */
  applyRailSmoothing: (opts: RailSmoothingParams) => 'ok' | 'no-rail';
  /** Keep whatever preview is open, as one undo entry. */
  commitToolPreview: () => void;
  /** Put back what the open preview changed, and drop its undo entry. */
  cancelToolPreview: () => void;
  /** How a range paste meets what is already there (SRVR-0323); Delete Existing until changed. */
  pasteMode: PasteMode;
  setPasteMode: (mode: PasteMode) => void;
  /** Shift+A (KEY-0145): Delete Existing → Prioritize Existing → Prioritize Pasted → Delete Existing. */
  cyclePasteMode: () => void;
  setHoveredBeat: (beat: number) => void;
  setScrollStep: (step: number) => void;
  /** Cycle the snap-grid family binary → ternary → all, remapping the current
   *  step to the nearest value in the new family. */
  cycleStepType: () => void;
  setPlaybackSpeed: (speed: number) => void;
  setMusicVolume: (vol: number) => void;
  setSfxVolume: (vol: number) => void;
  setCameraView: (view: CameraView) => void;
  setTimelineZoom: (zoom: number) => void;
  setNoteJumpSpeed: (njs: number) => void;
  setMapLocked: (locked: boolean) => void;
  setLoopEnabled: (enabled: boolean) => void;
  setMetronomeEnabled: (enabled: boolean) => void;
  setWaveformVisible: (visible: boolean) => void;
  setShowHumanFigure: (show: boolean) => void;
  setFigureMode: (mode: FigureMode) => void;
  setRenderMode: (mode: RenderMode) => void;
  setHeatmapEnabled: (enabled: boolean) => void;
  setGameRailRendering: (on: boolean) => void;
  setIntensityData: (data: IntensityData | null) => void;
  setArmSpanMeters: (m: number | null) => void;
  /** Async — fetches pattern difficulty for the active beatmap/difficulty. */
  refreshPatternDifficulty: () => Promise<void>;
  saveCustomCamera: (pos: [number, number, number], rot: [number, number, number]) => void;
  setCustomCameraPosition: (pos: [number, number, number]) => void;
  setCustomCameraRotation: (rot: [number, number, number]) => void;
  clearCustomCamera: () => void;
  setVrCamName: (index: number, name: string) => void;
  saveVrCamPose: (index: number, pos: [number, number, number], rot: [number, number, number]) => void;
  setDefaultCameraPosition: (pos: [number, number, number]) => void;
  setDefaultCameraRotation: (rot: [number, number, number]) => void;
  setLeftCameraPosition: (pos: [number, number, number]) => void;
  setLeftCameraRotation: (rot: [number, number, number]) => void;
  setRightCameraPosition: (pos: [number, number, number]) => void;
  setRightCameraRotation: (rot: [number, number, number]) => void;
  resetCameraPreset: (preset: 'default' | 'left' | 'right') => void;
  setFlyModeActive: (active: boolean) => void;
  requestSaveCustomCamera: () => void;
  setSettingsOpen: (open: boolean) => void;
  setBenchmarkOpen: (open: boolean) => void;
  setAdminUsersOpen: (open: boolean) => void;
  setMannequinSettings: (s: Partial<MannequinSettings>) => void;
  setGameplaySettings: (s: Partial<GameplaySettings>) => void;
  setObstacleConfig: (key: ObstacleConfigKey, update: Partial<ObstacleConfig>) => void;
  resetMannequinSettings: () => void;
  resetGameplaySettings: () => void;
  setInterfaceSettings: (s: Partial<InterfaceSettings>) => void;
  resetInterfaceSettings: () => void;
  setParticleSettings: (s: Partial<ParticleSettings>) => void;
  resetParticleSettings: () => void;
  setFileManagerOpen: (open: boolean) => void;
  setEditInfoOpen: (open: boolean) => void;
  /** Minimap camera (height / forward-position / tilt) + the adjust panel open
   *  state. Mirrors the Flow editor's minimap-camera tuning. */
  minimapCam: { height: number; posZ: number; tilt: number };
  setMinimapCam: (patch: Partial<{ height: number; posZ: number; tilt: number }>) => void;
  minimapCamOpen: boolean;
  setMinimapCamOpen: (open: boolean) => void;
  /** Minimap beat spacing multiplier (0.5–2). Same control as Flow's minimap
   *  Zoom — turn it up and packed sections stop looking like a solid bar. */
  minimapZoom: number;
  setMinimapZoom: (v: number) => void;
  /** Camera View panel: are the pose controls (⚙) showing? */
  cameraOptionsOpen: boolean;
  setCameraOptionsOpen: (open: boolean) => void;
  setNewMapOpen: (open: boolean) => void;
  setLeftSidebarOpen: (open: boolean) => void;
  setLeftSidebarWidth: (w: number) => void;
  setRightSidebarOpen: (open: boolean) => void;
  setUiHidden: (hidden: boolean) => void;
  setProjectSaved: (v: boolean) => void;
  setRightSidebarWidth: (w: number) => void;
  setBottomPanelOpen: (open: boolean) => void;
  setBottomPanelHeight: (h: number) => void;
  // ── Keybinding Methods ───────────────────────────────────────
  setKeybinding: (id: string, slot: 'primary' | 'secondary', combo: KeyCombo) => void;
  resetKeybindings: () => void;
  setKeybindingsOpen: (open: boolean) => void;

  // ── Clipboard Methods ──────────────────────────────────────────
  copySelected: () => void;
  cutSelected: () => void;
  pasteAtBeat: (beat: number) => void;
  pasteMirrorAtBeat: (beat: number) => void;

  // ── Grid Methods ───────────────────────────────────────────────
  setGridVisible: (v: boolean) => void;
  setGridSize: (s: number) => void;
  setSnapToGrid: (v: boolean) => void;
  setRailSnapping: (v: boolean) => void;
  /** `(x, y)` moved onto the rail concurrent with a note of `noteType` at `beat`, when Snap to Rail is on in
   *  Synth VR and such a rail exists; else `(x, y)` unchanged. */
  snapToRailPosition: (x: number, y: number, noteType: string, beat: number, skipIndex?: number) => [number, number];
  setGridGuideType: (t: GridGuideType) => void;
  setGridGuideVisible: (v: boolean) => void;
  setHighlightLevel: (level: number) => void;
  setGridWidth: (w: number) => void;
  setGridHeight: (h: number) => void;
  setGridDivisionsH: (d: number) => void;
  setGridDivisionsV: (d: number) => void;
  setGridColor: (c: string) => void;
  setGridOpacity: (o: number) => void;
  setNotesGridYOffset: (v: number) => void;
  snapToGridPosition: (x: number, y: number) => [number, number];
  /** Like `snapToGridPosition`, but on the grid's lines continued without end, so it never pulls a position back
   *  inside the grid. Walls use it (SRVR-0065). */
  snapWallToGridPosition: (x: number, y: number) => [number, number];

  // ── Rail Placement Methods ─────────────────────────────────────
  startRailPlacement: (noteIndex: number) => void;
  addRailNode: (position: [number, number, number]) => void;
  /** Ctrl + click (SRVR-0038): add a node at (x, y) on the current beat to the rail of `noteType` that runs
   *  through the beat, or else extend the nearest rail of that type behind it. `none` when there is neither. */
  addRailNodeNearCursor: (noteType: string, x: number, y: number, beat: number) => 'inserted' | 'extended' | 'none';
  finishRailPlacement: () => void;
  cancelRailPlacement: () => void;

  // ── Select All ─────────────────────────────────────────────────
  selectAll: () => void;

  /** `offGrid` skips the placement-limit clamp: a position a Snap to Rail landed on a rail that leaves the grid. */
  addNote: (note: Note, offGrid?: boolean) => void;
  addNotes: (notes: Note[]) => void;
  replaceNotes: (notes: Note[]) => void;
  removeNotes: (indices: number[]) => void;
  updateNote: (index: number, update: Partial<Note>, offGrid?: boolean) => void;
  /** Batch position/time move for a set of notes by index, in one undo step —
   *  used when dragging a gizmo handle of a multi-selection (group move). */
  /** An update with `offGrid` skips the placement-limit clamp (a group move, or a Snap to Rail landing). */
  moveSelectedNotes: (updates: Array<{ index: number; position?: [number, number, number]; time?: { beats: number; ms: number }; offGrid?: boolean }>) => void;
  /** Take ONE undo snapshot at the start of an interactive drag, then suppress
   *  per-frame snapshots until endDrag() — undo returns to the pre-drag state. */
  beginDrag: (label: string) => void;
  endDrag: () => void;
  mirrorSelected: () => void;
  /** Flip selected notes over the Y axis (vertical): negate Y, keep hand. */
  flipSelectedY: () => void;
  /** Add a mirrored (opposite-hand, mirrored-X) copy of each selected note. */
  duplicateSelectedOppositeHand: () => void;
  /** Synth VR: true from a Ctrl+D that made copies until the key is released, while the mouse may still
   *  reposition them (Track.cs `IsDuplicating`). */
  duplicateMoveActive: boolean;
  /** Synth VR: move the copies the held Ctrl+D just made by a pointer step in pixels; refused until
   *  250 ms after the press, as the desktop editor does. */
  duplicateMoveBy: (dxPx: number, dyPx: number) => void;
  /** Synth VR: the Ctrl+D key was released; the copies stay where they are. */
  duplicateMoveEnd: () => void;
  /** Scale the TIME spacing of the selection around its earliest note by
   *  `factor` (>1 spreads apart, <1 compresses). */
  scaleSelection: (factor: number) => void;
  /** Wipe every object (notes, slides, crouches, jumps, shape obstacles) from
   *  the active difficulty. Undoable. */
  deleteAllObjects: () => void;
  /** Place a note, honoring Mirror Mode (drops the mirror in the same undo step). */
  addPlacedNote: (note: Note, offGrid?: boolean) => void;
  /** Place a wall, honoring Mirror Mode (mirrors wall_left↔right, angle_left↔right). */
  addPlacedSlide: (slide: Slide) => void;
  toggleMirrorMode: () => void;
  toggleMirrorInvertY: () => void;
  /** Join the selected notes (same hand) into one rail — earliest is the head,
   *  the rest become rail nodes. */
  joinNotesToRail: () => void;
  /** Split the rail at the selected rail node into two independent rails. */
  splitRailAtNode: () => void;
  /** Resample the selected rail's nodes onto the current snap step (Catmull-Rom). */
  recalcRailNodesByStep: () => void;
  /** Place a wall/obstacle of the given type at the playhead (legacy Ctrl+1..8). */
  placeWallAtPlayhead: (wallType: string) => void;
  /** Synth VR wall button / hotkey (SRVR-0313): a beat holds at most one wall, of any kind. If the
   *  playhead's beat holds a wall of `kind` at the format centre, remove it and leave the beat empty;
   *  otherwise remove every wall on the beat and add one of `kind` at the centre. One undo entry. */
  toggleWallAtPlayhead: (kind: WallKind) => void;
  /** Place a note of the active note-tool's hand at the playhead (centre). */
  placeNoteAtPlayhead: () => void;
  /** Synth VR Note Fill (Ctrl + click on a note button): one note of `kind` at the centre on every Step of the area. */
  fillAreaWithNotes: (kind: NoteFilterKind) => void;
  toggleSameNoteOverwrite: () => void;
  /** Snip: insert a node into the selected rail at the playhead beat (on-curve). */
  snipRailAtPlayhead: () => void;
  /** Cycle the selected notes' hand/type: right → left → one-hand → two-hand. */
  cycleSelectedNoteType: () => void;
  /** Snap each selected note onto the nearest rail's path at its own beat. */
  snapSelectionToRail: () => void;
  /** Synth VR, Ctrl + Alt + click on a Left or Right note on the playhead's beat: add its opposite-hand copy
   *  mirrored about the grid's centre (a rail is mirrored with it). Refused, with the usual toast, when the
   *  beat's rule does not allow the copy. Track.cs `TryMirrorSelectedNote`. */
  addMirrorNote: (index: number) => void;
  /** Synth VR, Shift + click on a note on the playhead's beat: cycle the colour of it and of every note of
   *  its colour in the area range (the beat alone without one) Left → Right → One-hand special → Two-hand
   *  special → Left, or Left ↔ Right when the notes reached hold both hands. Track.cs
   *  `TryChangeColorSelectedNote`. */
  changeNoteColor: (index: number) => void;
  addSlide: (slide: Slide) => void;
  removeSlide: (index: number) => void;
  removeSlides: (indices: number[]) => void;
  updateSlide: (index: number, patch: Partial<Slide>) => void;
  sortSlides: () => void;
  addJump: (timeMs: number) => void;
  removeJump: (timeMs: number) => void;
  addCrouch: (timeMs: number) => void;
  removeCrouch: (timeMs: number) => void;
  /** Delete one square or triangle wall by its index in its own list (Remote Delete). One undo entry. */
  removeSquareTriangle: (kind: 'square' | 'triangle', index: number) => void;
  moveCrouch: (oldMs: number, newMs: number) => void;
  moveJump: (oldMs: number, newMs: number) => void;
  /** Set walls' X/Y in place (Alt-drag). Time, the position's third component and list order are
   *  kept; nothing is clamped — walls are not grid objects (SRVR-0065). One undo entry. */
  moveObstacles: (moves: ObstacleMove[]) => void;
  /** Add `deltaDeg` to each wall's `z_rotation`, wrapped to 0 <= r < 360. Crouches are not rotatable,
   *  so they are not an option here. One undo entry (none while a drag has suppressed them). */
  rotateObstacles: (targets: Array<{ kind: 'slide' | 'square' | 'triangle'; index: number }>, deltaDeg: number) => void;
  toggleJumpAtBeat: (beat: number) => void;
  toggleCrouchAtBeat: (beat: number) => void;
  undo: () => void;
  redo: () => void;
  setPatternDifficultyVisible: (visible: boolean) => void;
  setFovPanelOpen: (open: boolean) => void;
  setPerfMode: (mode: PerfMode) => void;
  /** Per-preset performance knobs. Edited from the Performance settings tab in
   *  either editor — both read this one store, so changes apply everywhere. */
  performanceSettings: PerformanceSettings;
  setPerformancePreset: (preset: PerfMode, patch: Partial<PerfPresetConfig>) => void;
  resetPerformancePreset: (preset: PerfMode) => void;
  resetPerformanceSettings: () => void;
  setActiveView: (view: ActiveView) => void;
  /** Flush the in-memory edits to the per-map localStorage draft immediately
   *  (manual "Save" — the same drafts autosave writes, but on demand). */
  saveProjectDraft: () => void;
  clearCurrentDifficulty: () => void;
  clearAllDifficulties: () => void;
  getCurrentDifficulty: () => Difficulty | null;
  snapBeat: (beat: number) => number;
  beatToTimeMs: (beat: number) => number;
}

// ── Store ───────────────────────────────────────────────────────────────────

// ── No map edits while the track plays (Synth VR) ───────────────────────────
//
// Every action that changes the map — notes, rails, walls, crouches and jumps, lights, bookmarks, the
// Shape Designer's art, the map's BPM and offset, and undo and redo — does nothing in Synth VR while the
// track is playing (docs/spec/srvr/editor-shell.md SRVR-0336). They are listed here rather than checked
// one by one inside each action so a new edit cannot slip past by being written without the check.
// Console and Flow keep editing while playing.
export const MAP_EDIT_ACTIONS = [
  // notes and rails
  'addNote', 'addPlacedNote', 'addNotes', 'replaceNotes', 'removeNotes', 'updateNote', 'moveSelectedNotes',
  'mirrorSelected', 'flipSelectedY', 'duplicateSelectedOppositeHand', 'scaleSelection', 'nudgeSelection',
  'cycleSelectedNoteType', 'snapSelectionToRail', 'addMirrorNote', 'joinNotesToRail', 'splitRailAtNode', 'recalcRailNodesByStep',
  'snipRailAtPlayhead', 'placeNoteAtPlayhead', 'fillAreaWithNotes', 'addRailNode', 'addRailNodeNearCursor', 'finishRailPlacement', 'updateRailNode', 'removeRailNode',
  // walls, crouches and jumps
  'addPlacedSlide', 'addSlide', 'removeSlide', 'removeSlides', 'updateSlide', 'placeWallAtPlayhead', 'toggleWallAtPlayhead',
  'removeSquareTriangle', 'addCrouch', 'removeCrouch', 'moveCrouch', 'toggleCrouchAtBeat',
  'addJump', 'removeJump', 'moveJump', 'toggleJumpAtBeat', 'moveObstacles', 'rotateObstacles',
  // the area range's edits, the clipboard's writes and drags
  'areaDelete', 'areaCut', 'areaPaste', 'areaFlip', 'cutSelected', 'pasteAtBeat', 'pasteMirrorAtBeat',
  // the Tools box (Synth VR)
  'rotateArea', 'previewRotateArea', 'scaleArea', 'previewScaleArea', 'previewRailSmoothing', 'applyRailSmoothing',
  'deleteAllObjects', 'clearCurrentDifficulty', 'clearAllDifficulties', 'addDifficulty', 'updateMeta', 'requestBpmChange', 'applyBpmChange',
  'beginDrag', 'startDrag', 'finishDrag',
  // history
  'undo', 'redo', 'undoDesign', 'redoDesign',
  // bookmarks and lights
  'addBookmark', 'toggleBookmarkAtPlayhead', 'removeBookmark', 'updateBookmarkName', 'setBookmarkColor', 'moveBookmark',
  'clearBookmarks', 'requestClearBookmarks', 'confirmClearBookmarks', 'addFlashEvent', 'toggleLightAtPlayhead', 'removeFlashEvent', 'updateFlashEvent', 'moveFlashEvent',
  'clearFlashEvents', 'updateFlashEvents', 'toggleEffectAtPlayhead', 'removeEffect',
  // the Shape Designer's art
  'addLibraryArtToMap', 'addDesignShape', 'updateDesignShape', 'updateDesignShapes', 'updateDesignMesh', 'updateDesignNode',
  'addDesignNode', 'removeDesignNode', 'removeDesignShape', 'removeDesignShapes', 'duplicateDesignShape', 'pasteDesignShape',
] as const;

/** Whether the map is closed to edits: the track is playing in the Synth VR editor. */
export function mapEditsLocked(s: { playback: { playing: boolean }; activeView?: string }): boolean {
  return s.playback.playing && isSynthVrView(s.activeView);
}

/** What a locked edit does in place of itself, where doing nothing would strand state. A drag that was
 *  already running when play started is dropped, so it does not wait for a drop that can no longer land. */
const LOCKED_EDIT_FALLBACK: Partial<Record<(typeof MAP_EDIT_ACTIONS)[number], (get: () => EditorState) => void>> = {
  finishDrag: get => get().cancelDrag(),
};

function guardMapEdits<T extends object>(get: () => EditorState, state: T): T {
  const actions = state as Record<string, unknown>;
  for (const name of MAP_EDIT_ACTIONS) {
    const fn = actions[name];
    if (typeof fn !== 'function') continue;
    actions[name] = (...args: unknown[]) => {
      if (mapEditsLocked(get())) { LOCKED_EDIT_FALLBACK[name]?.(get); return undefined; }
      return (fn as (...a: unknown[]) => unknown)(...args);
    };
  }
  return state;
}

export const useEditorStore = create<EditorState>((set, get) => guardMapEdits(get, {
  beatmap: null,
  lastBeatmapId: savedPrefs.lastBeatmapId || null,
  mapOwner: null,
  mapSlots: {},
  lastBeatmapIdByEditor: savedPrefs.lastBeatmapIdByEditor || {},
  activeDifficulty: savedPrefs.activeDifficulty || 'Expert',
  playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
  lastEditTime: savedPrefs.lastTime ?? 0,
  toolMode: 'select',
  snapDivision: savedPrefs.snapDivision || 16,
  selectedNoteIds: [],
  selectedSlideIds: [],
  selectedCrouches: [],
  selectedSquares: [],
  selectedTriangles: [],
  hoveredBeat: 0,
  scrollStep: Math.min(savedPrefs.scrollStep || 1, MAX_SCROLL_STEP),
  stepType: 'binary',
  mirrorMode: false,
  mirrorInvertY: false,
  sameNoteOverwrite: false,
  toolBeforeCtrl: null,
  lastNoteKind: null,
  playbackSpeed: savedPrefs.playbackSpeed || 1,
  musicVolume: savedPrefs.musicVolume ?? 0.8,
  sfxVolume: savedPrefs.sfxVolume ?? 0.3,
  cameraView: (savedPrefs.cameraView || 'default') as CameraView,
  timelineZoom: savedPrefs.timelineZoom ?? 1,
  noteJumpSpeed: savedPrefs.noteJumpSpeed ?? 2,
  mapLocked: savedPrefs.mapLocked ?? false,
  loopEnabled: false,
  metronomeEnabled: false,
  waveformVisible: true,
  showHumanFigure: savedPrefs.showHumanFigure ?? false,
  figureMode: savedPrefs.figureMode ?? 'off',
  renderMode: savedPrefs.renderMode ?? 'auto',
  heatmapEnabled: savedPrefs.heatmapEnabled ?? false,
  gameRailRendering: savedPrefs.gameRailRendering ?? false,
  intensityData: null,
  patternDifficulty: null,
  patternDifficultyLoading: false,
  armSpanMeters: null,
  customCameraPosition: savedPrefs.customCameraPosition || null,
  customCameraRotation: savedPrefs.customCameraRotation || null,
  vrCams: DEFAULT_VR_CAM_NAMES.map((name, i): VrCamSlot => {
    const s = savedPrefs.vrCams?.[i];
    return { name: s?.name || name, position: s?.position ?? null, rotation: s?.rotation ?? null };
  }),
  defaultCameraPosition: savedPrefs.defaultCameraPosition || null,
  defaultCameraRotation: savedPrefs.defaultCameraRotation || null,
  leftCameraPosition: savedPrefs.leftCameraPosition || null,
  leftCameraRotation: savedPrefs.leftCameraRotation || null,
  rightCameraPosition: savedPrefs.rightCameraPosition || null,
  rightCameraRotation: savedPrefs.rightCameraRotation || null,
  isFlyModeActive: false,
  saveCustomCameraRequested: false,
  settingsOpen: false,
  benchmarkOpen: false,
  adminUsersOpen: false,
  mannequinSettings: { ...DEFAULT_MANNEQUIN, ...savedPrefs.mannequinSettings },
  gameplaySettings: sanitizeGameplaySettings(savedPrefs.gameplaySettings),
  performanceSettings: sanitizePerformanceSettings(savedPrefs.performanceSettings),
  interfaceSettings: migrateTypographyColors({ ...DEFAULT_INTERFACE, ...savedPrefs.interfaceSettings }),
  particleSettings: { ...DEFAULT_PARTICLES, ...savedPrefs.particleSettings },
  fileManagerOpen: false,
  editInfoOpen: false,
  pendingBpmChange: null,
  newMapOpen: false,
  leftSidebarOpen: savedPrefs.leftSidebarOpen ?? true,
  leftSidebarWidth: savedPrefs.leftSidebarWidth ?? 200,
  rightSidebarOpen: savedPrefs.rightSidebarOpen ?? true,
  rightSidebarWidth: savedPrefs.rightSidebarWidth ?? 220,
  bottomPanelOpen: savedPrefs.bottomPanelOpen ?? true,
  bottomPanelHeight: savedPrefs.bottomPanelHeight ?? 300,
  uiHidden: false,
  projectSaved: false,
  undoStack: [],
  redoStack: [],
  patternDifficultyVisible: savedPrefs.patternDifficultyVisible ?? true,
  fovPanelOpen: savedPrefs.fovPanelOpen ?? false,
  perfMode: loadPerfMode(),
  activeView: (() => {
    const p = typeof window !== 'undefined' ? window.location.pathname : '';
    if (/^\/flow(\/|$)/.test(p)) return 'flow' as ActiveView;
    if (/^\/synth-vr(\/|$)/.test(p)) return 'synth-rider' as ActiveView;
    if (/^\/synth-console(\/|$)/.test(p)) return 'console' as ActiveView;
    return 'dashboard' as ActiveView;
  })(),

  // ── Keybinding System ──────────────────────────────────────────
  keybindings: (() => {
    // Reconcile any saved bindings against the CURRENT defaults rather than
    // trusting a saved copy blindly. A stale/partial `abm-keybindings` (older
    // schema, a dropped action, or a bad save) used to load verbatim — which
    // could silently leave a core action like toggle_play (Space → play/pause)
    // missing or unbound, killing that shortcut with no way to recover but a
    // manual reset. Now: start from the defaults (so every current action is
    // always present) and overlay only the user's saved primary/secondary for
    // ids that still exist.
    const defaults = DEFAULT_KEYBINDINGS;
    try {
      const raw = localStorage.getItem('abm-keybindings');
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length) {
          // Actions whose DEFAULT changed and whose old default was itself the
          // reported bug — a saved copy would keep shipping the broken binding to
          // exactly the people who complained. Adopt the new default for these and
          // keep every other personalisation. Bump the version + list together.
          const migratedIds = KEYBINDING_MIGRATED_IDS;
          const savedVersion = Number(localStorage.getItem(KEYBINDINGS_VERSION_KEY) || '0');
          const stale = savedVersion < KEYBINDINGS_VERSION;
          if (stale) {
            try { localStorage.setItem(KEYBINDINGS_VERSION_KEY, String(KEYBINDINGS_VERSION)); } catch { /* ignore */ }
          }
          const savedById = new Map<string, KeyBinding>(
            saved
              .filter((b: unknown): b is KeyBinding =>
                !!b && typeof (b as KeyBinding).id === 'string')
              .map((b: KeyBinding) => [b.id, b]),
          );
          return defaults.map((def) => {
            if (stale && migratedIds.has(def.id)) return def;
            const s = savedById.get(def.id);
            if (!s) return def;
            return {
              ...def,
              primary: s.primary ?? def.primary,
              secondary: s.secondary ?? def.secondary,
            };
          });
        }
      }
    } catch { /* ignore */ }
    try { localStorage.setItem(KEYBINDINGS_VERSION_KEY, String(KEYBINDINGS_VERSION)); } catch { /* ignore */ }
    return [...defaults];
  })(),
  keybindingsOpen: false,

  // ── Clipboard ──────────────────────────────────────────────────
  clipboard: [],
  clipboardSlides: [],
  clipboardBpm: 120,

  // ── Grid Settings ──────────────────────────────────────────────
  gridVisible: savedPrefs.gridVisible ?? true,
  gridSize: savedPrefs.gridSize ?? 0.1365,
  snapToGrid: savedPrefs.snapToGrid ?? true,
  railSnapping: savedPrefs.railSnapping ?? true,
  gridGuideType: (savedPrefs.gridGuideType as GridGuideType) || 'solid',
  gridGuideVisible: savedPrefs.gridGuideVisible ?? true,
  highlightLevel: savedPrefs.highlightLevel ?? (savedPrefs.highlightingEnabled === false ? 0 : 1),
  gridWidth: savedPrefs.gridWidth ?? DEFAULT_GRID.gridWidth,
  gridHeight: savedPrefs.gridHeight ?? DEFAULT_GRID.gridHeight,
  gridDivisionsH: savedPrefs.gridDivisionsH ?? DEFAULT_GRID.gridDivisionsH,
  gridDivisionsV: savedPrefs.gridDivisionsV ?? DEFAULT_GRID.gridDivisionsV,
  gridColor: savedPrefs.gridColor ?? '#7C3AED',
  gridOpacity: savedPrefs.gridOpacity ?? 0.25,
  notesGridYOffset: savedPrefs.notesGridYOffset ?? DEFAULT_GRID.notesGridYOffset,

  // ── Rail Placement ─────────────────────────────────────────────
  railPlacementActive: false,
  railPlacementNoteIndex: null,

  // ── Rail Node Selection & Editing ─────────────────────────────
  selectedRailNode: null,
  setSelectedRailNode: (noteIndex, nodeIndex) => {
    if (noteIndex === null || nodeIndex === null) {
      set({ selectedRailNode: null });
    } else {
      set({ selectedRailNode: { noteIndex, nodeIndex } });
    }
  },

  updateRailNode: (noteIndex, nodeIndex, position, offGrid = false) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || nodeIndex >= note.rail_nodes.length) return;
    // Reject a time-drag that would land this node on ANOTHER node's exact
    // time (or the head's) — two nodes on one time position break the rail
    // (reviewer ask). node[2] is encoded time = timeMs * 0.02; ±2 ms epsilon.
    const EPS_ENC = 2 * 0.02;
    const clashes = note.rail_nodes.some((n2, i) => i !== nodeIndex && Math.abs(n2[2] - position[2]) < EPS_ENC)
      || Math.abs(note.time_ms * 0.02 - position[2]) < EPS_ENC;
    if (clashes) return;
    // Clamp X/Y to grid bounds (Z is time-based, leave as-is)
    const [cx, cy] = offGrid ? [position[0], position[1]] : _clampNotePos(state, position[0], position[1]);
    const newNodes = [...note.rail_nodes];
    newNodes[nodeIndex] = [cx, cy, position[2]];
    get().updateNote(noteIndex, { rail_nodes: newNodes });
    // A node was moved: the notes on its rail follow (SRVR-0367). Adding and removing nodes do not re-snap.
    const moved = get().beatmap?.difficulties[get().activeDifficulty]?.notes[noteIndex];
    if (moved) _resnapToRails(set, get, [moved]);
  },

  removeRailNode: (noteIndex, nodeIndex) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || nodeIndex >= note.rail_nodes.length) return;
    const newNodes = note.rail_nodes.filter((_: [number, number, number], i: number) => i !== nodeIndex);
    if (newNodes.length === 0) {
      // No more nodes — remove the rail entirely
      get().updateNote(noteIndex, { has_rail: false, rail_nodes: null });
      // If rail placement was active for this note, cancel it
      if (state.railPlacementActive && state.railPlacementNoteIndex === noteIndex) {
        set({ railPlacementActive: false, railPlacementNoteIndex: null });
      }
    } else {
      get().updateNote(noteIndex, { rail_nodes: newNodes });
    }
    // Clear selection if the removed node was selected
    if (state.selectedRailNode?.noteIndex === noteIndex && state.selectedRailNode?.nodeIndex === nodeIndex) {
      set({ selectedRailNode: null });
    }
  },

  // ── Note Preview / Hover / Gizmo ─────────────────────────────
  previewPosition: null,
  setPreviewPosition: (pos) => set({ previewPosition: pos }),
  hoveredNoteIndex: null,
  setHoveredNoteIndex: (index) => set({ hoveredNoteIndex: index }),
  gizmoDragAxis: null,
  setGizmoDragAxis: (axis) => set({ gizmoDragAxis: axis }),

  draggingNoteIndex: null,

  // Bookmarks (named, global)
  bookmarks: [],
  bookmarkDialogOpen: false,
  customDifficultyDialogOpen: false,
  editingBookmarkBeat: null,

  // Shapes (decorative wall layer)
  designShapes: [],
  selectedDesignShapeIds: [],
  activeDesignMeshId: null,
  activeDesignNodeIndex: null,
  shapeDesignerOpen: false,
  designClipboard: null,
  designUndoStack: [],
  designRedoStack: [],
  designLibrary: loadDesignLibrary(),

  // Flash Events (global light effects)
  flashEvents: [],
  effects: {},
  flashDefaults: {
    color: '#FBBF24',
    intensity: 1.0,
    fadeInTime: 0.05,
    fadeOutTime: 0.3,
    duration: 0.25,
    repeat: 1,
    frequency: 3,
  },
  editingFlashBeat: null,
  selectedFlashBeats: [],

  // ── Beatmap ───────────────────────────────────────────────────

  loadBeatmap: (beatmap) => {
    // A map opened is a fresh start for the colour cycle, the held-Ctrl+D move and a clear-bookmarks warning that was
    // left open: confirming it must never clear the new map's bookmarks.
    _colorSession = null;
    _dupSession = null;
    if (get().clearBookmarksPending || get().duplicateMoveActive) set({ clearBookmarksPending: false, duplicateMoveActive: false });
    // Restore any locally-edited notes for this map; otherwise a refresh would
    // reload the backend's original .synth and silently undo the user's edits
    // (including deletions). The draft is the complete edited difficulties set.
    const diffDraft = loadDifficultyDraft(beatmap.id);
    if (diffDraft) beatmap = { ...beatmap, difficulties: diffDraft };
    // The song info the mapper set by hand wins over the .synth's (the BPM in particular).
    const metaDraft = isSynthVrView(get().activeView) ? loadMetaDraft(beatmap.id) : null;
    if (metaDraft) beatmap = { ...beatmap, ...metaDraft };
    const diffs = Object.keys(beatmap.difficulties);
    const saved = savedPrefs.activeDifficulty;
    const active = (saved && diffs.includes(saved)) ? saved
      : diffs.includes('Expert') ? 'Expert'
      : diffs.includes('Hard') ? 'Hard'
      : diffs[0] || 'Expert';
    // Restore any locally-edited flash events (strobe settings) for this map;
    // otherwise the backend's original lights (no strobe fields) would win.
    const flashDraft = loadFlashDraft(beatmap.id);
    const effectDraft = isSynthVrView(get().activeView) ? loadEffectDraft(beatmap.id) : null;
    const bookmarkDraft = loadBookmarkDraft(beatmap.id);
    const designDraft = loadDesignDraft(beatmap.id);
    // Attribute this map to the editor currently on screen so VR/Console stay
    // independent (and a refresh can restore each editor's own last map).
    const st = get();
    const owner: EditorKey = st.mapOwner
      ?? (st.activeView === 'console' ? 'console' : 'synth-rider');
    set({
      beatmap, lastBeatmapId: beatmap.id, activeDifficulty: active,
      mapOwner: owner,
      lastBeatmapIdByEditor: { ...st.lastBeatmapIdByEditor, [owner]: beatmap.id },
      selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
      areaRange: null, areaGesture: null, areaRecall: null,
      bookmarks: bookmarkDraft ?? (beatmap.bookmarks || []),
      flashEvents: flashDraft ?? (beatmap.flashEvents || []),
      effects: effectDraft ?? (beatmap.effects || {}),
      // Local edits (draft) win; otherwise restore decor embedded in the .synth
      // (present when the map was exported from Synapse), else none.
      designShapes: designDraft ?? (beatmap.decorWalls || []),
      selectedDesignShapeIds: [],
      activeDesignMeshId: null,
      activeDesignNodeIndex: null,
      designUndoStack: [], designRedoStack: [],
    });
  },

  closeBeatmap: () => {
    const st = get();
    // Also forget it for the owning editor so switching away + back (or a
    // refresh) doesn't resurrect the closed map.
    const slots = { ...st.mapSlots };
    const byEditor = { ...st.lastBeatmapIdByEditor };
    if (st.mapOwner) { delete slots[st.mapOwner]; delete byEditor[st.mapOwner]; }
    set({
      beatmap: null, lastBeatmapId: null, selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
      areaRange: null, areaGesture: null, areaRecall: null,
      mapSlots: slots, lastBeatmapIdByEditor: byEditor,
      playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
    });
  },

  saveProjectDraft: () => {
    const st = get();
    const bm = st.beatmap;
    if (!bm) return;
    // Difficulty draft autosaves on a debounce; flush it now. Flash / decor /
    // bookmark drafts already persist on every change, so they're up to date.
    saveDifficultyDraft(bm.id, bm.difficulties);
    saveFlashDraft(bm.id, st.flashEvents);
    if (isSynthVrView(st.activeView)) saveEffectDraft(bm.id, st.effects);
    saveBookmarkDraft(bm.id, st.bookmarks);
    saveDesignDraft(bm.id, st.designShapes);
  },

  clearCurrentDifficulty: () => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Clear current difficulty');
    const diffName = state.activeDifficulty;
    const diff = state.beatmap.difficulties[diffName];
    if (!diff) return;
    const cleared: Difficulty = {
      ...diff,
      notes: [],
      slides: [],
      crouches: [],
      jumps: [],
      note_count: 0,
      rail_count: 0,
      slide_count: 0,
      crouch_count: 0,
      jump_count: 0,
    };
    set({
      beatmap: {
        ...state.beatmap,
        difficulties: { ...state.beatmap.difficulties, [diffName]: cleared },
      },
      selectedNoteIds: [],
    });
  },

  clearAllDifficulties: () => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Clear all difficulties');
    const clearedDiffs: Record<string, Difficulty> = {};
    for (const [name, diff] of Object.entries(state.beatmap.difficulties)) {
      clearedDiffs[name] = {
        ...diff,
        notes: [],
        slides: [],
        crouches: [],
        jumps: [],
        note_count: 0,
        rail_count: 0,
        slide_count: 0,
        crouch_count: 0,
        jump_count: 0,
      };
    }
    set({
      beatmap: { ...state.beatmap, difficulties: clearedDiffs },
      selectedNoteIds: [],
    });
  },

  setActiveDifficulty: (name) => set(s => ({ activeDifficulty: name, selectedNoteIds: [], selectedSlideIds: [], ..._areaCleared(s) })),

  // Create an empty difficulty (default or custom-named) and switch to it.
  addDifficulty: (name) => {
    const state = get();
    if (!state.beatmap) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed in state.beatmap.difficulties) {
      if (trimmed) set(s => ({ activeDifficulty: trimmed, selectedNoteIds: [], ..._areaCleared(s) }));
      return;
    }
    pushUndo(state, `Add difficulty ${trimmed}`);
    const empty: Difficulty = {
      name: trimmed,
      note_count: 0, rail_count: 0, slide_count: 0, crouch_count: 0, jump_count: 0,
      duration_seconds: 0, duration_beats: 0,
      notes: [], slides: [], crouches: [], jumps: [],
    };
    set({
      beatmap: { ...state.beatmap, difficulties: { ...state.beatmap.difficulties, [trimmed]: empty } },
      activeDifficulty: trimmed,
      selectedNoteIds: [],
      ..._areaCleared(state),
    });
  },

  updateMeta: (meta) => {
    const { beatmap } = get();
    if (!beatmap) return;
    set({ beatmap: { ...beatmap, ...meta } });
    if (isSynthVrView(get().activeView)) saveMetaDraft(beatmap.id, meta);
  },

  requestBpmChange: (bpm) => {
    const { beatmap, activeView } = get();
    if (!beatmap || !Number.isFinite(bpm) || bpm <= 0 || bpm === beatmap.bpm) return;
    // Lights and bookmarks count: "keep times" moves their beats.
    const anything = hasTimedObjects(beatmap.difficulties) || get().bookmarks.length > 0 || get().flashEvents.length > 0
      || Object.values(get().effects).some(b => b.length > 0);
    if (!isSynthVrView(activeView) || !anything) { get().updateMeta({ bpm }); return; }
    set({ pendingBpmChange: bpm });
  },

  cancelBpmChange: () => set({ pendingBpmChange: null }),

  applyBpmChange: (mode) => {
    const state = get();
    const bpm = state.pendingBpmChange;
    const { beatmap } = state;
    set({ pendingBpmChange: null });
    if (!beatmap || bpm == null || bpm === beatmap.bpm) return;
    // Either way the objects' times or beats change under the new tempo, so what the undo history holds is no
    // longer a state this map can return to: it is cleared with the change.
    const difficulties = mode === 'times'
      ? rebeatDifficulties(beatmap.difficulties, bpm)                              // same times, new beats
      : rescaleDifficulties(beatmap.difficulties, beatmap.bpm || 120, bpm);        // same beats, new times
    set({ beatmap: { ...beatmap, bpm, difficulties }, undoStack: [], redoStack: [] });
    if (mode === 'times') {
      // Lights and bookmarks are stored in beats, so keeping their times moves their beats too.
      const k = bpm / (beatmap.bpm || 120);
      const bookmarks = get().bookmarks.map(b => ({ ...b, beat: b.beat * k }));
      const flashEvents = get().flashEvents.map(e => ({ ...e, time_beats: e.time_beats * k }));
      const effects = Object.fromEntries(Object.entries(get().effects).map(([d, beats]) => [d, beats.map(b => b * k)]));
      set({ bookmarks, flashEvents, effects });
      saveBookmarkDraft(beatmap.id, bookmarks);
      saveFlashDraft(beatmap.id, flashEvents);
      if (isSynthVrView(get().activeView)) saveEffectDraft(beatmap.id, effects);
    }
    if (isSynthVrView(get().activeView)) saveMetaDraft(beatmap.id, { bpm });
    get().setCurrentTime(state.playback.currentTime);
  },

  // ── Playback ──────────────────────────────────────────────────

  setPlaying: (playing) => {
    if (!playing) {
      // On pause: snap to the closest beat step line in the offset-adjusted CHART
      // beat frame (the frame the scene/placement use). beatmap.offset is in ms.
      // Uses the visible Step (scrollStep) so pausing lands on the same grid the
      // mapper is looking at — see snapBeat.
      const { scrollStep, beatmap, playback } = get();
      const bpm = beatmap?.bpm || 120;
      const offsetSec = (beatmap?.offset || 0) / 1000;
      const step = scrollStep > 0 ? scrollStep : 0.25;
      const chartBeat = ((playback.currentTime - offsetSec) / 60) * bpm;
      const snappedChartBeat = Math.max(0, Math.round(chartBeat / step) * step);
      // Audio time for that chart beat = chartBeat*60/bpm + offset.
      const snappedTime = (snappedChartBeat / bpm) * 60 + offsetSec;
      set({ playback: { ...playback, playing: false, currentTime: snappedTime, currentBeat: snappedChartBeat } });
    } else {
      set(s => ({ playback: { ...s.playback, playing: true } }));
    }
  },

  setCurrentTime: (time) => {
    const { beatmap, playback, scrollStep } = get();
    const bpm = beatmap?.bpm || 120;
    const offsetSec = (beatmap?.offset || 0) / 1000; // offset is stored in ms
    let currentBeat = Math.max(0, ((time - offsetSec) / 60) * bpm);
    let t = time;
    // While PAUSED the playhead always lands ON the step grid. The snap grid and
    // the bar lines are drawn at absolute beats, so a playhead sitting between
    // steps (after playback, a timeline click, a restored session, or any map
    // with an offset) made them look shifted off the trigger line — reviewers
    // read that as "the snap line isn't on the trigger" and "the bar line is a
    // step behind". Snapping here keeps the trigger ON the grid, which is what
    // "snap is measured from the trigger line" means. Playback stays exact.
    if (!playback.playing) {
      const step = scrollStep > 0 ? scrollStep : 0.25;
      currentBeat = Math.max(0, Math.round(currentBeat / step) * step);
      t = (currentBeat / bpm) * 60 + offsetSec;
    }
    set(s => ({ playback: { ...s.playback, currentTime: t, currentBeat } }));
  },

  setCurrentBeat: (beat) => {
    const { beatmap } = get();
    const bpm = beatmap?.bpm || 120;
    const offsetSec = (beatmap?.offset || 0) / 1000; // offset is stored in ms
    const clampedBeat = Math.max(0, beat);
    const time = (clampedBeat / bpm) * 60 + offsetSec;
    set(s => ({ playback: { ...s.playback, currentTime: time, currentBeat: clampedBeat } }));
  },

  setDuration: (duration) => set(s => ({ playback: { ...s.playback, duration } })),

  // ── Tools ─────────────────────────────────────────────────────

  setToolMode: (mode, temporary) => {
    const state = get();
    // If rail placement is active and this isn't a temporary switch (e.g. Ctrl-hold),
    // finish the current rail when switching to ANY different tool —
    // including switching between different rail tools (e.g. rail_right → rail_left)
    if (state.railPlacementActive && !temporary && mode !== state.toolMode) {
      get().finishRailPlacement();
    }

    // Auto-place/remove obstacle logic
    const OBSTACLE_WALL_MAP: Record<string, string> = {
      wall_left: 'wall_left', wall_right: 'wall_right',
      wall_diag_left: 'angle_left', wall_diag_right: 'angle_right',
      wall_center: 'center', crouch: 'crouch',
    };

    const prevWallType = OBSTACLE_WALL_MAP[state.toolMode];
    const newWallType = OBSTACLE_WALL_MAP[mode];
    const toObstacleTool = !!newWallType;

    // If toggling OFF an obstacle tool (clicking same tool again → select),
    // remove the obstacle nearest to the playhead that matches the wall type
    if (prevWallType && !toObstacleTool && !temporary && state.beatmap) {
      const currentTime = state.playback.currentTime;
      const timeMs = currentTime * 1000;
      const diff = state.beatmap.difficulties?.[state.activeDifficulty];
      if (diff?.slides) {
        const threshold = 50; // 50ms tolerance
        const idx = diff.slides.findIndex(s =>
          s.wall_type === prevWallType && Math.abs(s.time_ms - timeMs) < threshold
        );
        if (idx >= 0) {
          get().removeSlide(idx);
        }
      }
    }

    // A temporary switch to Select (Ctrl held) remembers the tool it left; any other switch forgets it.
    set({
      toolMode: mode, toolBeforeCtrl: temporary && mode === 'select' && state.toolMode !== 'select' ? state.toolMode : null,
      lastNoteKind: toolNoteKind(mode) ?? state.lastNoteKind,
    });

    // Auto-place an obstacle at the playhead when an obstacle tool is selected
    // If an obstacle of the same type already exists at the playhead, remove it instead (toggle)
    if (newWallType && !temporary && state.beatmap) {
      const bpm = state.beatmap.bpm || 120;
      const currentTime = state.playback.currentTime;
      const rawBeat = (currentTime / 60) * bpm;
      const timeMs = currentTime * 1000;
      const timeSeconds = currentTime;
      const diff = state.beatmap.difficulties?.[state.activeDifficulty];
      const threshold = 50; // 50ms tolerance
      const existingIdx = diff?.slides?.findIndex(s =>
        s.wall_type === newWallType && Math.abs(s.time_ms - timeMs) < threshold
      ) ?? -1;

      if (existingIdx >= 0) {
        // Toggle off — remove the existing obstacle of this type at the playhead
        get().removeSlide(existingIdx);
      } else {
        // Place a new obstacle
        const slide: import('../types/beatmap').Slide = {
          time_ms: timeMs, time_seconds: timeSeconds, time_beats: rawBeat,
          slide_type: 0, wall_type: newWallType, initialized: true,
          position: newWallPosition(timeMs), z_rotation: 0,
        };
        get().addSlide(slide);
      }
    }
  },
  setSnapDivision: (snap) => set({ snapDivision: snap }),

  // ── Selection (array-based) ───────────────────────────────────

  selectNote: (index, additive = false) => set(s => {
    if (additive) {
      const has = s.selectedNoteIds.includes(index);
      return { selectedNoteIds: has ? s.selectedNoteIds.filter(i => i !== index) : [...s.selectedNoteIds, index], ..._areaCleared(s) };
    }
    // Non-additive click selects just this note — clear any obstacle selection
    // too so single-click selection is unambiguous.
    return { selectedNoteIds: [index], selectedSlideIds: [], ..._areaCleared(s) };
  }),

  // A click or marquee that picks something replaces an area range (SRVR-0315 AC14).
  selectNotes: (indices) => set(s => ({ selectedNoteIds: indices, ...(indices.length ? _areaCleared(s) : {}) })),
  selectSlides: (indices) => set(s => ({ selectedSlideIds: indices, ...(indices.length ? _areaCleared(s) : {}) })),
  clearSelection: () => set({ selectedNoteIds: [], selectedSlideIds: [], selectedCrouches: [], selectedSquares: [], selectedTriangles: [] }),
  loopRegion: null,
  setLoopRegion: (r) => set({ loopRegion: r }),

  // ── Area selection (the desktop editor's Z range) ─────────────
  areaRange: null,
  areaGesture: null,
  areaRecall: null,
  areaFilters: DEFAULT_AREA_FILTERS,

  // Track.SelectionAreaProcessing / ToggleSelectionArea: no range → start one at the playhead; the
  // playhead on an end of a multi-beat range → grab that end; anywhere else → clear.
  areaKeyDown: () => {
    const s = get();
    if (s.areaGesture || s.playback.playing || s.railPlacementActive) return;
    const p = s.playback.currentBeat;
    const r = s.areaRange;
    if (!r) {
      set({ areaRange: { startBeat: p, endBeat: p }, areaGesture: { anchorBeat: p, pressBeat: p, grabbed: false },
        selectedNoteIds: [], selectedSlideIds: [] });
      return;
    }
    const single = nearBeat(r.startBeat, r.endBeat);
    if (!single && nearBeat(p, r.startBeat)) set({ areaGesture: { anchorBeat: r.endBeat, pressBeat: p, grabbed: true } });
    else if (!single && nearBeat(p, r.endBeat)) set({ areaGesture: { anchorBeat: r.startBeat, pressBeat: p, grabbed: true } });
    else set(_areaCleared(s));
  },

  areaKeyUp: () => {
    const s = get();
    const g = s.areaGesture;
    if (!g) return;
    const p = s.playback.currentBeat;
    // A grabbed end released where it was pressed: the desktop editor reads that as "clear".
    if (g.grabbed && nearBeat(p, g.pressBeat)) { set({ ..._areaCleared(s), areaGesture: null }); return; }
    set({ areaRange: effectiveAreaRange(s.areaRange, g, p), areaGesture: null });
  },

  areaExtendOrRecall: () => {
    const s = get();
    if (s.areaGesture) return;
    const r = s.areaRange;
    if (!r) {
      if (s.areaRecall) set({ areaRange: { ...s.areaRecall }, selectedNoteIds: [], selectedSlideIds: [] });
      return;
    }
    const p = s.playback.currentBeat;
    set({ areaRange: p >= r.startBeat ? { startBeat: r.startBeat, endBeat: p } : { startBeat: p, endBeat: r.endBeat } });
  },

  clearAreaRange: () => set(s => _areaCleared(s)),

  areaClipboard: null,

  areaDelete: () => {
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters);
    const lights = lightsInRange(ctx.state.flashEvents, ctx.range, ctx.state.areaFilters);
    const fx = effectsInRange(_activeEffects(ctx.state), ctx.range, ctx.state.areaFilters);
    // Track.cs DeleteBeatOrSelection removes the jumps in range whatever the Selection Options say; a
    // range never holds them otherwise (SRVR-0317), so Delete is the one operation that takes them.
    const jumpKept = (t: number) => !inRange(msToBeat(t, ctx.bpm), ctx.range);
    const jumpsHit = (ctx.diff.jumps ?? []).some(t => !jumpKept(t));
    if (isEmptyContents(c) && !lights.length && !fx.length && !jumpsHit) {
      // The desktop clears the selection marker after every Delete, even one that found nothing.
      if (ctx.state.areaRange || ctx.state.areaGesture) set(_areaCleared(ctx.state));
      return;
    }
    pushUndo(ctx.state, 'Delete selection', lights.length > 0 || fx.length > 0);
    const removed = removeContents(ctx.diff, c);
    _writeAreaDiff(set, get, jumpsHit ? { ...removed, jumps: removed.jumps.filter(jumpKept), jump_count: removed.jumps.filter(jumpKept).length } : removed, {
      ..._areaCleared(ctx.state),
      ...(lights.length ? _areaLights(ctx.state, removeLights(ctx.state.flashEvents, lights)) : {}),
      ...(fx.length ? _areaEffects(ctx.state, removeEffects(_activeEffects(ctx.state), fx)) : {}),
    });
    playDeleteSound();
  },

  areaCopy: () => {
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters);
    const lights = lightsInRange(ctx.state.flashEvents, ctx.range, ctx.state.areaFilters);
    const fx = effectsInRange(_activeEffects(ctx.state), ctx.range, ctx.state.areaFilters);
    if (isEmptyContents(c) && !lights.length && !fx.length) return;
    set({ areaClipboard: _areaClip(ctx, copyContents(ctx.diff, c), lights, fx), ..._areaCleared(ctx.state) });
  },

  areaCut: () => {
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters);
    const lights = lightsInRange(ctx.state.flashEvents, ctx.range, ctx.state.areaFilters);
    const fx = effectsInRange(_activeEffects(ctx.state), ctx.range, ctx.state.areaFilters);
    if (isEmptyContents(c) && !lights.length && !fx.length) return;
    pushUndo(ctx.state, 'Cut selection', lights.length > 0 || fx.length > 0);
    _writeAreaDiff(set, get, removeContents(ctx.diff, c), {
      areaClipboard: _areaClip(ctx, copyContents(ctx.diff, c), lights, fx), ..._areaCleared(ctx.state),
      ...(lights.length ? _areaLights(ctx.state, removeLights(ctx.state.flashEvents, lights)) : {}),
      ...(fx.length ? _areaEffects(ctx.state, removeEffects(_activeEffects(ctx.state), fx)) : {}),
    });
    playDeleteSound();
  },

  // Track.PasteAction in its default mode (pasteDeletesExistingRange): clear the target span of every
  // kind, whatever the toggles, then land the clipboard with its anchor on the playhead.
  areaPaste: (mirrored) => {
    const state = get();
    const clip = state.areaClipboard;
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff || !clip) return;
    const bpm = state.beatmap.bpm || 120;
    const target = state.playback.currentBeat;
    // A paste lands at or after its target beat, so a target inside the first two seconds is refused whole.
    if (_tooEarlyToPlace(state, (target / (bpm / 60)) * 1000)) return;
    let objs: AreaObjects = retimeObjects(clip, clip.anchorBeat, target, bpm);
    if (mirrored) objs = flipObjects(objs, 'x', bpm);
    // A paste never lands past the song's end — only knowable once the audio's duration is.
    if (state.playback.duration > 0) objs = objectsUpTo(objs, _songEndBeat(state, diff), bpm);
    const span = { startBeat: target, endBeat: target + clip.lengthBeats };
    // Lights (SRVR-0320 AC9): the delete-existing clear takes the span's lights only while Lights is on
    // (Track.cs 14854), then the copied lights land in every mode alike. Mirroring cannot move a light.
    const clipLights = clip.lights ?? [];
    const cleared = state.pasteMode === 'deleteExisting'
      ? removeLights(state.flashEvents, lightsInRange(state.flashEvents, span, state.areaFilters))
      : state.flashEvents;
    const lastBeat = state.playback.duration > 0 ? _songEndBeat(state, diff) : Infinity;
    const nextLights = pasteLights(cleared, clipLights, clip.anchorBeat, target, lastBeat);
    const lightsChanged = nextLights.length !== state.flashEvents.length
      || nextLights.some((e, i) => e !== state.flashEvents[i]);
    // Effects land the same way, in the active difficulty.
    const fxNow = _activeEffects(state);
    const fxCleared = state.pasteMode === 'deleteExisting'
      ? removeEffects(fxNow, effectsInRange(fxNow, span, state.areaFilters))
      : fxNow;
    const nextFx = pasteEffects(fxCleared, clip.effects ?? [], clip.anchorBeat, target, lastBeat);
    const fxChanged = nextFx.length !== fxNow.length || nextFx.some((b, i) => b !== fxNow[i]);
    pushUndo(state, mirrored ? 'Paste mirrored selection' : 'Paste selection', lightsChanged || fxChanged);
    _writeAreaDiff(set, get, pasteObjects(diff, objs, span, state.pasteMode, bpm), {
      ..._areaCleared(state),
      ...(lightsChanged ? _areaLights(state, nextLights) : {}),
      ...(fxChanged ? _areaEffects(state, nextFx) : {}),
    });
    if (countObjects(objs) || nextLights.length > cleared.length || nextFx.length > fxCleared.length) playPlaceSound();
  },

  pasteMode: 'deleteExisting',
  setPasteMode: (mode) => set({ pasteMode: mode }),
  cyclePasteMode: () => set(s => ({ pasteMode: PASTE_MODES[(PASTE_MODES.indexOf(s.pasteMode) + 1) % PASTE_MODES.length] })),

  areaFlip: (axis) => {
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters);
    if (isEmptyContents(c)) return;
    // Track.cs FlipSelected, X flip with exactly one of Left and Right ticked: a beat holding both hands would
    // end with two notes of one hand once the ticked one changes hand, so the beat keeps one note. The flipped
    // ticked note replaces the other, or with Prioritize Existing the other stays and the ticked one goes.
    const pairDrop = new Set<Note>();
    const f = ctx.state.areaFilters;
    if (axis === 'x' && f.left_hand !== f.right_hand) {
      const tickedKind = f.left_hand ? 'left_hand' : 'right_hand';
      const flippable = new Set(c.notes);
      const seen = new Set<number>();
      ctx.diff.notes.forEach((n, i) => {
        if (seen.has(i) || !inRange(n.time_beats, ctx.range)) return;
        const beat = ctx.diff.notes.map((m, j) => j).filter(j => nearBeat(ctx.diff.notes[j].time_beats, n.time_beats));
        beat.forEach(j => seen.add(j));
        if (beat.length !== 2) return;
        const ticked = beat.find(j => noteFilterKind(ctx.diff.notes[j]) === tickedKind);
        const other = beat.find(j => j !== ticked);
        if (ticked === undefined || other === undefined || !flippable.has(ticked)) return;
        const otherKind = noteFilterKind(ctx.diff.notes[other]);
        if (otherKind !== 'left_hand' && otherKind !== 'right_hand') return;
        if (ctx.state.pasteMode === 'prioritizeExisting') { pairDrop.add(ctx.diff.notes[ticked]); flippable.delete(ticked); }
        else pairDrop.add(ctx.diff.notes[other]);
      });
      c.notes = c.notes.filter(i => flippable.has(i));
    }
    pushUndo(ctx.state, axis === 'x' ? 'Flip selection' : 'Flip selection (Y)');
    const flipped = flipObjects(copyContents(ctx.diff, c), axis, ctx.bpm);
    const base = removeContents(ctx.diff, c);
    const kept = pairDrop.size ? withCounts({ ...base, notes: base.notes.filter(n => !pairDrop.has(n)) }) : base;
    // A flip changes no time, so the range stays as it was (SRVR-0321 AC10).
    _writeAreaDiff(set, get, insertObjects(kept, flipped), {});
    // Track.cs adds each flipped rail to the scene, which snaps the notes concurrent to it when Snap to Rail is on,
    // including any outside the range that were not flipped.
    const rails = flipped.notes.filter(n => n.has_rail);
    if (rails.length) _resnapToRails(set, get, rails);
  },

  // ── Tools box (Synth VR): Note Rotation, Scaling, Rail Smoothing ──────────
  //
  // A slider preview writes through the store, so the scene shows it with no change of its own; the
  // session keeps the difficulty it started from, so every preview is computed from that base and
  // repeated previews never accumulate rounding (SRVR-0352 AC5, SRVR-0354 AC3).

  toolPreview: null,

  rotateArea: (deg) => {
    if (!_toolsAllowed(get()) || !deg) return;
    if (_toolSession) get().commitToolPreview();
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = _notesOnly(objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters));
    if (!c.notes.length) return;
    pushUndo(ctx.state, 'Rotate selection');
    _writeAreaDiff(set, get, insertObjects(removeContents(ctx.diff, c), rotateObjects(copyContents(ctx.diff, c), deg)), {});
  },

  previewRotateArea: (deg) => {
    const state = get();
    if (!_toolsAllowed(state)) return;
    if (_toolSession && _toolSession.kind !== 'rotate') get().cancelToolPreview();
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const base = _toolSession?.base ?? ctx.diff;
    const c = _notesOnly(objectsInRange(base, ctx.range, ctx.bpm, ctx.state.areaFilters));
    if (!c.notes.length) return;
    if (!_toolSession) _openToolSession('rotate', ctx.state, base, 'Rotate selection');
    set({ toolPreview: 'rotate' });
    _writeAreaDiff(set, get, insertObjects(removeContents(base, c), rotateObjects(copyContents(base, c), deg)), {});
  },

  scaleArea: (xPct, yPct) => {
    if (!_toolsAllowed(get()) || !isFinite(xPct) || !isFinite(yPct)) return;
    if (xPct === 100 && yPct === 100) return;
    if (_toolSession) get().commitToolPreview();
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const c = objectsInRange(ctx.diff, ctx.range, ctx.bpm, ctx.state.areaFilters);
    if (isEmptyContents(c)) return;
    pushUndo(ctx.state, 'Scale selection');
    _writeAreaDiff(set, get, insertObjects(removeContents(ctx.diff, c), scaleObjects(copyContents(ctx.diff, c), xPct, yPct)), {});
  },

  // Unlike the desktop's Scaler, which changed nothing until OK, every change previews; Apply keeps it.
  previewScaleArea: (xPct, yPct) => {
    if (!_toolsAllowed(get()) || !isFinite(xPct) || !isFinite(yPct)) return;
    if (_toolSession && _toolSession.kind !== 'scale') get().cancelToolPreview();
    // Back at 100 % by 100 % there is nothing to show, so the preview is put back rather than kept as a no-op.
    if (xPct === 100 && yPct === 100) { get().cancelToolPreview(); return; }
    const ctx = _areaTarget(get());
    if (!ctx) return;
    const base = _toolSession?.base ?? ctx.diff;
    const c = objectsInRange(base, ctx.range, ctx.bpm, ctx.state.areaFilters);
    if (isEmptyContents(c)) return;
    if (!_toolSession) _openToolSession('scale', ctx.state, base, 'Scale selection');
    set({ toolPreview: 'scale' });
    _writeAreaDiff(set, get, insertObjects(removeContents(base, c), scaleObjects(copyContents(base, c), xPct, yPct)), {});
  },

  previewRailSmoothing: (opts) => {
    const state = get();
    if (!_toolsAllowed(state)) return 'no-rail';
    if (_toolSession && _toolSession.kind !== 'smooth') get().cancelToolPreview();
    const s = get();
    const base = _toolSession?.base ?? s.beatmap!.difficulties[s.activeDifficulty];
    if (!base) return 'no-rail';
    const index = _toolSession?.railIndex ?? _railOnPlayhead(s, base);
    if (index === null || index === undefined) return 'no-rail';
    if (!_toolSession) _openToolSession('smooth', s, base, 'Smooth rail', index);
    set({ toolPreview: 'smooth' });
    _writeAreaDiff(set, get, _smoothedDiff(base, index, opts, s.beatmap!.bpm || 120), {});
    return 'ok';
  },

  applyRailSmoothing: (opts) => {
    const state = get();
    if (!_toolsAllowed(state)) return 'no-rail';
    if (_toolSession?.kind === 'smooth') { get().commitToolPreview(); return 'ok'; }
    if (_toolSession) get().commitToolPreview();
    const s = get();
    const diff = s.beatmap!.difficulties[s.activeDifficulty];
    if (!diff) return 'no-rail';
    const index = _railOnPlayhead(s, diff);
    if (index === null) return 'no-rail';
    pushUndo(s, 'Smooth rail');
    _writeAreaDiff(set, get, _smoothedDiff(diff, index, opts, s.beatmap!.bpm || 120), {});
    return 'ok';
  },

  commitToolPreview: () => {
    if (!_toolSession) return;
    _toolSession = null;
    set({ toolPreview: null });
  },

  cancelToolPreview: () => {
    const session = _toolSession;
    if (!session) return;
    _toolSession = null;
    // Put back into the difficulty the preview was opened on, which a difficulty switch has just left.
    const state = get();
    if (state.beatmap && state.beatmap.id === session.beatmapId) {
      if (state.activeDifficulty === session.diffName) _writeAreaDiff(set, get, session.base, {});
      else set({ beatmap: { ...state.beatmap, difficulties: { ...state.beatmap.difficulties, [session.diffName]: session.base } } });
    }
    const cur = get();
    if (session.entry && cur.undoStack[cur.undoStack.length - 1] === session.entry) {
      set({ undoStack: cur.undoStack.slice(0, -1), redoStack: session.prevRedo });
    }
    set({ toolPreview: null });
  },

  setAreaFilters: (kinds, on) => set(s => {
    const next = { ...s.areaFilters };
    for (const k of kinds) next[k] = on;
    return { areaFilters: next };
  }),

  nudgeSelection: (dxCells, dyCells) => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const SCALE = GRID_SCALE * 10;
    const stepX = (state.gridWidth / Math.max(1, state.gridDivisionsH)) / SCALE;
    const stepY = (state.gridHeight / Math.max(1, state.gridDivisionsV)) / SCALE;
    pushUndo(state, `Move ${state.selectedNoteIds.length} notes`);
    const sel = new Set(state.selectedNoteIds);
    // X/Y nudge only — time is unchanged, so no re-sort and indices stay valid.
    const newNotes = diff.notes.map((n: Note, i: number) => {
      if (!sel.has(i)) return n;
      // A group nudged in Synth VR may leave the grid; a single note stays inside it.
      const free = isSynthVrView(state.activeView) && sel.size > 1;
      const nx = n.position[0] + dxCells * stepX, ny = n.position[1] + dyCells * stepY;
      const [cx, cy] = free ? [nx, ny] : _clampNotePos(state, nx, ny);
      return { ...n, position: [cx, cy, n.position[2]] as [number, number, number] };
    });
    setDiffNotes(set, get, newNotes);
  },
  setHoveredBeat: (beat) => set({ hoveredBeat: beat }),
  setScrollStep: (step) => set({ scrollStep: Math.min(step, MAX_SCROLL_STEP) }),

  cycleStepType: () => {
    const { stepType, scrollStep } = get();
    const order: StepType[] = ['binary', 'ternary', 'all'];
    const next = order[(order.indexOf(stepType) + 1) % order.length];
    // Remap the current step to the nearest value in the new family so the grid
    // density stays roughly the same across the switch.
    const opts = stepsForType(next);
    let best = opts[0], bestDist = Infinity;
    for (const o of opts) {
      const d = Math.abs(o - scrollStep);
      if (d < bestDist) { bestDist = d; best = o; }
    }
    set({ stepType: next, scrollStep: best });
  },
  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
  setMusicVolume: (vol) => set({ musicVolume: Math.max(0, Math.min(1, vol)) }),
  setSfxVolume: (vol) => set({ sfxVolume: Math.max(0, Math.min(1, vol)) }),
  setCameraView: (view) => set({ cameraView: view }),
  setTimelineZoom: (zoom) => set({ timelineZoom: Math.max(0.25, Math.min(16, zoom)) }),
  setNoteJumpSpeed: (njs) => set({ noteJumpSpeed: Math.max(0.1, Math.min(20, njs)) }),
  setMapLocked: (locked) => set({ mapLocked: locked }),
  setLoopEnabled: (enabled) => set({ loopEnabled: enabled }),
  setMetronomeEnabled: (enabled) => set({ metronomeEnabled: enabled }),
  setWaveformVisible: (visible) => set({ waveformVisible: visible }),
  setShowHumanFigure: (show) => set({ showHumanFigure: show }),
  setFigureMode: (mode) => set({ figureMode: mode, showHumanFigure: mode !== 'off' }),
  setRenderMode: (mode) => set({ renderMode: mode }),
  setHeatmapEnabled: (enabled) => set({ heatmapEnabled: enabled }),
  setGameRailRendering: (on) => set({ gameRailRendering: on }),
  setIntensityData: (data) => set({ intensityData: data }),
  setArmSpanMeters: (m) => {
    const prev = get().armSpanMeters;
    set({ armSpanMeters: m });
    // Re-fetch difficulty if arm span actually changed meaningfully (>1cm).
    if (m && (!prev || Math.abs(prev - m) > 0.01)) {
      get().refreshPatternDifficulty();
    }
  },
  refreshPatternDifficulty: async () => {
    const state = get();
    const beatmapId = state.beatmap?.id;
    const difficulty = state.activeDifficulty;
    if (!beatmapId || !difficulty) {
      set({ patternDifficulty: null });
      return;
    }
    if (state.patternDifficultyLoading) return;
    set({ patternDifficultyLoading: true });
    try {
      const report = await fetchPatternDifficulty({
        beatmapId,
        difficulty,
        armSpan: state.armSpanMeters ?? undefined,
      });
      // Guard against a newer fetch having superseded this one.
      const cur = get();
      if (cur.beatmap?.id === beatmapId && cur.activeDifficulty === difficulty) {
        set({ patternDifficulty: report, patternDifficultyLoading: false });
      } else {
        set({ patternDifficultyLoading: false });
      }
    } catch (err) {
      console.warn('[patternDifficulty] fetch failed:', err);
      set({ patternDifficulty: null, patternDifficultyLoading: false });
    }
  },
  saveCustomCamera: (pos, rot) => set({ customCameraPosition: pos, customCameraRotation: rot, saveCustomCameraRequested: false }),
  setCustomCameraPosition: (pos) => set({ customCameraPosition: pos }),
  setCustomCameraRotation: (rot) => set({ customCameraRotation: rot }),
  clearCustomCamera: () => set({ customCameraPosition: null, customCameraRotation: null }),
  setVrCamName: (index, name) => set(s => ({
    vrCams: s.vrCams.map((c, i) => (i === index ? { ...c, name } : c)),
  })),
  saveVrCamPose: (index, pos, rot) => set(s => ({
    vrCams: s.vrCams.map((c, i) => (i === index ? { ...c, position: pos, rotation: rot } : c)),
  })),
  setDefaultCameraPosition: (pos) => set({ defaultCameraPosition: pos }),
  setDefaultCameraRotation: (rot) => set({ defaultCameraRotation: rot }),
  setLeftCameraPosition: (pos) => set({ leftCameraPosition: pos }),
  setLeftCameraRotation: (rot) => set({ leftCameraRotation: rot }),
  setRightCameraPosition: (pos) => set({ rightCameraPosition: pos }),
  setRightCameraRotation: (rot) => set({ rightCameraRotation: rot }),
  resetCameraPreset: (preset) => {
    if (preset === 'default') set({ defaultCameraPosition: null, defaultCameraRotation: null });
    else if (preset === 'left') set({ leftCameraPosition: null, leftCameraRotation: null });
    else if (preset === 'right') set({ rightCameraPosition: null, rightCameraRotation: null });
  },
  setFlyModeActive: (active) => set({ isFlyModeActive: active }),
  requestSaveCustomCamera: () => set({ saveCustomCameraRequested: true }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setBenchmarkOpen: (open) => set({ benchmarkOpen: open }),
  setAdminUsersOpen: (open) => set({ adminUsersOpen: open }),
  setMannequinSettings: (p) => set(s => ({ mannequinSettings: { ...s.mannequinSettings, ...p } })),
  setGameplaySettings: (p) => set(s => ({ gameplaySettings: { ...s.gameplaySettings, ...p } })),
  setObstacleConfig: (key, update) => set(s => ({
    gameplaySettings: {
      ...s.gameplaySettings,
      obstacleConfigs: {
        ...s.gameplaySettings.obstacleConfigs,
        [key]: { ...s.gameplaySettings.obstacleConfigs[key], ...update },
      },
    },
  })),
  resetMannequinSettings: () => set({ mannequinSettings: { ...DEFAULT_MANNEQUIN } }),
  resetGameplaySettings: () => set({ gameplaySettings: { ...DEFAULT_GAMEPLAY } }),
  setInterfaceSettings: (p) => set(s => ({ interfaceSettings: { ...s.interfaceSettings, ...p } })),
  resetInterfaceSettings: () => set({ interfaceSettings: { ...DEFAULT_INTERFACE } }),
  setParticleSettings: (p) => set(s => ({ particleSettings: { ...s.particleSettings, ...p } })),
  resetParticleSettings: () => set({ particleSettings: { ...DEFAULT_PARTICLES } }),

  // Per-preset performance knobs. New record + new preset object on every write —
  // reference inequality is what drives the persistence subscription below.
  setPerformancePreset: (preset, patch) => set(s => ({
    performanceSettings: {
      ...s.performanceSettings,
      [preset]: {
        ...s.performanceSettings[preset],
        ...patch,
        flowFx: { ...s.performanceSettings[preset].flowFx, ...patch.flowFx },
      },
    },
  })),
  resetPerformancePreset: (preset) => set(s => ({
    performanceSettings: {
      ...s.performanceSettings,
      [preset]: { ...DEFAULT_PERFORMANCE[preset], flowFx: { ...DEFAULT_PERFORMANCE[preset].flowFx } },
    },
  })),
  resetPerformanceSettings: () => set({ performanceSettings: sanitizePerformanceSettings(undefined) }),
  setFileManagerOpen: (open) => set({ fileManagerOpen: open }),
  setEditInfoOpen: (open) => set({ editInfoOpen: open }),
  minimapCam: savedPrefs.minimapCam ?? { height: 7.4, posZ: 1.7, tilt: 40 },
  setMinimapCam: (patch) => set(s => ({ minimapCam: { ...s.minimapCam, ...patch } })),
  minimapCamOpen: false,
  setMinimapCamOpen: (open) => set({ minimapCamOpen: open }),
  minimapZoom: savedPrefs.minimapZoom ?? 1,
  setMinimapZoom: (v) => set({ minimapZoom: Math.max(0.5, Math.min(2, v)) }),
  cameraOptionsOpen: false,
  setCameraOptionsOpen: (open) => set({ cameraOptionsOpen: open }),
  setNewMapOpen: (open) => set({ newMapOpen: open }),
  setLeftSidebarOpen: (open) => set({ leftSidebarOpen: open }),
  setLeftSidebarWidth: (w) => set({ leftSidebarWidth: w }),
  setRightSidebarOpen: (open) => set({ rightSidebarOpen: open }),
  setUiHidden: (hidden) => set({ uiHidden: hidden }),
  setProjectSaved: (v) => set({ projectSaved: v }),
  setRightSidebarWidth: (w) => set({ rightSidebarWidth: w }),
  setBottomPanelOpen: (open) => set({ bottomPanelOpen: open }),
  setBottomPanelHeight: (h) => set({ bottomPanelHeight: h }),

  // ── Note Editing ──────────────────────────────────────────────

  addNote: (note, offGrid = false) => {
    const state = get();
    if (!state.beatmap) return;
    if (_tooEarlyToPlace(state, note.time_ms)) return;
    pushUndo(state, 'Add note');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // Clamp position to grid bounds
    const [cx, cy] = offGrid ? [note.position[0], note.position[1]] : _clampNotePos(state, note.position[0], note.position[1]);
    const clamped: Note = {
      ...note,
      position: [cx, cy, note.position[2]] as [number, number, number],
    };
    const newNotes = [...diff.notes, clamped].sort((a, b) => a.time_ms - b.time_ms);
    if (!setDiffNotes(set, get, newNotes)) return;
    markPlaced(noteKey(clamped.time_ms, clamped.position[0], clamped.position[1]));
    playPlaceSound();
  },

  addPlacedNote: (note, offGrid = false) => {
    const state = get();
    if (!state.beatmap) return;
    if (_tooEarlyToPlace(state, note.time_ms)) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const [cx, cy] = offGrid ? [note.position[0], note.position[1]] : _clampNotePos(state, note.position[0], note.position[1]);
    const primary: Note = { ...note, position: [cx, cy, note.position[2]] as [number, number, number] };
    const toAdd: Note[] = [primary];
    if (state.mirrorMode) {
      const m: Note = JSON.parse(JSON.stringify(primary));
      if (m.hand === 'right') { m.hand = 'left'; m.note_type = 'left_hand'; }
      else if (m.hand === 'left') { m.hand = 'right'; m.note_type = 'right_hand'; }
      if (m.rail_nodes) {
        m.rail_nodes = m.rail_nodes.map(([x, y, z]: [number, number, number]) =>
          [-x, state.mirrorInvertY ? -y : y, z] as [number, number, number]);
      }
      const my = state.mirrorInvertY ? -primary.position[1] : primary.position[1];
      // The twin is clamped on its own (SRVR-0009 AC7): the primary being off the grid, on a rail, says nothing
      // about where its mirror is. With Snap to Rail on it lands on the rail of its own hand when one is concurrent
      // there, which may lie off the grid; otherwise it stays inside.
      const [cx2, cy2] = _clampNotePos(state, -primary.position[0], my);
      const [mx2, my2] = get().snapToRailPosition(cx2, cy2, m.note_type, primary.time_beats);
      m.position = [mx2, my2, primary.position[2]] as [number, number, number];
      // Skip the mirror if it lands exactly on the primary (centre note, same
      // hand) so a centre placement doesn't stack two identical notes.
      const samePos = Math.abs(mx2 - primary.position[0]) < 1e-4 && Math.abs(my2 - primary.position[1]) < 1e-4;
      const sameHand = m.hand === primary.hand && m.note_type === primary.note_type;
      if (!(samePos && sameHand)) toAdd.push(m);
    }
    // Same-Note-Overwrite: drop any existing note that shares a slot (beat +
    // cell) with one we're about to place, so the new note replaces it.
    let baseNotes = diff.notes;
    if (state.sameNoteOverwrite && isSynthVrView(state.activeView)) {
      // Track.cs: the notes placed win the beat. A note of the same hand, a special, or an overlapping note of the
      // other hand is replaced wherever it sits on the beat; an other-hand note clear of it stays
      // (ResolveConflictNotesForPaste, Prioritize Pasted).
      const here = diff.notes.filter(n => nearBeat(n.time_beats, primary.time_beats));
      const kept = new Set(resolveNoteConflicts(toAdd, here, 'prioritizePasted'));
      baseNotes = diff.notes.filter(n => !here.includes(n) || kept.has(n));
    } else if (state.sameNoteOverwrite) {
      const occupies = (n: Note) => toAdd.some(a =>
        Math.abs(n.time_ms - a.time_ms) < 2 &&
        Math.abs(n.position[0] - a.position[0]) < 0.05 &&
        Math.abs(n.position[1] - a.position[1]) < 0.05);
      baseNotes = diff.notes.filter(n => !occupies(n));
    }
    // Synth VR: a note that would break the beat's rule (SRVR-0337) is not placed. The mirror copy is
    // judged on its own: it is skipped alone if only it breaks the rule; if the note itself does, nothing is.
    if (isSynthVrView(state.activeView)) {
      const placed: Note[] = [];
      for (let i = 0; i < toAdd.length; i++) {
        const problem = placementProblem([...baseNotes, ...placed], toAdd[i]);
        if (!problem) { placed.push(toAdd[i]); continue; }
        if (i === 0) { _noteRuleHandler?.(problem); return; }
      }
      toAdd.length = 0;
      toAdd.push(...placed);
    }
    pushUndo(state, state.mirrorMode ? 'Add mirrored note' : 'Add note');
    // Hand the appended (unsorted) array to setDiffNotes: it re-sorts AND remaps
    // selectedNoteIds/selectedRailNode by identity, so an existing selection stays
    // valid. (Pre-sorting here would hide the reorder and leave stale indices.)
    if (!setDiffNotes(set, get, [...baseNotes, ...toAdd])) return;
    markPlaced(noteKey(primary.time_ms, primary.position[0], primary.position[1]));
    playPlaceSound();
  },

  addPlacedSlide: (slide) => {
    const state = get();
    if (!state.beatmap) return;
    if (_tooEarlyToPlace(state, slide.time_ms)) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // No stacked DUPLICATE walls (reviewer ask): placing the same wall type on
    // an occupied beat toggles it off instead of piling a second copy. Different
    // types on one beat stay allowed — left+right walls etc. are real patterns.
    const EPS_MS = 2;
    if (diff.slides.some(s2 => Math.abs(s2.time_ms - slide.time_ms) < EPS_MS && s2.wall_type === slide.wall_type)) {
      pushUndo(state, 'Remove wall');
      setDiffSlides(set, get, diff.slides.filter(s2 => !(Math.abs(s2.time_ms - slide.time_ms) < EPS_MS && s2.wall_type === slide.wall_type)));
      set(_staleSlideSelectionCleared(get()));
      return;
    }
    pushUndo(state, state.mirrorMode ? 'Add mirrored wall' : 'Add wall');
    const toAdd: Slide[] = [slide];
    if (state.mirrorMode) {
      const MIR: Record<string, string> = {
        wall_left: 'wall_right', wall_right: 'wall_left',
        angle_left: 'angle_right', angle_right: 'angle_left',
      };
      const mt = MIR[slide.wall_type];
      if (mt) {
        const m: Slide = { ...slide, wall_type: mt };
        // Console walls carry an explicit position — mirror its X too.
        if (Array.isArray(m.position) && m.position.length) {
          m.position = [-m.position[0], ...m.position.slice(1)];
        }
        toAdd.push(m);
      }
    }
    const newSlides = [...diff.slides, ...toAdd].sort((a, b) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
    // setDiffSlides doesn't remap indices, so a prior slide selection is now stale.
    set(_staleSlideSelectionCleared(get()));
  },

  toggleMirrorMode: () => set({ mirrorMode: !get().mirrorMode }),
  toggleMirrorInvertY: () => {
    const invert = !get().mirrorInvertY;
    set({ mirrorInvertY: invert });
    // Synth VR says what the key just did, in the scene's toast: nothing else on screen changes with it.
    if (isSynthVrView(get().activeView)) {
      _noteRuleHandler?.(`Mirroring ${invert ? 'X and Y' : 'X only'}${get().mirrorMode ? '' : ' (Mirror Mode is off)'}`);
    }
  },

  addNotes: (notes) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Add ${notes.length} notes`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes, ...notes].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
  },

  replaceNotes: (notes) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Replace with ${notes.length} notes`);
    const sorted = [...notes].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, sorted);
    set({ selectedNoteIds: [] });
  },

  removeNotes: (indices) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, `Remove ${indices.length} notes`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const indexSet = new Set(indices);
    // Emit a particle "explosion" at each deleted note (rendered by the scene),
    // coloured by the note's gameplay colour — paired with the delete SFX.
    const gp = state.gameplaySettings;
    const colorOf = (n: Note): string => {
      const t = (n.note_type || '').toLowerCase();
      if (t.includes('both')) return gp.noteColorBothHands;
      if (t.includes('one') || t.includes('special')) return gp.noteColorOneHand;
      if (n.hand === 'left') return gp.noteColorLeft;
      return gp.noteColorRight;
    };
    for (const idx of indices) {
      const n = diff.notes[idx];
      if (n) emitNoteBurst({ x: n.position[0], y: n.position[1], timeBeats: n.time_beats, color: colorOf(n) });
    }
    const newNotes = diff.notes.filter((_: Note, i: number) => !indexSet.has(i));
    setDiffNotes(set, get, newNotes);
    if (indices.length > 0) playDeleteSound();

    // If the deleted note was the active rail placement target, cancel rail placement
    if (state.railPlacementActive && state.railPlacementNoteIndex !== null && indexSet.has(state.railPlacementNoteIndex)) {
      set({ railPlacementActive: false, railPlacementNoteIndex: null });
    }
    // If the deleted note had a selected rail node, clear selection
    if (state.selectedRailNode && indexSet.has(state.selectedRailNode.noteIndex)) {
      set({ selectedRailNode: null });
    }
    set({ selectedNoteIds: [] });
  },

  updateNote: (index, update, offGrid = false) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Update note');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || index >= diff.notes.length) return;
    const newNotes = [...diff.notes];
    let merged = { ...newNotes[index], ...update };
    // Clamp position to grid bounds (safety net) — only a position this call was given: a head left off the grid
    // by a group move is not pulled back by an edit that never touched it (SRVR-0369 AC6).
    // Console keeps clamping whenever the note has a position, as it always did.
    if ((update.position || !isSynthVrView(state.activeView)) && merged.position && !offGrid) {
      const [cx, cy] = _clampNotePos(state, merged.position[0], merged.position[1]);
      merged = { ...merged, position: [cx, cy, merged.position[2]] as [number, number, number] };
    }
    newNotes[index] = merged;
    setDiffNotes(set, get, newNotes);
    // A rail head moved in place or in time: the notes on its rail follow. A node move re-snaps from
    // `updateRailNode`; writing `rail_nodes` here (adding or removing a node) re-snaps nothing (SRVR-0367 AC7).
    if (merged.has_rail && (update.position || update.time_beats !== undefined)) _resnapToRails(set, get, [merged]);
  },

  moveSelectedNotes: (updates) => {
    const state = get();
    if (!state.beatmap || updates.length === 0) return;
    pushUndo(state, `Move ${updates.length} note${updates.length === 1 ? '' : 's'}`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes];
    for (const u of updates) {
      if (u.index < 0 || u.index >= newNotes.length) continue;
      const n = { ...newNotes[u.index] };
      // When the note carries a rail, shift its nodes by the SAME delta as the
      // head so the whole rail moves rigidly (preserving its shape) instead of
      // the head detaching. Delta is measured from the note's current position,
      // so it accumulates correctly across per-frame drag updates.
      const hasRail = n.has_rail && !!n.rail_nodes;
      if (u.position) {
        const [cx, cy] = u.offGrid ? [u.position[0], u.position[1]] : _clampNotePos(state, u.position[0], u.position[1]);
        const dX = cx - n.position[0], dY = cy - n.position[1];
        n.position = [cx, cy, u.position[2]] as [number, number, number];
        if (hasRail && (dX !== 0 || dY !== 0)) {
          n.rail_nodes = n.rail_nodes!.map(([x, y, z]) => [x + dX, y + dY, z] as [number, number, number]);
        }
      }
      if (u.time) {
        const dMs = u.time.ms - n.time_ms;
        n.time_beats = u.time.beats;
        n.time_ms = u.time.ms;
        n.time_seconds = u.time.ms / 1000;
        // Keep position.z in sync with time, exactly as updateNote does.
        n.position = [n.position[0], n.position[1], u.time.ms * 0.02] as [number, number, number];
        if (hasRail && dMs !== 0) {
          // rail node z is encoded time (timeMs * 0.02) — shift by the same dt.
          n.rail_nodes = n.rail_nodes!.map(([x, y, z]) => [x, y, z + dMs * 0.02] as [number, number, number]);
        }
      }
      newNotes[u.index] = n;
    }
    setDiffNotes(set, get, newNotes);
    // Moved rails: the notes on them follow, except any this same move carried itself.
    const movedRails = updates.filter(u => (u.position || u.time) && newNotes[u.index]?.has_rail).map(u => newNotes[u.index]);
    if (movedRails.length) _resnapToRails(set, get, movedRails, new Set(updates.map(u => newNotes[u.index])));
  },

  beginDrag: (label) => {
    _undoSuppressed = false;          // make sure the start snapshot is recorded
    pushUndo(get(), label);
    _undoSuppressed = true;           // then skip every per-frame snapshot
  },
  endDrag: () => { _undoSuppressed = false; },

  mirrorSelected: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    pushUndo(state, 'Mirror notes');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;

    const newNotes = [...diff.notes];
    for (const idx of state.selectedNoteIds) {
      if (idx >= newNotes.length) continue;
      const n = { ...newNotes[idx] };
      n.position = [-n.position[0], n.position[1], n.position[2]] as [number, number, number];
      if (n.hand === 'right') { n.hand = 'left'; n.note_type = 'left_hand'; }
      else if (n.hand === 'left') { n.hand = 'right'; n.note_type = 'right_hand'; }
      if (n.rail_nodes) {
        n.rail_nodes = n.rail_nodes.map(([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]);
      }
      newNotes[idx] = n;
    }
    setDiffNotes(set, get, newNotes);
  },

  flipSelectedY: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    pushUndo(state, 'Flip notes (Y)');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newNotes = [...diff.notes];
    for (const idx of state.selectedNoteIds) {
      if (idx >= newNotes.length) continue;
      const n = { ...newNotes[idx] };
      // Vertical flip: negate Y only, keep X + handedness (the complement of
      // mirrorSelected, which flips X + swaps hand).
      n.position = [n.position[0], -n.position[1], n.position[2]] as [number, number, number];
      if (n.rail_nodes) {
        n.rail_nodes = n.rail_nodes.map(([x, y, z]: [number, number, number]) => [x, -y, z] as [number, number, number]);
      }
      newNotes[idx] = n;
    }
    setDiffNotes(set, get, newNotes);
  },

  duplicateMoveActive: false,

  duplicateMoveBy: (dxPx, dyPx) => {
    const sess = _dupSession;
    if (!sess || performance.now() - sess.startedAt < DUPLICATE_MOVE_DELAY_MS) return;
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff) return;
    // The copies' heads stay on the grid, as any note the mapper moves by hand does; the nodes of a
    // rail follow its head by the same offset.
    const L = PLACEMENT_LIMITS;
    const lo: [number, number] = [Math.max(...sess.copies.map(c => L.left - c.start[0])), Math.max(...sess.copies.map(c => L.bottom - c.start[1]))];
    const hi: [number, number] = [Math.min(...sess.copies.map(c => L.right - c.start[0])), Math.min(...sess.copies.map(c => L.top - c.start[1]))];
    sess.raw = [
      Math.min(hi[0], Math.max(lo[0], sess.raw[0] + dxPx * DUPLICATE_MOVE_GAIN)),
      Math.min(hi[1], Math.max(lo[1], sess.raw[1] - dyPx * DUPLICATE_MOVE_GAIN)),
    ];
    let [ox, oy] = sess.raw;
    if (state.snapToGrid) {
      const first = sess.copies[0].start;
      const [sx, sy] = state.snapToGridPosition(first[0] + ox, first[1] + oy);
      ox = Math.min(hi[0], Math.max(lo[0], sx - first[0]));
      oy = Math.min(hi[1], Math.max(lo[1], sy - first[1]));
    }
    const newNotes = diff.notes.map(n => {
      const c = sess.copies.find(k => k.hand === n.hand && nearBeat(k.beats, n.time_beats));
      if (!c) return n;
      return {
        ...n,
        position: [c.start[0] + ox, c.start[1] + oy, n.position[2]] as [number, number, number],
        rail_nodes: c.nodes ? c.nodes.map(([x, y, z]) => [x + ox, y + oy, z] as [number, number, number]) : n.rail_nodes,
      };
    });
    setDiffNotes(set, get, newNotes);
  },

  duplicateMoveEnd: () => {
    _dupSession = null;
    if (get().duplicateMoveActive) set({ duplicateMoveActive: false });
  },

  duplicateSelectedOppositeHand: () => {
    const state = get();
    if (isSynthVrView(state.activeView)) { _duplicateOppositeHandVr(set, get); return; }
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const additions: Note[] = [];
    for (const idx of state.selectedNoteIds) {
      const src = diff.notes[idx];
      if (!src) continue;
      const dup = JSON.parse(JSON.stringify(src)) as Note;
      // Mirror across X and flip handedness — the copy lands on the opposite
      // hand at the same time, turning a one-hand pattern into two-hand.
      dup.position = [-dup.position[0], dup.position[1], dup.position[2]] as [number, number, number];
      if (dup.hand === 'right') { dup.hand = 'left'; dup.note_type = 'left_hand'; }
      else if (dup.hand === 'left') { dup.hand = 'right'; dup.note_type = 'right_hand'; }
      if (dup.rail_nodes) {
        dup.rail_nodes = dup.rail_nodes.map(([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]);
      }
      additions.push(dup);
    }
    if (additions.length === 0) return;
    pushUndo(state, `Duplicate ${additions.length} to opposite hand`);
    const newNotes = [...diff.notes, ...additions].sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });   // indices shifted by the sort
  },

  scaleSelection: (factor) => {
    const state = get();
    if (!state.beatmap || !isFinite(factor) || factor <= 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const bpm = state.beatmap.bpm || 120;
    const sel = state.selectedNoteIds.filter(i => i < diff.notes.length);
    if (sel.length < 2) return;   // need a span to scale
    // Anchor on the earliest selected note; scale every selected note's time
    // away from (or toward) it. time_beats/time_seconds are re-derived from the
    // new chart-relative time_ms so the offset/beat frame stays consistent.
    const anchorMs = Math.min(...sel.map(i => diff.notes[i].time_ms));
    pushUndo(state, factor > 1 ? 'Expand selection' : 'Compress selection');
    const selSet = new Set(sel);
    const newNotes = diff.notes
      .map((n, i) => {
        if (!selSet.has(i)) return n;
        const newMs = anchorMs + (n.time_ms - anchorMs) * factor;
        const scaled: Note = {
          ...n,
          time_ms: newMs,
          time_seconds: newMs / 1000,
          time_beats: (newMs / 1000) * (bpm / 60),
          position: [n.position[0], n.position[1], newMs * 0.02] as [number, number, number],
        };
        // Scale the rail's node times by the SAME factor around the anchor, so the
        // whole rail stretches with its head and the head stays before its nodes.
        if (n.has_rail && n.rail_nodes) {
          scaled.rail_nodes = n.rail_nodes.map(([x, y, z]) => {
            const newNodeMs = anchorMs + (z / 0.02 - anchorMs) * factor;
            return [x, y, newNodeMs * 0.02] as [number, number, number];
          });
        }
        return scaled;
      })
      .sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });   // indices shifted by the re-sort
  },

  deleteAllObjects: () => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const isEmpty =
      diff.notes.length === 0 && diff.slides.length === 0 &&
      diff.crouches.length === 0 && diff.jumps.length === 0 &&
      !diff.squares?.length && !diff.triangles?.length && !diff.crouch_obstacles?.length;
    if (isEmpty) return;
    pushUndo(state, 'Delete all objects');
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: {
            ...diff,
            notes: [], slides: [], crouches: [], jumps: [],
            // Only reset the optional shape arrays when they exist, so a VR map
            // keeps its absent shape (undefined) rather than growing empty ones.
            ...(diff.crouch_obstacles ? { crouch_obstacles: [] } : {}),
            ...(diff.squares ? { squares: [] } : {}),
            ...(diff.triangles ? { triangles: [] } : {}),
            note_count: 0, rail_count: 0, slide_count: 0,
            crouch_count: 0, jump_count: 0,
            duration_beats: 0, duration_seconds: 0,
          },
        },
      },
      selectedNoteIds: [],
      selectedSlideIds: [],
      selectedRailNode: null,
      railPlacementActive: false,
      railPlacementNoteIndex: null,
    }));
  },

  // Synth VR: the desktop's join (railJoin.ts) — from the playhead with no area, the area's pieces
  // with one, of the current type. Console and Flow keep joining the selected notes (SRVR-0030).
  joinNotesToRail: () => {
    const state = get();
    if (isSynthVrView(state.activeView)) {
      const diff = state.beatmap?.difficulties[state.activeDifficulty];
      if (!state.beatmap || !diff) return;
      const bpm = state.beatmap.bpm || 120;
      const tolMs = onBeatTolerance(state.activeView, state.scrollStep, bpm) * (60000 / bpm);
      const kind = joinKind(state.toolMode, state.lastNoteKind);
      const range = effectiveAreaRange(state.areaRange, state.areaGesture, state.playback.currentBeat);
      const result = range
        ? areaJoin(diff.notes, range, tolMs, kind)
        : playheadJoin(diff.notes, state.beatToTimeMs(state.playback.currentBeat), tolMs, kind);
      if ('refused' in result) { if (result.refused) _noteRuleHandler?.(result.refused); return; }
      pushUndo(state, 'Join notes into rail');
      if (!setDiffNotes(set, get, result.notes)) return;
      // The removed pieces shift array indices. A range re-selects its own notes; nothing else should
      // point at a note that moved.
      set({ selectedRailNode: null, ...(range ? {} : { selectedNoteIds: [] }) });
      return;
    }
    if (!state.beatmap || state.selectedNoteIds.length < 2) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const sel = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => ({ i, n: diff.notes[i] }))
      .sort((a, b) => a.n.time_ms - b.n.time_ms);
    if (sel.length < 2) return;
    const head = sel[0].n;
    const rest = sel.slice(1);
    // A rail is single-hand — only join notes that share the head's hand.
    if (!rest.every(r => r.n.hand === head.hand)) return;
    // A node must sit STRICTLY after the head in time; drop any selected note that
    // shares the head's beat (a stacked / two-hand pair) so it can't become a
    // degenerate node coinciding exactly with the head.
    const restNodes = rest.filter(r => r.n.time_ms > head.time_ms);
    if (restNodes.length === 0) return;
    pushUndo(state, 'Join notes into rail');
    // rail_nodes carry ENCODED time (timeMs * 0.02); the head stays the note's
    // own position, every other selected note becomes a subsequent node.
    const nodes: [number, number, number][] = [...(head.rail_nodes ?? [])];
    for (const r of restNodes) {
      nodes.push([r.n.position[0], r.n.position[1], r.n.time_ms * 0.02]);
      if (r.n.rail_nodes) nodes.push(...r.n.rail_nodes);   // absorb existing sub-rails
    }
    nodes.sort((a, b) => a[2] - b[2]);
    const consumed = new Set(restNodes.map(r => r.i));
    const newNotes = diff.notes
      .map((n, idx) => idx === sel[0].i ? { ...n, has_rail: true, rail_nodes: nodes } : n)
      .filter((_, idx) => !consumed.has(idx));
    setDiffNotes(set, get, newNotes);
    set({ selectedNoteIds: [] });
  },

  // Synth VR: the desktop's split at the playhead (railSplit.ts). Console and Flow keep splitting at the
  // selected node (SRVR-0031).
  splitRailAtNode: () => {
    const state = get();
    if (isSynthVrView(state.activeView)) { _vrSplit(state, set, get, false); return; }
    const sel = state.selectedRailNode;
    if (!state.beatmap || !sel) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const note = diff.notes[sel.noteIndex];
    if (!note || !note.has_rail || !note.rail_nodes || sel.nodeIndex >= note.rail_nodes.length) return;
    pushUndo(state, 'Split rail');
    const bpm = state.beatmap.bpm || 120;
    const rn = note.rail_nodes;
    const k = sel.nodeIndex;
    const splitNode = rn[k];
    const before = rn.slice(0, k);
    const after = rn.slice(k + 1);
    const timeMs = splitNode[2] / 0.02;
    // Original rail keeps the nodes before the split; the split node becomes the
    // head of a new rail carrying the nodes after it.
    const orig: Note = {
      ...note,
      has_rail: before.length > 0,
      rail_nodes: before.length > 0 ? before : null,
    };
    const head2: Note = {
      ...note,
      time_ms: timeMs,
      time_seconds: timeMs / 1000,
      time_beats: (timeMs / 1000) * (bpm / 60),
      position: [splitNode[0], splitNode[1], splitNode[2]] as [number, number, number],
      has_rail: after.length > 0,
      rail_nodes: after.length > 0 ? after : null,
      note_id: '',
    };
    const newNotes = diff.notes.map((n, idx) => idx === sel.noteIndex ? orig : n);
    newNotes.push(head2);
    newNotes.sort((a, b) => a.time_ms - b.time_ms);
    setDiffNotes(set, get, newNotes);
    set({ selectedRailNode: null, selectedNoteIds: [] });
  },

  recalcRailNodesByStep: () => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return;
    // Target: the selected rail node's note, else a single selected note w/ a rail.
    let noteIndex = -1;
    if (state.selectedRailNode) noteIndex = state.selectedRailNode.noteIndex;
    else if (state.selectedNoteIds.length === 1) noteIndex = state.selectedNoteIds[0];
    if (noteIndex < 0 || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return;
    const bpm = state.beatmap.bpm || 120;
    const step = state.scrollStep || 0.25;
    // node[2] is encoded time (timeMs * 0.02); convert to/from chart beats purely
    // (offset-free) to match the renderer's decode.
    const decodeBeat = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
    const encTime = (beat: number) => (beat * 60000 / bpm) * 0.02;
    // Control points in beat-space: the head, then every node.
    const ctrl = [
      { b: note.time_beats, x: note.position[0], y: note.position[1] },
      ...note.rail_nodes.map(n => ({ b: decodeBeat(n[2]), x: n[0], y: n[1] })),
    ].sort((a, b) => a.b - b.b);
    const startBeat = ctrl[0].b, endBeat = ctrl[ctrl.length - 1].b;
    if (endBeat - startBeat < 1e-4) return;
    // Uniform Catmull-Rom (tension 0.5) on one axis.
    const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const t2 = t * t, t3 = t2 * t;
      return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
    };
    const sampleAt = (beat: number) => {
      let i = 0;
      while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
      const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
      const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
      const seg = Math.max(1e-6, p2.b - p1.b);
      const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
      return { x: cr(p0.x, p1.x, p2.x, p3.x, t), y: cr(p0.y, p1.y, p2.y, p3.y, t) };
    };
    pushUndo(state, 'Recalc rail nodes');
    const newNodes: [number, number, number][] = [];
    for (let b = startBeat + step; b < endBeat - 1e-4; b += step) {
      const p = sampleAt(b);
      newNodes.push([p.x, p.y, encTime(b)]);
    }
    // Keep the exact endpoint so the rail preserves its length.
    const last = ctrl[ctrl.length - 1];
    newNodes.push([last.x, last.y, encTime(endBeat)]);
    const newNotes = diff.notes.map((n, idx) => idx === noteIndex ? { ...n, rail_nodes: newNodes } : n);
    setDiffNotes(set, get, newNotes);
  },

  placeWallAtPlayhead: (wallType) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const beat = state.playback.currentBeat;
    const timeMs = get().beatToTimeMs(beat);
    if (wallType === 'crouch') { get().addCrouch(timeMs); return; }
    if (wallType === 'square' || wallType === 'triangle') {
      _addSquareTriangle(wallType, timeMs, beat);
      return;
    }
    // Slide walls (left/right/center/angle_*) — mirror-aware, placed at the format's centre.
    get().addPlacedSlide({
      time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
      slide_type: 0, wall_type: wallType, initialized: true,
      position: newWallPosition(timeMs), z_rotation: 0,
    });
  },

  // The desktop editor's ToggleMovementSectionToChart (Track.cs): find the wall on the beat, delete
  // it, and add the pressed kind unless the deleted one was that kind at the same position. Walls are
  // spread over four collections here, so "the wall on the beat" is gathered from all of them.
  toggleWallAtPlayhead: (kind) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return;
    const beat = state.playback.currentBeat;
    const timeMs = get().beatToTimeMs(beat);
    if (_tooEarlyToPlace(state, timeMs)) return;
    const onBeat = (t: number) => Math.abs(t - timeMs) < WALL_BEAT_MS;

    const slides = diff.slides ?? [];
    const crouches = diff.crouches ?? [];
    const squares = diff.squares ?? [];
    const triangles = diff.triangles ?? [];

    const sameAtCentre =
      kind === 'crouch'
        ? crouches.some(t => onBeat(t) && _atCentre(diff.crouch_obstacles?.[_crouchObstacleIndex(diff.crouch_obstacles, t)]?.position))
          // A crouch placed by the Console grid is a slide; it is still a crouch on this beat.
          || slides.some(s => onBeat(s.time_ms) && s.wall_type === 'crouch' && _atCentre(s.position))
        : kind === 'square' ? squares.some(o => onBeat(o.time_ms) && _atCentre(o.position))
        : kind === 'triangle' ? triangles.some(o => onBeat(o.time_ms) && _atCentre(o.position))
        : slides.some(s => onBeat(s.time_ms) && s.wall_type === kind && _atCentre(s.position));
    const occupied = slides.some(s => onBeat(s.time_ms)) || crouches.some(onBeat)
      || squares.some(o => onBeat(o.time_ms)) || triangles.some(o => onBeat(o.time_ms));

    // Clear the beat — every wall of every kind, and the positions of the crouches removed.
    let nextSlides = slides.filter(s => !onBeat(s.time_ms));
    let nextCrouches = crouches.filter(t => !onBeat(t));
    let nextSquares = squares.filter(o => !onBeat(o.time_ms));
    let nextTriangles = triangles.filter(o => !onBeat(o.time_ms));
    const removedCrouches = crouches.filter(onBeat);
    const crouchObstacles = diff.crouch_obstacles?.filter(o => !removedCrouches.some(t => Math.abs(o.time_ms - t) <= CROUCH_MATCH_MS));

    if (!sameAtCentre) {
      const position = newWallPosition(timeMs);
      const ob: Obstacle = { time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat, position, z_rotation: 0, initialized: true };
      const byTime = (a: { time_ms: number }, b: { time_ms: number }) => a.time_ms - b.time_ms;
      // Mirror mode is ignored: a beat holds one wall, so there is no room for a mirrored second.
      if (kind === 'crouch') nextCrouches = [...nextCrouches, timeMs].sort((a, b) => a - b);
      else if (kind === 'square') nextSquares = [...nextSquares, ob].sort(byTime);
      else if (kind === 'triangle') nextTriangles = [...nextTriangles, ob].sort(byTime);
      else nextSlides = [...nextSlides, { ...ob, slide_type: 0, wall_type: kind }].sort(byTime);
    }

    pushUndo(state, sameAtCentre ? 'Remove wall' : occupied ? 'Replace wall' : 'Add wall');
    set({
      beatmap: {
        ...state.beatmap,
        difficulties: {
          ...state.beatmap.difficulties,
          [state.activeDifficulty]: {
            ...diff,
            slides: nextSlides, slide_count: nextSlides.length,
            crouches: nextCrouches, crouch_count: nextCrouches.length,
            // A difficulty that never had squares or triangles is not given empty ones.
            ...(diff.squares || nextSquares.length ? { squares: nextSquares } : {}),
            ...(diff.triangles || nextTriangles.length ? { triangles: nextTriangles } : {}),
            ...(crouchObstacles ? { crouch_obstacles: crouchObstacles } : {}),
          },
        },
      },
      // The slide array was rebuilt without an index remap, so a slide selection is stale.
      ...(state.selectedSlideIds.length ? { selectedSlideIds: [] } : {}),
    });
  },

  placeNoteAtPlayhead: () => {
    const state = get();
    if (!state.beatmap) return;
    const HAND: Record<string, { hand: 'left' | 'right' | 'special'; note_type: string }> = {
      note_left: { hand: 'left', note_type: 'left_hand' },
      note_right: { hand: 'right', note_type: 'right_hand' },
      note_onehand: { hand: 'special', note_type: 'one_hand_special' },
      note_bothhands: { hand: 'special', note_type: 'both_hands_special' },
      rail_left: { hand: 'left', note_type: 'left_hand' },
      rail_right: { hand: 'right', note_type: 'right_hand' },
      rail_onehand: { hand: 'special', note_type: 'one_hand_special' },
      rail_bothhands: { hand: 'special', note_type: 'both_hands_special' },
    };
    const map = HAND[state.toolMode];
    if (!map) return;   // only meaningful while a note/rail tool is active
    const beat = state.playback.currentBeat;
    const timeMs = get().beatToTimeMs(beat);
    get().addPlacedNote({
      time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
      position: [0, 0, timeMs * 0.02],
      note_type: map.note_type, hand: map.hand,
      direction: 0, has_rail: false, rail_nodes: null,
      combo_id: 0, note_id: '',
    });
  },

  // Note Fill (Track.cs `FillSelectionWithNote`): on every Step of the area, remove what the new note
  // conflicts with (`fillConflicts`, among the types the Selection Options let it see) and place the
  // note at the centre. No mirror and no beat rule: the desktop's fill uses neither. One undo step.
  fillAreaWithNotes: (kind) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff || !isSynthVrView(state.activeView)) return;
    const current = state.playback.currentBeat;
    const range = effectiveAreaRange(state.areaRange, state.areaGesture, current);
    if (!range) { _noteRuleHandler?.('No Area Selected.'); return; }
    if (current < range.startBeat - 1e-6 || current > range.endBeat + 1e-6) {
      _noteRuleHandler?.('Current Beat Outside Selected Area');
      return;
    }
    const HAND = { left_hand: 'left', right_hand: 'right', one_hand_special: 'special', both_hands_special: 'special' } as const;
    let notes = diff.notes;
    let changed = false;
    for (const beat of fillBeats(range, state.scrollStep > 0 ? state.scrollStep : 0.25)) {
      const timeMs = get().beatToTimeMs(beat);
      const seen = notes.filter(n => Math.abs(n.time_ms - timeMs) < SAME_BEAT_MS && state.areaFilters[noteFilterKind(n)]);
      const gone = new Set(fillConflicts(seen, kind));
      if (gone.size) { notes = notes.filter(n => !gone.has(n)); changed = true; }
      if (_tooEarlyToPlace(state, timeMs)) continue;
      notes = [...notes, {
        time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
        position: [0, 0, timeMs * 0.02], note_type: kind, hand: HAND[kind],
        direction: 0, has_rail: false, rail_nodes: null, combo_id: 0, note_id: '',
      }];
      changed = true;
    }
    if (!changed) return;
    pushUndo(state, 'Fill with notes');
    if (!setDiffNotes(set, get, notes)) return;
    playPlaceSound();
  },

  toggleSameNoteOverwrite: () => {
    const state = get();
    if (!isSynthVrView(state.activeView)) { set({ sameNoteOverwrite: !state.sameNoteOverwrite }); return; }
    // Track.cs ToggleSameNoteOverwriteAction: refused while the track plays, and it says what it did.
    if (state.playback.playing) return;
    const on = !state.sameNoteOverwrite;
    set({ sameNoteOverwrite: on });
    _noteRuleHandler?.(`Same Note Overwrite Mode ${on ? 'Enabled' : 'Disabled'}`);
  },

  snipRailAtPlayhead: () => {
    const state = get();
    if (isSynthVrView(state.activeView)) { _vrSplit(state, set, get, true); return; }
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return;
    let noteIndex = -1;
    if (state.selectedRailNode) noteIndex = state.selectedRailNode.noteIndex;
    else if (state.selectedNoteIds.length === 1) noteIndex = state.selectedNoteIds[0];
    if (noteIndex < 0 || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return;
    const bpm = state.beatmap.bpm || 120;
    const decodeBeat = (enc: number) => (enc / 0.02 / 1000) * (bpm / 60);
    const encTime = (b: number) => (b * 60000 / bpm) * 0.02;
    const beat = state.playback.currentBeat;
    const ctrl = [
      { b: note.time_beats, x: note.position[0], y: note.position[1] },
      ...note.rail_nodes.map(n => ({ b: decodeBeat(n[2]), x: n[0], y: n[1] })),
    ].sort((a, b2) => a.b - b2.b);
    const startBeat = ctrl[0].b, endBeat = ctrl[ctrl.length - 1].b;
    if (beat <= startBeat + 1e-3 || beat >= endBeat - 1e-3) return;   // only inside the span
    const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const t2 = t * t, t3 = t2 * t;
      return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
    };
    let i = 0;
    while (i < ctrl.length - 2 && ctrl[i + 1].b < beat) i++;
    const p1 = ctrl[i], p2 = ctrl[i + 1] ?? ctrl[i];
    const p0 = ctrl[i - 1] ?? p1, p3 = ctrl[i + 2] ?? p2;
    const seg = Math.max(1e-6, p2.b - p1.b);
    const t = Math.min(1, Math.max(0, (beat - p1.b) / seg));
    const newNode: [number, number, number] = [cr(p0.x, p1.x, p2.x, p3.x, t), cr(p0.y, p1.y, p2.y, p3.y, t), encTime(beat)];
    const nodes = [...note.rail_nodes];
    let insertIdx = nodes.length;
    for (let j = 0; j < nodes.length; j++) { if (newNode[2] < nodes[j][2]) { insertIdx = j; break; } }
    nodes.splice(insertIdx, 0, newNode);
    pushUndo(state, 'Snip rail (add node)');
    const newNotes = diff.notes.map((n, idx) => idx === noteIndex ? { ...n, rail_nodes: nodes } : n);
    setDiffNotes(set, get, newNotes);
    // The inserted node shifts every node at/after insertIdx up by one — keep a
    // selected node on this same rail pointing at the node it was before.
    const selRN = state.selectedRailNode;
    if (selRN && selRN.noteIndex === noteIndex && insertIdx <= selRN.nodeIndex) {
      set({ selectedRailNode: { ...selRN, nodeIndex: selRN.nodeIndex + 1 } });
    }
  },

  cycleSelectedNoteType: () => {
    const state = get();
    if (!state.beatmap || state.selectedNoteIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const ORDER: { hand: 'left' | 'right' | 'special'; note_type: string }[] = [
      { hand: 'right', note_type: 'right_hand' },
      { hand: 'left', note_type: 'left_hand' },
      { hand: 'special', note_type: 'one_hand_special' },
      { hand: 'special', note_type: 'both_hands_special' },
    ];
    pushUndo(state, 'Cycle note type');
    const sel = new Set(state.selectedNoteIds);
    const newNotes = diff.notes.map((n, idx) => {
      if (!sel.has(idx)) return n;
      const cur = ORDER.findIndex(o => o.note_type === n.note_type);
      const nxt = ORDER[(cur + 1) % ORDER.length];   // cur === -1 → ORDER[0]
      return { ...n, hand: nxt.hand, note_type: nxt.note_type };
    });
    setDiffNotes(set, get, newNotes);
  },

  addMirrorNote: (index) => {
    const state = get();
    if (!state.beatmap || !isSynthVrView(state.activeView)) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    const src = diff?.notes[index];
    if (!diff || !src) return;
    const kind = noteFilterKind(src);
    if (kind !== 'left_hand' && kind !== 'right_hand') return;   // a Special has no opposite
    const bpm = state.beatmap.bpm || 120;
    const here = ((state.playback.currentTime - (state.beatmap.offset || 0) / 1000) / 60) * bpm;
    if (Math.abs(src.time_beats - here) > onBeatTolerance(state.activeView, state.scrollStep, bpm)) return;   // only the playhead's beat
    const m = flipNote(src, 'x');
    m.note_id = '';
    pushUndo(state, 'Add mirror note');
    if (!setDiffNotes(set, get, [...diff.notes, m])) return;
    playPlaceSound();
  },

  changeNoteColor: (index) => {
    const state = get();
    if (!state.beatmap || !isSynthVrView(state.activeView)) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    const found = diff?.notes[index];
    if (!diff || !found) return;
    const bpm = state.beatmap.bpm || 120;
    const p = state.playback.currentBeat;
    if (Math.abs(found.time_beats - p) > onBeatTolerance(state.activeView, state.scrollStep, bpm)) return;   // only the playhead's beat
    const range = effectiveAreaRange(state.areaRange, state.areaGesture, p);
    if (range && !inRange(p, range)) { _noteRuleHandler?.('Current Beat Outside Selected Area'); return; }
    // The session ends when the playhead moves to another beat, or another map or difficulty is open.
    const mapKey = `${state.beatmap.id}/${state.activeDifficulty}`;
    if (_colorSession && (_colorSession.map !== mapKey || !nearBeat(_colorSession.beat, p))) _colorSession = null;
    const ranged = !!range && range.endBeat !== range.startBeat;
    const span = range ?? { startBeat: p, endBeat: p };
    const reached = (n: Note) => (ranged && _colorSession ? _colorSession.beats.some(b => nearBeat(b, n.time_beats)) : inRange(n.time_beats, span));
    // The notes on each beat reached, in the order they are stored.
    const groups: number[][] = [];
    diff.notes.forEach((n, i) => {
      if (!reached(n)) return;
      const g = groups.find(grp => nearBeat(diff.notes[grp[0]].time_beats, n.time_beats));
      if (g) g.push(i); else groups.push([i]);
    });
    // A beat that holds two notes, or both hands somewhere in what is reached, swaps Left and Right only.
    const isHand = (n: Note, k: string) => n.note_type === k;
    const swap = groups.some(g => g.length >= 2)
      || (groups.some(g => isHand(diff.notes[g[0]], 'left_hand')) && groups.some(g => isHand(diff.notes[g[0]], 'right_hand')));
    if (swap && groups.length) _noteRuleHandler?.('Swapping Left/Right Hand Notes');
    const special = (n: Note) => n.hand === 'special';
    const NEXT: Record<string, string> = swap
      ? { left_hand: 'right_hand', right_hand: 'left_hand', one_hand_special: 'both_hands_special', both_hands_special: 'left_hand' }
      : { left_hand: 'right_hand', right_hand: 'one_hand_special', one_hand_special: 'both_hands_special', both_hands_special: 'left_hand' };
    const HAND: Record<string, Note['hand']> = { left_hand: 'left', right_hand: 'right', one_hand_special: 'special', both_hands_special: 'special' };
    const changes = new Map<number, string>();
    const changedBeats: number[] = [];
    for (const g of groups) {
      for (const i of g) {
        const n = diff.notes[i];
        if (special(n) && n.note_type !== found.note_type) break;   // a special of another colour ends the beat
        if (special(found) && !special(n)) break;
        if (n.note_type !== found.note_type && !(!special(n) && swap)) continue;
        const next = NEXT[n.note_type];
        if (next) { changes.set(i, next); }
      }
      if (g.some(i => changes.has(i))) changedBeats.push(diff.notes[g[0]].time_beats);
    }
    if (changes.size === 0) return;
    pushUndo(state, 'Change note color');
    const newNotes = diff.notes.map((n, i) => (changes.has(i) ? { ...n, note_type: changes.get(i)!, hand: HAND[changes.get(i)!] } : n));
    if (!setDiffNotes(set, get, newNotes)) return;
    _colorSession = { beat: _colorSession?.beat ?? p, beats: changedBeats, map: mapKey };
  },

  // Shift+R. Works on the area selection's range (the playhead's beat without one), not on the selected
  // notes, and does not depend on the Snap to Rail toggle — Track.cs SnapToSelectedRails.
  snapSelectionToRail: () => {
    const state = get();
    if (!isSynthVrView(state.activeView)) { _snapSelectionToNearestRail(set, get); return; }   // the other views keep what they had
    const t = _areaTarget(state);
    if (!t) return;
    const newNotes = snapConcurrentNotesToRails(t.diff.notes, t.bpm, t.range, t.state.areaFilters, _snapShape(t.state));
    if (!newNotes) return;
    pushUndo(t.state, 'Snap to rail');
    setDiffNotes(set, get, newNotes);
  },

  // ── Slide Editing ──────────────────────────────────────────────

  addSlide: (slide) => {
    const state = get();
    if (!state.beatmap) return;
    if (_tooEarlyToPlace(state, slide.time_ms)) return;
    pushUndo(state, 'Add slide');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = [...diff.slides, slide].sort((a, b) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
  },

  removeSlide: (index) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove slide');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = diff.slides.filter((_: Slide, i: number) => i !== index);
    setDiffSlides(set, get, newSlides);
    set(_staleSlideSelectionCleared(get()));
  },

  removeSlides: (indices) => {
    const state = get();
    if (!state.beatmap || indices.length === 0) return;
    pushUndo(state, `Remove ${indices.length} obstacles`);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const drop = new Set(indices);
    const newSlides = diff.slides.filter((_: Slide, i: number) => !drop.has(i));
    setDiffSlides(set, get, newSlides);
    set(_staleSlideSelectionCleared(get()));
  },

  // In-place obstacle edit (no re-sort, so the gizmo's index stays stable during
  // a drag; call sortSlides() once when the drag ends).
  updateSlide: (index, patch) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || index < 0 || index >= diff.slides.length) return;
    const newSlides = [...diff.slides];
    newSlides[index] = { ...newSlides[index], ...patch };
    setDiffSlides(set, get, newSlides);
  },

  sortSlides: () => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newSlides = [...diff.slides].sort((a: Slide, b: Slide) => a.time_ms - b.time_ms);
    setDiffSlides(set, get, newSlides);
  },

  // ── Jump / Crouch Editing ─────────────────────────────────────

  addJump: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Add jump');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newJumps = [...diff.jumps, timeMs].sort((a, b) => a - b);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, jumps: newJumps, jump_count: newJumps.length },
        },
      },
    }));
  },

  removeJump: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove jump');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.jumps.indexOf(timeMs);
    if (idx === -1) return;
    const newJumps = diff.jumps.filter((_: number, i: number) => i !== idx);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, jumps: newJumps, jump_count: newJumps.length },
        },
      },
    }));
  },

  addCrouch: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    if (_tooEarlyToPlace(state, timeMs)) return;
    pushUndo(state, 'Add crouch');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const newCrouches = [...diff.crouches, timeMs].sort((a, b) => a - b);
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, crouches: newCrouches, crouch_count: newCrouches.length },
        },
      },
    }));
  },

  removeSquareTriangle: (kind, index) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    const list = kind === 'square' ? diff?.squares : diff?.triangles;
    if (!diff || !list || index < 0 || index >= list.length) return;
    pushUndo(state, kind === 'square' ? 'Remove square' : 'Remove triangle');
    const next = removeContents(diff, {
      notes: [], slides: [], crouches: [],
      squares: kind === 'square' ? [index] : [], triangles: kind === 'triangle' ? [index] : [],
    });
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: { ...state.beatmap!.difficulties, [state.activeDifficulty]: next } },
    }));
  },

  removeCrouch: (timeMs) => {
    const state = get();
    if (!state.beatmap) return;
    pushUndo(state, 'Remove crouch');
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.crouches.indexOf(timeMs);
    if (idx === -1) return;
    const newCrouches = diff.crouches.filter((_: number, i: number) => i !== idx);
    // The crouch's position goes with it, or the next crouch at this time would inherit it.
    const obsIdx = _crouchObstacleIndex(diff.crouch_obstacles, timeMs);
    const obstacles = obsIdx === -1 ? {} : { crouch_obstacles: diff.crouch_obstacles!.filter((_, i) => i !== obsIdx) };
    set(() => ({
      beatmap: {
        ...state.beatmap!,
        difficulties: {
          ...state.beatmap!.difficulties,
          [state.activeDifficulty]: { ...diff, ...obstacles, crouches: newCrouches, crouch_count: newCrouches.length },
        },
      },
    }));
  },

  // Move a crouch / jump in time (drag). Identified by value; replace + re-sort.
  moveCrouch: (oldMs, newMs) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.crouches.indexOf(oldMs);
    if (idx === -1) return;
    const arr = [...diff.crouches];
    arr[idx] = newMs;
    arr.sort((a: number, b: number) => a - b);
    // Moving a crouch in time keeps its file position: carry its crouch_obstacles entry along.
    const obsIdx = _crouchObstacleIndex(diff.crouch_obstacles, oldMs);
    let obstacles = {};
    if (obsIdx !== -1) {
      const bpm = state.beatmap.bpm || 120;
      const moved = diff.crouch_obstacles!.map((o, i) => (i !== obsIdx ? o : {
        ...o, time_ms: newMs, time_seconds: newMs / 1000, time_beats: (newMs / 1000) * (bpm / 60),
      }));
      moved.sort((a, b) => a.time_ms - b.time_ms);
      obstacles = { crouch_obstacles: moved };
    }
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: { ...diff, ...obstacles, crouches: arr },
      } },
    }));
  },

  moveJump: (oldMs, newMs) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const idx = diff.jumps.indexOf(oldMs);
    if (idx === -1) return;
    const arr = [...diff.jumps];
    arr[idx] = newMs;
    arr.sort((a: number, b: number) => a - b);
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: { ...diff, jumps: arr },
      } },
    }));
  },

  moveObstacles: (moves) => {
    const state = get();
    if (!state.beatmap || moves.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // Keep the position's third component; a wall with no position gets the one a new wall would.
    const placed = (pos: number[] | null | undefined, timeMs: number, x: number, y: number) =>
      [x, y, pos && pos.length >= 3 ? pos[2] : newWallPosition(timeMs)[2]];
    let slides: Slide[] | null = null;
    let squares: Obstacle[] | null = null;
    let triangles: Obstacle[] | null = null;
    let crouchObstacles: Obstacle[] | null = null;
    for (const m of moves) {
      if (m.kind === 'crouch') {
        if (!diff.crouches.some(t => Math.abs(t - m.timeMs) <= CROUCH_MATCH_MS)) continue;
        crouchObstacles ??= [...(diff.crouch_obstacles ?? [])];
        const i = _crouchObstacleIndex(crouchObstacles, m.timeMs);
        if (i !== -1) {
          const o = crouchObstacles[i];
          crouchObstacles[i] = { ...o, position: placed(o.position, o.time_ms, m.x, m.y) };
        } else {
          // A crouch with no file position is drawn at the centre; once moved it needs one, keyed to
          // its time, so the crouch list itself never changes.
          const bpm = state.beatmap.bpm || 120;
          crouchObstacles.push({
            time_ms: m.timeMs, time_seconds: m.timeMs / 1000, time_beats: (m.timeMs / 1000) * (bpm / 60),
            position: placed(null, m.timeMs, m.x, m.y), z_rotation: 0, initialized: true,
          });
          crouchObstacles.sort((a, b) => a.time_ms - b.time_ms);
        }
      } else if (m.kind === 'slide') {
        slides ??= [...diff.slides];
        const s = slides[m.index];
        if (!s) continue;
        slides[m.index] = { ...s, position: placed(s.position, s.time_ms, m.x, m.y) };
      } else {
        const arr = m.kind === 'square'
          ? (squares ??= [...(diff.squares ?? [])])
          : (triangles ??= [...(diff.triangles ?? [])]);
        const o = arr[m.index];
        if (!o) continue;
        arr[m.index] = { ...o, position: placed(o.position, o.time_ms, m.x, m.y) };
      }
    }
    if (!slides && !squares && !triangles && !crouchObstacles) return;
    pushUndo(state, 'Move obstacle');
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: {
          ...diff,
          ...(slides ? { slides } : {}),
          ...(squares ? { squares } : {}),
          ...(triangles ? { triangles } : {}),
          ...(crouchObstacles ? { crouch_obstacles: crouchObstacles } : {}),
        },
      } },
    }));
  },

  rotateObstacles: (targets, deltaDeg) => {
    const state = get();
    if (!state.beatmap || targets.length === 0 || deltaDeg === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // A wall with no file position is written without its zRotation, so the first rotation gives it
    // the position it is drawn at (the same fallback moveObstacles uses).
    const rotated = <T extends Slide | Obstacle>(w: T): T => {
      const [x, y] = wallFileXY(w.position);
      const z = w.position && w.position.length >= 3 ? w.position[2] : newWallPosition(w.time_ms)[2];
      return { ...w, position: w.position ?? [x, y, z], z_rotation: normalizeWallRotation((w.z_rotation ?? 0) + deltaDeg) };
    };
    let slides: Slide[] | null = null;
    let squares: Obstacle[] | null = null;
    let triangles: Obstacle[] | null = null;
    for (const t of targets) {
      if (t.kind === 'slide') {
        slides ??= [...diff.slides];
        if (slides[t.index]) slides[t.index] = rotated(slides[t.index]);
      } else {
        const arr = t.kind === 'square'
          ? (squares ??= [...(diff.squares ?? [])])
          : (triangles ??= [...(diff.triangles ?? [])]);
        if (arr[t.index]) arr[t.index] = rotated(arr[t.index]);
      }
    }
    pushUndo(state, 'Rotate wall');
    set(() => ({
      beatmap: { ...state.beatmap!, difficulties: {
        ...state.beatmap!.difficulties,
        [state.activeDifficulty]: {
          ...diff,
          ...(slides ? { slides } : {}),
          ...(squares ? { squares } : {}),
          ...(triangles ? { triangles } : {}),
        },
      } },
    }));
  },

  toggleJumpAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    const bpm = state.beatmap.bpm;
    const timeMs = Math.round((beat / (bpm / 60)) * 1000);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    // Check if a jump exists near this time (within 10ms)
    const existing = diff.jumps.findIndex((t: number) => Math.abs(t - timeMs) < 10);
    if (existing >= 0) {
      get().removeJump(diff.jumps[existing]);
    } else {
      get().addJump(timeMs);
    }
  },

  toggleCrouchAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    const bpm = state.beatmap.bpm;
    const timeMs = Math.round((beat / (bpm / 60)) * 1000);
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const existing = diff.crouches.findIndex((t: number) => Math.abs(t - timeMs) < 10);
    if (existing >= 0) {
      get().removeCrouch(diff.crouches[existing]);
    } else {
      get().addCrouch(timeMs);
    }
  },

  // ── Undo/Redo ─────────────────────────────────────────────────

  undo: () => {
    const { undoStack, beatmap, redoStack } = get();
    if (undoStack.length === 0 || !beatmap) return;
    const prev = undoStack[undoStack.length - 1];
    const cur = get();
    const lights = prev.flashEvents ? { flashEvents: cur.flashEvents, effects: cur.effects } : {};
    set({
      redoStack: [...redoStack, { label: prev.label, difficulties: { ...beatmap.difficulties }, ...lights }],
      undoStack: undoStack.slice(0, -1),
      beatmap: { ...beatmap, difficulties: prev.difficulties },
      selectedNoteIds: [],
      ...restoredLights(cur, prev),
    });
    if (prev.flashEvents) saveFlashDraft(beatmap.id, prev.flashEvents);
    if (prev.effects) saveEffectDraft(beatmap.id, prev.effects);
  },

  redo: () => {
    const { redoStack, beatmap, undoStack } = get();
    if (redoStack.length === 0 || !beatmap) return;
    const next = redoStack[redoStack.length - 1];
    const cur = get();
    const lights = next.flashEvents ? { flashEvents: cur.flashEvents, effects: cur.effects } : {};
    set({
      undoStack: [...undoStack, { label: next.label, difficulties: { ...beatmap.difficulties }, ...lights }],
      redoStack: redoStack.slice(0, -1),
      beatmap: { ...beatmap, difficulties: next.difficulties },
      selectedNoteIds: [],
      ...restoredLights(cur, next),
    });
    if (next.flashEvents) saveFlashDraft(beatmap.id, next.flashEvents);
    if (next.effects) saveEffectDraft(beatmap.id, next.effects);
  },

  // ── Keybinding Methods ───────────────────────────────────────

  setKeybinding: (id, slot, combo) => {
    const bindings = [...get().keybindings];
    const idx = bindings.findIndex(b => b.id === id);
    if (idx === -1) return;
    bindings[idx] = { ...bindings[idx], [slot]: combo };
    set({ keybindings: bindings });
    try { localStorage.setItem('abm-keybindings', JSON.stringify(bindings)); } catch { /* ignore */ }
  },

  resetKeybindings: () => {
    const bindings = [...DEFAULT_KEYBINDINGS];
    set({ keybindings: bindings });
    try { localStorage.setItem('abm-keybindings', JSON.stringify(bindings)); } catch { /* ignore */ }
  },

  setKeybindingsOpen: (open) => set({ keybindingsOpen: open }),

  // ── Clipboard Methods ──────────────────────────────────────────

  copySelected: () => {
    const state = get();
    if (!state.beatmap) return;
    if (state.selectedNoteIds.length === 0 && state.selectedSlideIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const notes = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => JSON.parse(JSON.stringify(diff.notes[i])) as Note);
    const slides = state.selectedSlideIds
      .filter(i => i < (diff.slides?.length ?? 0))
      .map(i => JSON.parse(JSON.stringify(diff.slides[i])) as Slide);
    set({ clipboard: notes, clipboardSlides: slides, clipboardBpm: state.beatmap.bpm, areaClipboard: null });
  },

  cutSelected: () => {
    const state = get();
    if (!state.beatmap) return;
    if (state.selectedNoteIds.length === 0 && state.selectedSlideIds.length === 0) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff) return;
    const notes = state.selectedNoteIds
      .filter(i => i < diff.notes.length)
      .map(i => JSON.parse(JSON.stringify(diff.notes[i])) as Note);
    const slides = state.selectedSlideIds
      .filter(i => i < (diff.slides?.length ?? 0))
      .map(i => JSON.parse(JSON.stringify(diff.slides[i])) as Slide);
    set({ clipboard: notes, clipboardSlides: slides, clipboardBpm: state.beatmap.bpm, areaClipboard: null });
    if (notes.length) get().removeNotes([...state.selectedNoteIds]);
    if (slides.length) get().removeSlides([...state.selectedSlideIds]);
  },

  pasteAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    if (state.clipboard.length === 0 && state.clipboardSlides.length === 0) return;
    const bpm = state.beatmap.bpm;
    if (_tooEarlyToPlace(state, (beat / (bpm / 60)) * 1000)) return;
    // Earliest beat across BOTH lists, so notes and walls keep their relative
    // spacing when a mixed selection is pasted.
    const beatOffset = beat - _clipboardMinBeat(state.clipboard, state.clipboardSlides);
    const newNotes = state.clipboard.map(n => {
      const newBeat = n.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      return {
        ...JSON.parse(JSON.stringify(n)),
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeMs / 1000,
      } as Note;
    });
    const newSlides = state.clipboardSlides.map(s => {
      const newBeat = s.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      return {
        ...JSON.parse(JSON.stringify(s)),
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeMs / 1000,
      } as Slide;
    });
    if (newNotes.length) get().addNotes(newNotes);
    for (const s of newSlides) get().addPlacedSlide(s);
  },

  pasteMirrorAtBeat: (beat) => {
    const state = get();
    if (!state.beatmap) return;
    if (state.clipboard.length === 0 && state.clipboardSlides.length === 0) return;
    const bpm = state.beatmap.bpm;
    if (_tooEarlyToPlace(state, (beat / (bpm / 60)) * 1000)) return;
    const beatOffset = beat - _clipboardMinBeat(state.clipboard, state.clipboardSlides);
    const newNotes = state.clipboard.map(n => {
      const newBeat = n.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      const mirrored = JSON.parse(JSON.stringify(n)) as Note;
      mirrored.time_beats = newBeat;
      mirrored.time_ms = timeMs;
      mirrored.time_seconds = timeMs / 1000;
      mirrored.position = [-mirrored.position[0], mirrored.position[1], mirrored.position[2]];
      if (mirrored.hand === 'right') { mirrored.hand = 'left'; mirrored.note_type = 'left_hand'; }
      else if (mirrored.hand === 'left') { mirrored.hand = 'right'; mirrored.note_type = 'right_hand'; }
      if (mirrored.rail_nodes) {
        mirrored.rail_nodes = mirrored.rail_nodes.map(
          ([x, y, z]: [number, number, number]) => [-x, y, z] as [number, number, number]
        );
      }
      return mirrored;
    });
    const newSlides = state.clipboardSlides.map(s => {
      const newBeat = s.time_beats + beatOffset;
      const timeMs = (newBeat / (bpm / 60)) * 1000;
      const mirrored = JSON.parse(JSON.stringify(s)) as Slide;
      mirrored.time_beats = newBeat;
      mirrored.time_ms = timeMs;
      mirrored.time_seconds = timeMs / 1000;
      if (mirrored.position && mirrored.position.length >= 1) {
        mirrored.position = [-(mirrored.position[0] ?? 0), ...mirrored.position.slice(1)];
      }
      // A side-locked wall must also swap sides, or the mirror keeps the original
      // lane's mesh.
      mirrored.wall_type = _MIRROR_WALL_TYPE[mirrored.wall_type] ?? mirrored.wall_type;
      return mirrored;
    });
    if (newNotes.length) get().addNotes(newNotes);
    for (const s of newSlides) get().addPlacedSlide(s);
  },

  // ── Grid Methods ───────────────────────────────────────────────

  setGridVisible: (v) => set({ gridVisible: v }),
  setGridSize: (s) => set({ gridSize: s }),
  setSnapToGrid: (v) => set({ snapToGrid: v }),
  setRailSnapping: (v) => set({ railSnapping: v }),
  snapToRailPosition: (x, y, noteType, beat, skipIndex = -1) => {
    const s = get();
    if (!s.railSnapping || !isSynthVrView(s.activeView) || !s.beatmap) return [x, y];
    const diff = s.beatmap.difficulties[s.activeDifficulty];
    return (diff && railSnapXY(diff.notes, noteType, beat, s.beatmap.bpm || 120, skipIndex, _snapShape(s))) || [x, y];
  },
  setGridGuideType: (t) => set({ gridGuideType: t }),
  setGridGuideVisible: (v) => set({ gridGuideVisible: v }),
  setHighlightLevel: (level) => set({ highlightLevel: ((Math.round(level) % 4) + 4) % 4 }),
  setGridWidth: (w) => set({ gridWidth: w }),
  setGridHeight: (h) => set({ gridHeight: h }),
  setGridDivisionsH: (d) => set({ gridDivisionsH: d }),
  setGridDivisionsV: (d) => set({ gridDivisionsV: d }),
  setGridColor: (c) => set({ gridColor: c }),
  setGridOpacity: (o) => set({ gridOpacity: o }),
  setNotesGridYOffset: (v) => set({ notesGridYOffset: v }),

  snapToGridPosition: (x, y) => {
    const { snapToGrid, gridWidth, gridHeight, gridDivisionsH, gridDivisionsV, notesGridYOffset } = get();
    if (!snapToGrid) return [x, y];
    // The nearest drawn intersection inside the placement limits — the lattice IS the drawn lines,
    // centred where GridGuide draws them, so a snapped note sits on a line (SRVR-0006).
    return snapToGridLines(x, y, { gridWidth, gridHeight, gridDivisionsH, gridDivisionsV, notesGridYOffset });
  },

  snapWallToGridPosition: (x, y) => {
    const { snapToGrid, gridWidth, gridHeight, gridDivisionsH, gridDivisionsV, notesGridYOffset } = get();
    if (!snapToGrid) return [x, y];
    return snapToGridLinesUnbounded(x, y, { gridWidth, gridHeight, gridDivisionsH, gridDivisionsV, notesGridYOffset });
  },

  // ── Rail Placement Methods ─────────────────────────────────────

  startRailPlacement: (noteIndex) => {
    const state = get();
    if (!state.beatmap) return;
    const diff = state.beatmap.difficulties[state.activeDifficulty];
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    // Enable rail on this note if not already
    if (!note.has_rail) {
      get().updateNote(noteIndex, { has_rail: true, rail_nodes: [] });
    }
    set({ railPlacementActive: true, railPlacementNoteIndex: noteIndex });
  },

  addRailNode: (position) => {
    const state = get();
    if (!state.railPlacementActive || state.railPlacementNoteIndex === null) return;
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!diff) return;
    const noteIndex = state.railPlacementNoteIndex;
    if (noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];

    // Enforce forward-only: new node must be after the head note in time
    // position[2] = timeMs * 0.02, note.time_ms is in ms
    const newTimeMs = position[2] / 0.02;
    if (newTimeMs <= note.time_ms) return; // reject backward nodes
    if (_tooEarlyToPlace(state, newTimeMs)) return;

    // Insert in sorted position by time (Z component) so mid-rail insertions work
    const nodes = note.rail_nodes ? [...note.rail_nodes] : [];
    let insertIdx = nodes.length; // default: append at end
    for (let i = 0; i < nodes.length; i++) {
      if (position[2] < nodes[i][2]) {
        insertIdx = i;
        break;
      }
    }
    nodes.splice(insertIdx, 0, position);
    // Synth VR Mirror Mode: the mirror rail takes the mirrored node, in the same undo entry.
    if (state.mirrorMode && isSynthVrView(state.activeView)) {
      const twin = _mirrorRailIndex(diff.notes, note);
      if (twin >= 0) {
        pushUndo(state, 'Add mirrored rail node');
        const next = diff.notes.map((n, i) =>
          i === noteIndex ? { ...n, rail_nodes: nodes }
            : i === twin ? { ...n, rail_nodes: _withRailNode(n.rail_nodes, _mirrorRailNode(state, position)) } : n);
        setDiffNotes(set, get, next);
        return;
      }
    }
    get().updateNote(noteIndex, { rail_nodes: nodes });
  },

  addRailNodeNearCursor: (noteType, x, y, beat) => {
    const state = get();
    const diff = state.beatmap?.difficulties[state.activeDifficulty];
    if (!state.beatmap || !diff) return 'none';
    const bpm = state.beatmap.bpm || 120;
    const tol = onBeatTolerance(state.activeView, state.scrollStep, bpm);
    const beatOfZ = (z: number) => (z / 0.02 / 1000) * (bpm / 60);
    const here = beat;
    if (_tooEarlyToPlace(state, (beat / (bpm / 60)) * 1000)) return 'none';

    // Every rail of this note type: where it starts, where it ends, and whether a node already stands on `here`.
    let through: { index: number; head: number } | null = null;   // runs through the beat, no node on it
    let behind: { index: number; end: number } | null = null;     // wholly behind the beat; the latest end wins
    diff.notes.forEach((n: Note, index: number) => {
      if (!n.has_rail || n.note_type !== noteType) return;
      const nodes = n.rail_nodes ?? [];
      const head = n.time_beats;
      const end = nodes.length ? Math.max(...nodes.map(nd => beatOfZ(nd[2]))) : head;
      if (Math.abs(head - here) < tol || nodes.some(nd => Math.abs(beatOfZ(nd[2]) - here) < tol)) return;   // a node is already there
      if (head < here && end > here) {
        if (!through || head > through.head) through = { index, head };
      } else if (end < here) {
        if (!behind || end > behind.end) behind = { index, end };
      }
    });
    const target = (through as { index: number } | null)?.index ?? (behind as { index: number } | null)?.index;
    if (target === undefined) return 'none';

    const [cx, cy] = _clampNotePos(state, x, y);
    const timeMs = get().beatToTimeMs(here);
    const note = diff.notes[target];
    const nodes = note.rail_nodes ? [...note.rail_nodes] : [];
    const node: [number, number, number] = [cx, cy, timeMs * 0.02];
    let insertAt = nodes.length;
    for (let i = 0; i < nodes.length; i++) { if (node[2] < nodes[i][2]) { insertAt = i; break; } }
    nodes.splice(insertAt, 0, node);
    // Synth VR Mirror Mode: the mirror rail takes the mirrored node, in the same undo entry.
    const twin = state.mirrorMode && isSynthVrView(state.activeView) ? _mirrorRailIndex(diff.notes, note) : -1;
    if (twin >= 0) {
      pushUndo(state, 'Add mirrored rail node');
      setDiffNotes(set, get, diff.notes.map((n, i) =>
        i === target ? { ...n, rail_nodes: nodes }
          : i === twin ? { ...n, rail_nodes: _withRailNode(n.rail_nodes, _mirrorRailNode(state, node)) } : n));
    } else {
      get().updateNote(target, { rail_nodes: nodes });
    }
    return through ? 'inserted' : 'extended';
  },

  finishRailPlacement: () => {
    set({ railPlacementActive: false, railPlacementNoteIndex: null });
  },

  cancelRailPlacement: () => {
    const state = get();
    if (state.railPlacementNoteIndex !== null) {
      const diff = state.beatmap?.difficulties[state.activeDifficulty];
      if (diff && state.railPlacementNoteIndex < diff.notes.length) {
        const note = diff.notes[state.railPlacementNoteIndex];
        if (note.rail_nodes && note.rail_nodes.length === 0) {
          // Synth VR Mirror Mode: the mirror rail that never got a node goes with it.
          const twin = state.mirrorMode && isSynthVrView(state.activeView) ? _mirrorRailIndex(diff.notes, note) : -1;
          if (twin >= 0 && diff.notes[twin].rail_nodes?.length === 0) get().updateNote(twin, { has_rail: false, rail_nodes: null });
          get().updateNote(state.railPlacementNoteIndex, { has_rail: false, rail_nodes: null });
        }
      }
    }
    set({ railPlacementActive: false, railPlacementNoteIndex: null });
  },

  // ── Note Dragging ──────────────────────────────────────────────

  startDrag: (index) => {
    const diff = get().getCurrentDifficulty();
    if (!diff || index >= diff.notes.length) return;
    set({ draggingNoteIndex: index, selectedNoteIds: [index] });
  },

  finishDrag: (newBeat, newX, newY) => {
    const state = get();
    const idx = state.draggingNoteIndex;
    if (idx === null) return;
    const diff = state.getCurrentDifficulty();
    if (!diff || idx >= diff.notes.length) return;

    const bpm = state.beatmap?.bpm || 120;
    const timeMs = (newBeat / (bpm / 60)) * 1000;
    const timeSeconds = timeMs / 1000;

    // If multiple notes are selected, compute the delta and apply to all
    const selectedIds = state.selectedNoteIds;
    if (selectedIds.length > 1 && selectedIds.includes(idx)) {
      const origNote = diff.notes[idx];
      const dBeat = newBeat - origNote.time_beats;
      const dX = newX - origNote.position[0];
      const dY = newY - origNote.position[1];

      pushUndo(state, `Move ${selectedIds.length} notes`);
      const newNotes = [...diff.notes];
      for (const id of selectedIds) {
        if (id >= newNotes.length) continue;
        const n = newNotes[id];
        const nBeat = n.time_beats + dBeat;
        const nTimeMs = (nBeat / (bpm / 60)) * 1000;
        // Synth VR: a dragged group may leave the grid.
        const [cx, cy] = isSynthVrView(state.activeView) ? [n.position[0] + dX, n.position[1] + dY] : _clampNotePos(state, n.position[0] + dX, n.position[1] + dY);
        newNotes[id] = {
          ...n,
          time_beats: nBeat,
          time_ms: nTimeMs,
          time_seconds: nTimeMs / 1000,
          position: [cx, cy, nTimeMs * 0.02] as [number, number, number],
        };
      }
      newNotes.sort((a, b) => a.time_ms - b.time_ms);
      setDiffNotes(set, get, newNotes);
    } else {
      // Single note drag
      pushUndo(state, 'Move note');
      const newNotes = [...diff.notes];
      const [cx, cy] = _clampNotePos(state, newX, newY);
      newNotes[idx] = {
        ...newNotes[idx],
        time_beats: newBeat,
        time_ms: timeMs,
        time_seconds: timeSeconds,
        position: [cx, cy, timeMs * 0.02] as [number, number, number],
      };
      newNotes.sort((a, b) => a.time_ms - b.time_ms);
      setDiffNotes(set, get, newNotes);
    }

    set({ draggingNoteIndex: null });
  },

  cancelDrag: () => {
    set({ draggingNoteIndex: null });
  },

  // ── Bookmarks (named, global) ──────────────────────────────────

  addBookmark: (beat, name) => {
    if (_tooEarlyToPlace(get(), get().beatToTimeMs(beat))) return;
    const bm = get().bookmarks;
    // Don't add duplicate near this beat (within 0.01 tolerance)
    if (bm.some(b => Math.abs(b.beat - beat) < 0.01)) return;
    const next = [...bm, { beat, name }].sort((a, b) => a.beat - b.beat);
    set({ bookmarks: next });
    saveBookmarkDraft(get().beatmap?.id, next);
  },

  toggleBookmarkAtPlayhead: () => {
    const s = get();
    const beat = s.playback.currentBeat;
    if (s.bookmarks.some(b => Math.abs(b.beat - beat) < 0.01)) { s.removeBookmark(beat); return; }
    if (_tooEarlyToPlace(s, s.beatToTimeMs(beat))) return;
    s.addBookmark(beat, '');
    set({ editingBookmarkBeat: beat });
  },

  removeBookmark: (beat) => {
    const state = get();
    const editing = state.editingBookmarkBeat != null && Math.abs(state.editingBookmarkBeat - beat) < 0.01
      ? null : state.editingBookmarkBeat;
    const next = state.bookmarks.filter(b => Math.abs(b.beat - beat) >= 0.01);
    set({ bookmarks: next, editingBookmarkBeat: editing });
    saveBookmarkDraft(state.beatmap?.id, next);
  },

  updateBookmarkName: (beat, name) => {
    const next = get().bookmarks.map(b => Math.abs(b.beat - beat) < 0.01 ? { ...b, name } : b);
    set({ bookmarks: next });
    saveBookmarkDraft(get().beatmap?.id, next);
  },

  setBookmarkColor: (beat, color) => {
    const next = get().bookmarks.map(b => Math.abs(b.beat - beat) < 0.01 ? { ...b, color } : b);
    set({ bookmarks: next });
    saveBookmarkDraft(get().beatmap?.id, next);
  },

  moveBookmark: (oldBeat, newBeat) => {
    const state = get();
    const bm = state.bookmarks;
    const idx = bm.findIndex(b => Math.abs(b.beat - oldBeat) < 0.01);
    if (idx < 0) return;
    if (bm.some((b, i) => i !== idx && Math.abs(b.beat - newBeat) < 0.01)) return; // don't stack
    const next = bm.map((b, i) => (i === idx ? { ...b, beat: newBeat } : b)).sort((a, b) => a.beat - b.beat);
    const wasEditing = state.editingBookmarkBeat != null && Math.abs(state.editingBookmarkBeat - oldBeat) < 0.01;
    set({ bookmarks: next, ...(wasEditing ? { editingBookmarkBeat: newBeat } : {}) });
    saveBookmarkDraft(state.beatmap?.id, next);
  },

  clearBookmarks: () => {
    set({ bookmarks: [], editingBookmarkBeat: null });
    saveBookmarkDraft(get().beatmap?.id, []);
  },

  clearBookmarksPending: false,

  requestClearBookmarks: () => {
    const state = get();
    if (state.bookmarks.length === 0) return;
    if (!isSynthVrView(state.activeView)) { state.clearBookmarks(); return; }
    set({ clearBookmarksPending: true });
  },

  confirmClearBookmarks: () => {
    if (!get().clearBookmarksPending) return;
    set({ clearBookmarksPending: false });
    get().clearBookmarks();
  },

  cancelClearBookmarks: () => set({ clearBookmarksPending: false }),

  setBookmarkDialogOpen: (open) => set({ bookmarkDialogOpen: open }),
  setCustomDifficultyDialogOpen: (open) => set({ customDifficultyDialogOpen: open }),

  setEditingBookmarkBeat: (beat) => set({ editingBookmarkBeat: beat }),

  jumpToNextBookmark: () => {
    const { bookmarks, playback, beatmap } = get();
    if (bookmarks.length === 0 || !beatmap) return;
    const current = playback.currentBeat;
    const next = bookmarks.find(b => b.beat > current + 0.01);
    if (next !== undefined) {
      const bpm = beatmap.bpm || 120;
      const offset = beatmap.offset || 0;
      const time = (next.beat / (bpm / 60)) + (offset / 1000);
      get().setCurrentTime(Math.max(0, time));
    }
  },

  jumpToPrevBookmark: () => {
    const { bookmarks, playback, beatmap } = get();
    if (bookmarks.length === 0 || !beatmap) return;
    const current = playback.currentBeat;
    const prev = [...bookmarks].reverse().find(b => b.beat < current - 0.01);
    if (prev !== undefined) {
      const bpm = beatmap.bpm || 120;
      const offset = beatmap.offset || 0;
      const time = (prev.beat / (bpm / 60)) + (offset / 1000);
      get().setCurrentTime(Math.max(0, time));
    }
  },

  // ── Flash Events (global light effects) ────────────────────────

  // ── Shapes ────────────────────────────────────────────────
  // Design has its OWN undo/redo history (separate from the note/slide history)
  // since it's a separate, localStorage-persisted layer. Continuous slider edits
  // to the same group coalesce into one history step (pushDesignUndo below).

  // My Library — save/reuse wall arts across projects. Server-backed (per-user
  // ownership + public sharing). The server is the source of truth; we do NOT
  // mirror it to localStorage — a shared browser must never cache one user's
  // shapes where another user could see them. On a server blip we keep the
  // in-memory state rather than blanking the panel.
  loadDesignLibraryFromServer: async () => {
    // One-time migration: upload the pre-existing localStorage-only library as
    // the current user's private shapes. Per-shape best-effort so one shape that
    // can't upload (e.g. an id already owned by someone else → 403) never blocks
    // the batch. The flag is set only once the server is confirmed reachable
    // (listShapes below succeeds), so a full outage safely retries next open
    // without losing the local copy (still under DESIGN_LIB_KEY).
    const needMigrate = !localStorage.getItem(DESIGN_LIB_MIGRATED_KEY);
    if (needMigrate) {
      for (const s of loadDesignLibrary()) {
        try { await saveShape(s); } catch { /* skip a shape that won't upload */ }
      }
    }
    try {
      const shapes = await listShapes();
      set({ designLibrary: shapes });
      if (needMigrate) localStorage.setItem(DESIGN_LIB_MIGRATED_KEY, '1');
    } catch {
      // Server unreachable — keep whatever is already in memory; retry next open.
    }
  },
  addToDesignLibrary: (groupId) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const saved = makeSavedShape(g, g.name || '');
    // Optimistic: show it immediately, then persist server-side and refresh.
    const lib = [{ ...saved, visibility: 'private' as const, is_owner: true }, ...state.designLibrary];
    set({ designLibrary: lib });
    void (async () => {
      try {
        await saveShape(saved);
        await get().loadDesignLibraryFromServer();
      } catch { /* offline — the optimistic in-memory entry stands in */ }
    })();
  },
  removeFromDesignLibrary: (id) => {
    const lib = get().designLibrary.filter(s => s.id !== id);
    set({ designLibrary: lib });
    void deleteShape(id).catch(() => { /* offline — local removal stands */ });
  },
  setLibraryShapeVisibility: async (id, isPublic) => {
    await setShapeVisibility(id, isPublic);
    const lib = get().designLibrary.map(s =>
      (s.id === id ? { ...s, visibility: (isPublic ? 'public' : 'private') as 'private' | 'public' } : s));
    set({ designLibrary: lib });
  },
  addLibraryArtToMap: (id) => {
    const state = get();
    const saved = state.designLibrary.find(s => s.id === id);
    if (!saved) return;
    pushDesignUndo(state, true);
    const g = instantiateSavedShape(saved, state.playback.currentBeat);
    const next = [...state.designShapes, g];
    set({ designShapes: next, selectedDesignShapeIds: [g.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },

  addDesignShape: () => {
    const state = get();
    pushDesignUndo(state, true);
    // New decor comes up in a random vivid neon colour so it pops against the dark
    // stage out of the box (the panel can retint it).
    const group = makeDesignShape(state.playback.currentBeat, randomNeonColor());
    const next = [...state.designShapes, group];
    set({ designShapes: next, selectedDesignShapeIds: [group.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignShape: (id, patch, force = false) => {
    const state = get();
    // Coalesce rapid edits to the same group (slider drags) into one undo step.
    // Discrete/structural edits (Combine toggles, add/remove/duplicate part,
    // primary promotion) pass force=true so they ALWAYS get their own step —
    // otherwise they'd merge into whatever slider/text edit preceded them
    // within the coalescing window and become impossible to revert alone.
    pushDesignUndo(state, force, `update:${id}`);
    const next = state.designShapes.map(g => (g.id === id ? { ...g, ...patch } : g));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignShapes: (ids, patch, force = false) => {
    const state = get();
    if (ids.length === 0) return;
    // Coalesce rapid multi-edits (slider drags on a multi-selection) into one step.
    pushDesignUndo(state, force, `update:${[...ids].sort().join(',')}`);
    const set2 = new Set(ids);
    const next = state.designShapes.map(g => (set2.has(g.id) ? { ...g, ...patch } : g));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  updateDesignMesh: (groupId, partId, patch, force = false) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    // Coalesce rapid edits to the same part (slider drags / gizmo drags) into one step.
    pushDesignUndo(state, force, `updatepart:${partId}`);
    const next = state.designShapes.map(w => (w.id === groupId
      ? { ...w, parts: (w.parts || []).map(p => (p.id === partId ? { ...p, ...patch } : p)) }
      : w));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  // ── Custom-curve control nodes ─────────────────────────────────
  // The path is seeded (makeDefaultCustomPath) on demand so these always operate
  // on a real array, even for a group that just switched to the Custom curve.
  updateDesignNode: (groupId, index, patch, force = false) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    if (index < 0 || index >= path.length) return;
    // Coalesce rapid gizmo/slider drags on the same node into one undo step.
    pushDesignUndo(state, force, `updatenode:${groupId}:${index}`);
    const nextPath = path.map((p, i) => (i === index ? { ...p, ...patch } : p));
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath } : w));
    set({ designShapes: next });
    saveDesignDraft(state.beatmap?.id, next);
  },
  addDesignNode: (groupId) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    pushDesignUndo(state, true);
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    // Extend past the tip along the last segment's direction, so the new node
    // appears just beyond the end of the path (not on top of it).
    const last = path[path.length - 1];
    const prev = path[path.length - 2] || last;
    const dx = last.x - prev.x, dy = last.y - prev.y, dz = last.z - prev.z;
    const nn: DesignPathNode = {
      x: Math.round((last.x + (dx || 1)) * 100) / 100,
      y: Math.round((last.y + dy) * 100) / 100,
      z: Math.round((last.z + dz) * 100) / 100,
    };
    const nextPath = [...path, nn];
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath, curve: 'custom' as const } : w));
    set({ designShapes: next, activeDesignNodeIndex: nextPath.length - 1 });
    saveDesignDraft(state.beatmap?.id, next);
  },
  removeDesignNode: (groupId, index) => {
    const state = get();
    const g = state.designShapes.find(w => w.id === groupId);
    if (!g) return;
    const path = (g.customPath && g.customPath.length) ? g.customPath : makeDefaultCustomPath();
    if (path.length <= 2 || index < 0 || index >= path.length) return;   // keep at least 2 nodes
    pushDesignUndo(state, true);
    const nextPath = path.filter((_, i) => i !== index);
    const next = state.designShapes.map(w => (w.id === groupId ? { ...w, customPath: nextPath } : w));
    const sel = state.activeDesignNodeIndex;
    set({
      designShapes: next,
      activeDesignNodeIndex: sel == null ? null : Math.min(sel, nextPath.length - 1),
    });
    saveDesignDraft(state.beatmap?.id, next);
  },
  setActiveDesignNodeIndex: (index) => set({ activeDesignNodeIndex: index }),
  removeDesignShape: (id) => get().removeDesignShapes([id]),
  removeDesignShapes: (ids) => {
    const state = get();
    if (ids.length === 0) return;
    pushDesignUndo(state, true);
    const kill = new Set(ids);
    const next = state.designShapes.filter(g => !kill.has(g.id));
    set({
      designShapes: next,
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => !kill.has(id)),
    });
    saveDesignDraft(state.beatmap?.id, next);
  },
  duplicateDesignShape: (id, name) => {
    const state = get();
    const src = state.designShapes.find(g => g.id === id);
    if (!src) return;
    pushDesignUndo(state, true);
    const copy: DesignShape = {
      ...src,
      id: `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      // Nudge forward in time (not jumped to the playhead) so the duplicate
      // sits visibly next to the original instead of landing on top of it.
      beat: src.beat + 2,
      name: name ?? src.name,
    };
    const next = [...state.designShapes, copy];
    set({ designShapes: next, selectedDesignShapeIds: [copy.id], activeDesignMeshId: PRIMARY_MESH_ID, activeDesignNodeIndex: null });
    saveDesignDraft(state.beatmap?.id, next);
  },
  // Entering a wall art (a real id) always lands on the primary shape; going
  // back to the list (null) hides the move-gizmo. Node selection always resets.
  selectDesignShape: (id) => set({ selectedDesignShapeIds: id ? [id] : [], activeDesignMeshId: id ? PRIMARY_MESH_ID : null, activeDesignNodeIndex: null }),
  setSelectedDesignShapeIds: (ids) => {
    const uniq = Array.from(new Set(ids));
    // The move-gizmo only makes sense for a single selected wall art.
    set({ selectedDesignShapeIds: uniq, activeDesignMeshId: uniq.length === 1 ? PRIMARY_MESH_ID : null, activeDesignNodeIndex: null });
  },
  // Switching the active shape clears any selected custom-path node.
  setActiveDesignMeshId: (id) => set({ activeDesignMeshId: id, activeDesignNodeIndex: null }),
  setShapeDesignerOpen: (open) => set({ shapeDesignerOpen: open }),
  copyDesignShape: () => {
    const state = get();
    // Copy the primary (last-selected) group.
    const id = state.selectedDesignShapeIds[state.selectedDesignShapeIds.length - 1];
    const src = state.designShapes.find(g => g.id === id);
    if (src) set({ designClipboard: { ...src } });
  },
  pasteDesignShape: () => {
    const state = get();
    const src = state.designClipboard;
    if (!src) return;
    pushDesignUndo(state, true);
    const copy: DesignShape = {
      ...src,
      id: `decor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      beat: state.playback.currentBeat,
    };
    const next = [...state.designShapes, copy];
    set({ designShapes: next, selectedDesignShapeIds: [copy.id], activeDesignMeshId: PRIMARY_MESH_ID, shapeDesignerOpen: true });
    saveDesignDraft(state.beatmap?.id, next);
  },
  undoDesign: () => {
    const state = get();
    if (state.designUndoStack.length === 0) return;
    const prev = state.designUndoStack[state.designUndoStack.length - 1];
    set({
      designShapes: prev,
      designUndoStack: state.designUndoStack.slice(0, -1),
      designRedoStack: [...state.designRedoStack, state.designShapes],
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => prev.some(g => g.id === id)),
    });
    saveDesignDraft(state.beatmap?.id, prev);
    _designUndoKey = '';   // break coalescing so the next edit starts a fresh step
  },
  redoDesign: () => {
    const state = get();
    if (state.designRedoStack.length === 0) return;
    const nextWalls = state.designRedoStack[state.designRedoStack.length - 1];
    set({
      designShapes: nextWalls,
      designRedoStack: state.designRedoStack.slice(0, -1),
      designUndoStack: [...state.designUndoStack, state.designShapes],
      selectedDesignShapeIds: state.selectedDesignShapeIds.filter(id => nextWalls.some(g => g.id === id)),
    });
    saveDesignDraft(state.beatmap?.id, nextWalls);
    _designUndoKey = '';
  },

  addFlashEvent: (event) => {
    if (_tooEarlyToPlace(get(), get().beatToTimeMs(event.time_beats))) return;
    const fe = get().flashEvents;
    if (fe.some(e => Math.abs(e.time_beats - event.time_beats) < 0.01)) return;
    const next = [...fe, event].sort((a, b) => a.time_beats - b.time_beats);
    pushLightUndo(get(), 'Add light');
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  toggleLightAtPlayhead: () => {
    const s = get();
    const beat = s.playback.currentBeat;
    if (s.flashEvents.some(e => Math.abs(e.time_beats - beat) < 0.01)) { s.removeFlashEvent(beat); return; }
    // A new light is a normal single pulse; strobe is opt-in per light (its popup).
    s.addFlashEvent({ ...s.flashDefaults, time_beats: beat, repeat: 1 });
  },

  toggleEffectAtPlayhead: () => {
    const s = get();
    const diff = s.activeDifficulty;
    const beat = s.playback.currentBeat;
    const mine = s.effects[diff] ?? [];
    if (mine.some(b => Math.abs(b - beat) < 0.01)) { s.removeEffect(beat); return; }
    if (_tooEarlyToPlace(s, s.beatToTimeMs(beat))) return;
    const next = { ...s.effects, [diff]: [...mine, beat].sort((a, b) => a - b) };
    pushLightUndo(s, 'Add effect');
    set({ effects: next });
    saveEffectDraft(s.beatmap?.id, next);
  },

  removeEffect: (beat) => {
    const s = get();
    const diff = s.activeDifficulty;
    const kept = (s.effects[diff] ?? []).filter(b => Math.abs(b - beat) >= 0.01);
    if (kept.length === (s.effects[diff] ?? []).length) return;
    const next = { ...s.effects, [diff]: kept };
    pushLightUndo(s, 'Remove effect');
    set({ effects: next });
    saveEffectDraft(s.beatmap?.id, next);
  },

  removeFlashEvent: (beat) => {
    const state = get();
    const next = state.flashEvents.filter(e => Math.abs(e.time_beats - beat) >= 0.01);
    const sel = state.selectedFlashBeats.filter(b => Math.abs(b - beat) >= 0.01);
    const editing = state.editingFlashBeat != null && Math.abs(state.editingFlashBeat - beat) < 0.01
      ? (sel[0] ?? null) : state.editingFlashBeat;
    if (next.length !== state.flashEvents.length) pushLightUndo(state, 'Remove light');
    set({ flashEvents: next, selectedFlashBeats: sel, editingFlashBeat: editing });
    saveFlashDraft(state.beatmap?.id, next);
  },

  updateFlashEvent: (beat, updates) => {
    // A popup slider or text field fires this per change: one undo step per light, not per keystroke.
    pushLightUndo(get(), 'Edit light', `light:${beat}`);
    const next = get().flashEvents.map(e => Math.abs(e.time_beats - beat) < 0.01 ? { ...e, ...updates } : e);
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  moveFlashEvent: (oldBeat, newBeat) => {
    const state = get();
    const fe = state.flashEvents;
    const idx = fe.findIndex(e => Math.abs(e.time_beats - oldBeat) < 0.01);
    if (idx < 0) return;
    // Don't land on top of another light.
    if (fe.some((e, i) => i !== idx && Math.abs(e.time_beats - newBeat) < 0.01)) return;
    const next = fe
      .map((e, i) => (i === idx ? { ...e, time_beats: newBeat } : e))
      .sort((a, b) => a.time_beats - b.time_beats);
    const wasEditing = state.editingFlashBeat != null && Math.abs(state.editingFlashBeat - oldBeat) < 0.01;
    pushLightUndo(state, 'Move light');
    // Keep the selection pointing at the moved light.
    const sel = state.selectedFlashBeats.map(b => Math.abs(b - oldBeat) < 0.01 ? newBeat : b);
    set({ flashEvents: next, selectedFlashBeats: sel, ...(wasEditing ? { editingFlashBeat: newBeat } : {}) });
    saveFlashDraft(state.beatmap?.id, next);
  },

  clearFlashEvents: () => {
    if (get().flashEvents.length) pushLightUndo(get(), 'Clear lights');
    set({ flashEvents: [], selectedFlashBeats: [], editingFlashBeat: null });
    saveFlashDraft(get().beatmap?.id, []);
  },

  setEditingFlashBeat: (beat) => set({
    editingFlashBeat: beat,
    selectedFlashBeats: beat == null ? [] : [beat],
  }),

  setSelectedFlashBeats: (beats) => set({
    selectedFlashBeats: beats,
    editingFlashBeat: beats.length ? beats[beats.length - 1] : null,
  }),

  toggleFlashSelection: (beat, additive) => {
    const state = get();
    if (!additive) {
      // Plain click: if this light is already the sole selection, a second click
      // closes the popup. Otherwise select just this one.
      const isSole = state.selectedFlashBeats.length === 1
        && Math.abs(state.selectedFlashBeats[0] - beat) < 0.01;
      set(isSole
        ? { selectedFlashBeats: [], editingFlashBeat: null }
        : { selectedFlashBeats: [beat], editingFlashBeat: beat });
      return;
    }
    const has = state.selectedFlashBeats.some(b => Math.abs(b - beat) < 0.01);
    const sel = has
      ? state.selectedFlashBeats.filter(b => Math.abs(b - beat) >= 0.01)
      : [...state.selectedFlashBeats, beat];
    set({ selectedFlashBeats: sel, editingFlashBeat: has ? (sel[sel.length - 1] ?? null) : beat });
  },

  updateFlashEvents: (beats, updates) => {
    const inSet = (t: number) => beats.some(b => Math.abs(b - t) < 0.01);
    pushLightUndo(get(), 'Edit lights', `lights:${[...beats].sort().join(',')}`);
    const next = get().flashEvents.map(e => inSet(e.time_beats) ? { ...e, ...updates } : e);
    set({ flashEvents: next });
    saveFlashDraft(get().beatmap?.id, next);
  },

  setFlashDefaults: (defaults) => {
    set({ flashDefaults: { ...get().flashDefaults, ...defaults } });
  },

  // ── Select All ─────────────────────────────────────────────────

  selectAll: () => {
    const diff = get().getCurrentDifficulty();
    if (!diff) return;
    set({
      selectedNoteIds: diff.notes.map((_: Note, i: number) => i),
      selectedSlideIds: (diff.slides || []).map((_: Slide, i: number) => i),
      // In Synth VR, the desktop editor's SelectAll is also a range over the whole track (SRVR-0316 AC7).
      ...(get().activeView === 'synth-rider'
        ? { areaRange: { startBeat: 0, endBeat: _selectAllEndBeat(get(), diff) }, areaGesture: null }
        : {}),
    });
  },

  setPatternDifficultyVisible: (visible) => set({ patternDifficultyVisible: visible }),
  setFovPanelOpen: (open) => set({ fovPanelOpen: open }),
  setPerfMode: (mode) => {
    set({ perfMode: mode });
    try { localStorage.setItem('synapse_perf_mode', mode); } catch { /* ignore */ }
  },
  setActiveView: (view) => {
    const st = get();
    const isEditor = (v: ActiveView): v is EditorKey => v === 'synth-rider' || v === 'console';
    // Entering an editor that doesn't own the live map: stash the current map in
    // its owner's slot and restore the target editor's own map (VR ⇄ Console are
    // independent). Non-editor views (dashboard/flow) leave the live map alone.
    if (isEditor(view) && st.mapOwner && st.mapOwner !== view) {
      const outSlot: MapSlot = {
        beatmap: st.beatmap,
        lastBeatmapId: st.lastBeatmapId,
        activeDifficulty: st.activeDifficulty,
        bookmarks: st.bookmarks,
        flashEvents: st.flashEvents,
        effects: st.effects,
      };
      const inSlot = st.mapSlots[view];
      set({
        activeView: view,
        mapOwner: view,
        mapSlots: { ...st.mapSlots, [st.mapOwner]: outSlot },
        beatmap: inSlot?.beatmap ?? null,
        lastBeatmapId: inSlot?.lastBeatmapId ?? null,
        activeDifficulty: inSlot?.activeDifficulty ?? st.activeDifficulty,
        bookmarks: inSlot?.bookmarks ?? [],
        flashEvents: inSlot?.flashEvents ?? [],
        effects: inSlot?.effects ?? {},
        selectedNoteIds: [], selectedSlideIds: [], undoStack: [], redoStack: [],
        playback: { playing: false, currentTime: 0, currentBeat: 0, duration: 0 },
      });
      return;
    }
    // First editor entered this session claims ownership of the auto-loaded map.
    if (isEditor(view) && !st.mapOwner) {
      set({ activeView: view, mapOwner: view });
      return;
    }
    set({ activeView: view });
  },

  // ── Helpers ───────────────────────────────────────────────────

  getCurrentDifficulty: () => {
    const { beatmap, activeDifficulty } = get();
    return beatmap?.difficulties[activeDifficulty] || null;
  },

  // Time snapping follows the VISIBLE "Step" control (scrollStep), not the separate
  // snapDivision dropdown. scrollStep already carries the exact step in beats for
  // every family — including triplets (1/3, 1/6…), which a power-of-two
  // snapDivision could never express, so ternary Step used to be rounded away the
  // moment you placed or paused.
  snapBeat: (beat) => {
    const step = get().scrollStep;
    if (!step || step <= 0) return beat;
    return Math.round(beat / step) * step;
  },

  beatToTimeMs: (beat) => {
    const { beatmap } = get();
    const bpm = beatmap?.bpm || 120;
    return (beat / (bpm / 60)) * 1000;
  },
}));

// ── Internal Helpers ────────────────────────────────────────────────────────

/** Clamp X/Y to the placement limits in note-space (store-level safety net). The limits are the
 *  desktop editor's and fixed: no grid setting and no note size moves them. The scene's
 *  `_clampToGrid` calls the same function (style rule 9). */
function _clampNotePos(_state: EditorState, x: number, y: number): [number, number] {
  return clampToPlacementLimits(x, y);
}

/** Track.cs `DuplicateNoteOppositeHand`: how far the copy sits from its source, in note units. */
const DUPLICATE_X_OFFSET = 0.275;

/**
 * Synth VR's Ctrl+D, as the desktop editor does it (Track.cs `DuplicateNotesOppositeHand`). The beats it
 * acts on are the area range's, else the playhead's. A beat that holds exactly one note and that note is
 * a left or right hand note or rail gains a copy of the other hand on the same beat: same Y, X moved
 * 0.275 toward the copy's own side (left of a right-hand note, right of a left-hand one), or the other
 * way when that would leave the grid. The rail's nodes move by the same X; nothing is mirrored.
 */
function _duplicateOppositeHandVr(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
): void {
  const target = _areaTarget(get());
  if (!target) return;
  const { state, diff, range, bpm } = target;
  const inArea = objectsInRange(diff, range, bpm, null).notes;
  const additions: Note[] = [];
  for (const idx of inArea) {
    const src = diff.notes[idx];
    if (src.hand !== 'left' && src.hand !== 'right') continue;                 // a special is not duplicated
    if (diff.notes.filter(n => nearBeat(n.time_beats, src.time_beats)).length !== 1) continue;   // one note on the beat
    let dx = src.hand === 'right' ? -DUPLICATE_X_OFFSET : DUPLICATE_X_OFFSET;
    const [cx, cy] = clampToPlacementLimits(src.position[0] + dx, src.position[1]);
    if (cx !== src.position[0] + dx || cy !== src.position[1]) dx = -dx;        // off the grid: the other side
    const dup = JSON.parse(JSON.stringify(src)) as Note;
    dup.position = [src.position[0] + dx, src.position[1], src.position[2]];
    if (dup.rail_nodes) dup.rail_nodes = dup.rail_nodes.map(([x, y, z]) => [x + dx, y, z] as [number, number, number]);
    dup.hand = src.hand === 'right' ? 'left' : 'right';
    dup.note_type = src.hand === 'right' ? 'left_hand' : 'right_hand';
    additions.push(dup);
  }
  if (additions.length === 0) return;
  pushUndo(state, `Duplicate ${additions.length} to opposite hand`);
  const newNotes = [...diff.notes, ...additions].sort((a, b) => a.time_ms - b.time_ms);
  if (!setDiffNotes(set, get, newNotes)) return;
  _dupSession = {
    startedAt: performance.now(),
    raw: [0, 0],
    copies: additions.map(d => ({
      beats: d.time_beats, hand: d.hand as 'left' | 'right',
      start: [d.position[0], d.position[1]] as [number, number],
      nodes: d.rail_nodes ? d.rail_nodes.map(n => [...n] as [number, number, number]) : null,
    })),
  };
  set(() => ({ duplicateMoveActive: true, ...(state.areaRange ? {} : { selectedNoteIds: [] }) }));   // indices shifted by the sort
}

/** The beats `changeNoteColor` last changed, so that cycling again inside an area range reaches only the notes
 *  that were first changed and not ones that already had the colour they were cycled to; it ends when the
 *  playhead moves to another beat (Track.cs `colorChangeSessionInProgress`). */
let _colorSession: { beat: number; beats: number[]; map: string } | null = null;

/** Track.cs: a held Ctrl+D moves the copies only after this long. */
const DUPLICATE_MOVE_DELAY_MS = 250;
/** Note units per pixel of pointer movement, the gain of the move handles. */
const DUPLICATE_MOVE_GAIN = 0.005;

/** The copies the last Ctrl+D made, where they began, and how far the held key has moved them. */
let _dupSession: {
  startedAt: number;
  raw: [number, number];
  copies: Array<{ beats: number; hand: 'left' | 'right'; start: [number, number]; nodes: [number, number, number][] | null }>;
} | null = null;

/** Synth VR Mirror Mode: the rail that mirrors `src` — the other hand's rail (a special's own type) on the same
 *  beat — or -1 when there is none. */
function _mirrorRailIndex(notes: readonly Note[], src: Note): number {
  const type = src.hand === 'right' ? 'left_hand' : src.hand === 'left' ? 'right_hand' : src.note_type;
  return notes.findIndex(n => n !== src && n.has_rail && n.note_type === type && nearBeat(n.time_beats, src.time_beats));
}

/** A rail node as its mirror rail holds it: X mirrored, Y too with `mirrorInvertY`, each held inside the grid on its
 *  own (SRVR-0009 AC7). Time is the same. */
function _mirrorRailNode(state: EditorState, node: [number, number, number]): [number, number, number] {
  const [x, y] = _clampNotePos(state, -node[0], state.mirrorInvertY ? -node[1] : node[1]);
  return [x, y, node[2]];
}

/** `nodes` with `node` inserted where its time puts it. */
function _withRailNode(nodes: readonly [number, number, number][] | null, node: [number, number, number]): [number, number, number][] {
  const out = nodes ? [...nodes] : [];
  let at = out.length;
  for (let i = 0; i < out.length; i++) { if (node[2] < out[i][2]) { at = i; break; } }
  out.splice(at, 0, node);
  return out;
}

// While an interactive drag is in progress we take ONE snapshot at the start
// (via beginDrag) and suppress the per-frame snapshots the move-updates would
// otherwise push — so undo jumps straight back to the pre-drag state.
let _undoSuppressed = false;

// The last entry pushUndo wrote and the history it replaced, so an edit that is then refused can take its
// own entry back (see setDiffNotes).
let _lastUndoPush: { entry: HistoryEntry; prevUndo: HistoryEntry[]; prevRedo: HistoryEntry[] } | null = null;

// The entry a coalescing edit (a popup slider or text field) last pushed, so the next edit with the same
// key joins it instead of burying the undo stack. An undo pops the entry, which ends the run.
let _coalescedEntry: HistoryEntry | null = null;
let _coalesceKey: string | null = null;

/**
 * Snapshot for undo. In Synth VR every entry also carries the lights and the effects, because their
 * edits are history entries too: the history is one line over notes, walls, lights and effects, and
 * restoring all of them keeps it consistent. Bookmarks are not part of it. `withLights` forces the
 * snapshot in the other views (area operations); `coalesceKey` merges consecutive edits of one thing.
 */
function pushUndo(state: EditorState, label: string, withLights = false, coalesceKey?: string) {
  if (_undoSuppressed) { _lastUndoPush = null; return; }   // mid-drag: the start snapshot is not this edit's
  if (!state.beatmap) return;
  const top = state.undoStack[state.undoStack.length - 1];
  if (coalesceKey && coalesceKey === _coalesceKey && top && top === _coalescedEntry) return;
  const lights = withLights || isSynthVrView(state.activeView);
  const entry: HistoryEntry = {
    label,
    difficulties: JSON.parse(JSON.stringify(state.beatmap.difficulties)),
    ...(lights ? {
      flashEvents: JSON.parse(JSON.stringify(state.flashEvents)),
      effects: JSON.parse(JSON.stringify(state.effects)),
    } : {}),
  };
  const stack = [...state.undoStack.slice(-49), entry];
  _lastUndoPush = { entry, prevUndo: state.undoStack, prevRedo: state.redoStack };
  _coalesceKey = coalesceKey ?? null;
  _coalescedEntry = coalesceKey ? entry : null;
  useEditorStore.setState({ undoStack: stack, redoStack: [] });
}

/** The lights and effects an entry restores, with the light selection kept only on lights that still exist. */
function restoredLights(cur: EditorState, entry: HistoryEntry): Partial<EditorState> {
  if (!entry.flashEvents) return {};
  const alive = (b: number) => entry.flashEvents!.some(e => Math.abs(e.time_beats - b) < 0.01);
  const sel = cur.selectedFlashBeats.filter(alive);
  return {
    flashEvents: entry.flashEvents,
    ...(entry.effects ? { effects: entry.effects } : {}),
    selectedFlashBeats: sel,
    editingFlashBeat: cur.editingFlashBeat != null && alive(cur.editingFlashBeat) ? cur.editingFlashBeat : (sel[sel.length - 1] ?? null),
  };
}

/** A light or effect edit's undo entry: Synth VR only, the other views never had one. */
function pushLightUndo(state: EditorState, label: string, coalesceKey?: string) {
  if (isSynthVrView(state.activeView)) pushUndo(state, label, false, coalesceKey);
}

/** Take back the undo entry the refused edit just pushed, if it is still the newest. */
function undoLastPush() {
  const last = _lastUndoPush;
  _lastUndoPush = null;
  if (!last) return;
  const cur = useEditorStore.getState();
  if (cur.undoStack[cur.undoStack.length - 1] !== last.entry) return;
  useEditorStore.setState({ undoStack: last.prevUndo, redoStack: last.prevRedo });
}

// Told when an edit is refused for putting a wrong combination of notes on one beat, so the scene can
// say why. Set by the scene; the store only calls it.
let _noteRuleHandler: ((message: string) => void) | null = null;
let _noteRuleLast = { message: '', at: 0 };
export function setNoteRuleRejectionHandler(fn: ((message: string) => void) | null): void {
  _noteRuleHandler = fn ? (message => {
    // A drag is refused on every frame it stays on a bad beat; say it once a second, not sixty times.
    const now = Date.now();
    if (message === _noteRuleLast.message && now - _noteRuleLast.at < 1000) return;
    _noteRuleLast = { message, at: now };
    fn(message);
  }) : null;
}

/** Synth VR: nothing is placed in the first two seconds of the track. */
export const MIN_PLACEMENT_MS = 2000;
export const PLACEMENT_TOO_EARLY = 'Nothing can be placed before the first 2 seconds of the track';
/** True (and the scene is told why) when a Synth VR placement at `timeMs` falls inside the first two
 *  seconds. A millisecond of slack lets a beat that lands on 2 s through despite float error. */
function _tooEarlyToPlace(state: EditorState, timeMs: number): boolean {
  if (!isSynthVrView(state.activeView) || timeMs >= MIN_PLACEMENT_MS - 1) return false;
  _noteRuleHandler?.(PLACEMENT_TOO_EARLY);
  return true;
}

// ── Square and Triangle walls ───────────────────────────────────────────────
// Walls like any other, but the format stores them in their own `squares` / `triangles` collections
// rather than in `slides`, so they have their own add instead of addSlide (the hotkeys outside Synth VR
// use it; Synth VR places through toggleWallAtPlayhead).

const SQUARE_TRIANGLE_KEY = { square: 'squares', triangle: 'triangles' } as const;

/** Append a square or triangle at the format's centre — as the desktop editor's wall buttons place
 *  a new wall — keeping the collection sorted by time. One undo entry, `Add square`/`Add triangle`. */
function _addSquareTriangle(kind: 'square' | 'triangle', timeMs: number, beat: number) {
  const state = useEditorStore.getState();
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  if (!state.beatmap || !diff) return;
  if (_tooEarlyToPlace(state, timeMs)) return;
  const ob: Obstacle = {
    time_ms: timeMs, time_seconds: timeMs / 1000, time_beats: beat,
    position: newWallPosition(timeMs), z_rotation: 0, initialized: true,
  };
  const key = SQUARE_TRIANGLE_KEY[kind];
  const arr = [...(diff[key] ?? []), ob].sort((a, b) => a.time_ms - b.time_ms);
  pushUndo(state, `Add ${kind}`);
  useEditorStore.setState({
    beatmap: {
      ...state.beatmap,
      difficulties: { ...state.beatmap.difficulties, [state.activeDifficulty]: { ...diff, [key]: arr } },
    },
  });
}

// Design-wall undo history (separate from note history). Snapshots are just the
// previous `designShapes` array (immutably replaced on every edit). Continuous
// edits to the SAME group within a short window coalesce into one step so a
// slider drag is a single undo — `force` bypasses coalescing (add/remove/paste).
let _designUndoKey = '';
let _designUndoTs = 0;
function pushDesignUndo(state: EditorState, force: boolean, key = '') {
  const now = Date.now();
  if (!force && key && key === _designUndoKey && now - _designUndoTs < 700) {
    _designUndoTs = now;
    return;
  }
  _designUndoKey = force ? '' : key;
  _designUndoTs = now;
  const stack = [...state.designUndoStack.slice(-49), state.designShapes];
  useEditorStore.setState({ designUndoStack: stack, designRedoStack: [] });
}

function setDiffNotes(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
  notes: Note[]
): boolean {
  const state = get();
  const { beatmap, activeDifficulty } = state;
  if (!beatmap) return false;
  const diff = beatmap.difficulties[activeDifficulty];
  if (!diff) return false;

  // The scene windows notes with a binary search over time_beats, so an array
  // that is out of time order silently drops notes outside the mounted window —
  // they turn invisible AND unclickable (so they can't be deleted either).
  // Callers that only append already sort; the ones that CHANGE a note's time
  // (updateNote / moveSelectedNotes / rail-node time edits) did not. Enforce it
  // at the single choke point instead, so no future caller can regress it.
  let out = notes;
  let indexFix: Partial<EditorState> | null = null;
  for (let i = 1; i < notes.length; i++) {
    if (notes[i].time_ms < notes[i - 1].time_ms) {
      // Selection, hover and rail placement all address notes by ARRAY INDEX,
      // so re-sorting has to carry those indices to the notes' new positions.
      out = [...notes].sort((a, b) => a.time_ms - b.time_ms);
      const newIndexOf = new Map<Note, number>();
      for (let j = 0; j < out.length; j++) newIndexOf.set(out[j], j);
      const remap = (idx: number | null): number | null => {
        if (idx === null || idx < 0 || idx >= notes.length) return idx;
        const next = newIndexOf.get(notes[idx]);
        return next === undefined ? idx : next;
      };
      const rail = state.selectedRailNode;
      indexFix = {
        selectedNoteIds: state.selectedNoteIds
          .map(remap)
          .filter((i): i is number => i !== null),
        selectedRailNode: rail
          ? { ...rail, noteIndex: remap(rail.noteIndex) ?? rail.noteIndex }
          : null,
        railPlacementNoteIndex: remap(state.railPlacementNoteIndex),
        hoveredNoteIndex: remap(state.hoveredNoteIndex),
      };
      break;
    }
  }

  // Synth VR: a Special is alone on its beat, a Left or Right shares it only with the opposite hand, and
  // never more than two notes (SRVR-0337). The whole edit is refused, with its undo entry, if the notes it
  // would leave break that on a beat that was not already broken.
  if (isSynthVrView(state.activeView)) {
    const problem = newBeatProblem(diff.notes, out);
    if (problem) {
      undoLastPush();
      _noteRuleHandler?.(problem);
      return false;
    }
  }

  set(() => ({
    ...indexFix,
    beatmap: {
      ...beatmap,
      difficulties: {
        ...beatmap.difficulties,
        [activeDifficulty]: {
          ...diff,
          notes: out,
          note_count: out.length,
          rail_count: out.filter(n => n.has_rail).length,
          duration_beats: out.length > 0 ? Math.max(...out.map(n => n.time_beats)) : 0,
          duration_seconds: out.length > 0 ? Math.max(...out.map(n => n.time_seconds)) : 0,
        },
      },
    },
  }));
  return true;
}

function setDiffSlides(
  set: (fn: (s: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
  slides: Slide[]
) {
  const { beatmap, activeDifficulty } = get();
  if (!beatmap) return;
  const diff = beatmap.difficulties[activeDifficulty];
  if (!diff) return;
  set(() => ({
    beatmap: {
      ...beatmap,
      difficulties: {
        ...beatmap.difficulties,
        [activeDifficulty]: { ...diff, slides, slide_count: slides.length },
      },
    },
  }));
}

// ── An area range selects what it holds (SRVR-0315 AC12–AC14) ─────────────────
// Whenever the range in force changes (live while the area key is held), the Selection Options change,
// or the active difficulty's notes or walls change, the note and slide-wall selection becomes the
// range's notes and slide walls that the options let through — as the box selector would select them.
// Written with setState, not selectNotes/selectSlides: those clear the range (AC10's click half).
// `_rangeSelection` is the selection the range last wrote, so clearing the range clears only that, and
// never a selection the mapper made by clicking (which clears the range in the same update).
let _rangeSelection: { notes: number[]; slides: number[] } | null = null;
const _sameRange = (a: AreaRange | null, b: AreaRange | null) =>
  a === b || (!!a && !!b && a.startBeat === b.startBeat && a.endBeat === b.endBeat);
const _sameIds = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

// Track.cs ends the colour-change session the moment the playhead's beat differs from the one it began on,
// including a move away and back, which is how an area is selected: Z held while the playhead travels.
useEditorStore.subscribe((state, prev) => {
  if (_colorSession && state.playback.currentBeat !== prev.playback.currentBeat) _colorSession = null;
});

useEditorStore.subscribe((state, prev) => {
  const range = effectiveAreaRange(state.areaRange, state.areaGesture, state.playback.currentBeat);
  const prevRange = effectiveAreaRange(prev.areaRange, prev.areaGesture, prev.playback.currentBeat);
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  const prevDiff = prev.beatmap?.difficulties[prev.activeDifficulty];
  const changed = !_sameRange(range, prevRange) || state.areaFilters !== prev.areaFilters
    || (!!range && (diff?.notes !== prevDiff?.notes || diff?.slides !== prevDiff?.slides
      // Crouches, squares and triangles join a range in Synth VR only: a range left set in another view
      // must not start re-selecting on a crouch edit there.
      || (isSynthVrView(state.activeView) && (diff?.crouches !== prevDiff?.crouches || diff?.squares !== prevDiff?.squares || diff?.triangles !== prevDiff?.triangles))));
  if (!changed) return;
  if (!range || !diff || !state.beatmap) {
    const mine = _rangeSelection
      && state.selectedNoteIds === _rangeSelection.notes && state.selectedSlideIds === _rangeSelection.slides;
    _rangeSelection = null;
    const walls = state.selectedCrouches.length || state.selectedSquares.length || state.selectedTriangles.length;
    if (mine && (state.selectedNoteIds.length || state.selectedSlideIds.length)) {
      useEditorStore.setState({ selectedNoteIds: [], selectedSlideIds: [], selectedCrouches: [], selectedSquares: [], selectedTriangles: [] });
    } else if (walls) {
      useEditorStore.setState({ selectedCrouches: [], selectedSquares: [], selectedTriangles: [] });
    }
    return;
  }
  const bpm = state.beatmap.bpm || 120;
  const c = objectsInRange(diff, range, bpm, state.areaFilters);
  // A crouch is identified by its time, as everywhere else (objectDrag.ts).
  const crouchTimes = c.crouches.map(i => diff.crouches[i]);
  const sameWalls = _sameIds(c.squares, state.selectedSquares) && _sameIds(c.triangles, state.selectedTriangles)
    && _sameIds(crouchTimes, state.selectedCrouches);
  if (_sameIds(c.notes, state.selectedNoteIds) && _sameIds(c.slides, state.selectedSlideIds)) {
    _rangeSelection = { notes: state.selectedNoteIds, slides: state.selectedSlideIds };
    if (sameWalls) return;
    useEditorStore.setState({ selectedCrouches: crouchTimes, selectedSquares: c.squares, selectedTriangles: c.triangles });
    return;
  }
  _rangeSelection = { notes: c.notes, slides: c.slides };
  useEditorStore.setState({
    selectedNoteIds: c.notes, selectedSlideIds: c.slides,
    selectedCrouches: crouchTimes, selectedSquares: c.squares, selectedTriangles: c.triangles,
  });
});

// ── Persist preferences to localStorage on change ────────────────────────────

useEditorStore.subscribe((state, prevState) => {
  if (
    state.cameraView !== prevState.cameraView ||
    state.playbackSpeed !== prevState.playbackSpeed ||
    state.scrollStep !== prevState.scrollStep ||
    state.snapDivision !== prevState.snapDivision ||
    state.timelineZoom !== prevState.timelineZoom ||
    state.noteJumpSpeed !== prevState.noteJumpSpeed ||
    state.mapLocked !== prevState.mapLocked ||
    state.musicVolume !== prevState.musicVolume ||
    state.sfxVolume !== prevState.sfxVolume ||
    state.activeDifficulty !== prevState.activeDifficulty ||
    state.leftSidebarOpen !== prevState.leftSidebarOpen ||
    state.leftSidebarWidth !== prevState.leftSidebarWidth ||
    state.rightSidebarOpen !== prevState.rightSidebarOpen ||
    state.rightSidebarWidth !== prevState.rightSidebarWidth ||
    state.bottomPanelOpen !== prevState.bottomPanelOpen ||
    state.bottomPanelHeight !== prevState.bottomPanelHeight ||
    state.showHumanFigure !== prevState.showHumanFigure ||
    state.figureMode !== prevState.figureMode ||
    state.renderMode !== prevState.renderMode ||
    state.heatmapEnabled !== prevState.heatmapEnabled ||
    state.gameRailRendering !== prevState.gameRailRendering ||
    state.patternDifficultyVisible !== prevState.patternDifficultyVisible ||
    state.fovPanelOpen !== prevState.fovPanelOpen ||
    state.activeView !== prevState.activeView ||
    state.customCameraPosition !== prevState.customCameraPosition ||
    state.customCameraRotation !== prevState.customCameraRotation ||
    state.vrCams !== prevState.vrCams ||
    state.defaultCameraPosition !== prevState.defaultCameraPosition ||
    state.defaultCameraRotation !== prevState.defaultCameraRotation ||
    state.leftCameraPosition !== prevState.leftCameraPosition ||
    state.leftCameraRotation !== prevState.leftCameraRotation ||
    state.rightCameraPosition !== prevState.rightCameraPosition ||
    state.rightCameraRotation !== prevState.rightCameraRotation ||
    state.mannequinSettings !== prevState.mannequinSettings ||
    state.gameplaySettings !== prevState.gameplaySettings ||
    state.interfaceSettings !== prevState.interfaceSettings ||
    state.particleSettings !== prevState.particleSettings ||
    state.performanceSettings !== prevState.performanceSettings ||
    state.lastBeatmapId !== prevState.lastBeatmapId ||
    state.lastBeatmapIdByEditor !== prevState.lastBeatmapIdByEditor ||
    state.gridVisible !== prevState.gridVisible ||
    state.gridSize !== prevState.gridSize ||
    state.snapToGrid !== prevState.snapToGrid ||
    state.railSnapping !== prevState.railSnapping ||
    state.gridGuideType !== prevState.gridGuideType ||
    state.gridGuideVisible !== prevState.gridGuideVisible ||
    state.highlightLevel !== prevState.highlightLevel ||
    state.gridWidth !== prevState.gridWidth ||
    state.gridHeight !== prevState.gridHeight ||
    state.gridDivisionsH !== prevState.gridDivisionsH ||
    state.gridDivisionsV !== prevState.gridDivisionsV ||
    state.gridColor !== prevState.gridColor ||
    state.gridOpacity !== prevState.gridOpacity ||
    state.notesGridYOffset !== prevState.notesGridYOffset ||
    state.minimapCam !== prevState.minimapCam ||
    state.minimapZoom !== prevState.minimapZoom ||
    // Save the scrub position at most ~once per second so a refresh returns to
    // roughly where you were editing.
    Math.floor(state.playback.currentTime) !== Math.floor(prevState.playback.currentTime)
  ) {
    savePrefs({
      cameraView: state.cameraView,
      minimapCam: state.minimapCam,
      minimapZoom: state.minimapZoom,
      lastTime: state.playback.currentTime,
      playbackSpeed: state.playbackSpeed,
      scrollStep: state.scrollStep,
      snapDivision: state.snapDivision,
      timelineZoom: state.timelineZoom,
      noteJumpSpeed: state.noteJumpSpeed,
      mapLocked: state.mapLocked,
      musicVolume: state.musicVolume,
      sfxVolume: state.sfxVolume,
      activeDifficulty: state.activeDifficulty,
      leftSidebarOpen: state.leftSidebarOpen,
      leftSidebarWidth: state.leftSidebarWidth,
      rightSidebarOpen: state.rightSidebarOpen,
      rightSidebarWidth: state.rightSidebarWidth,
      bottomPanelOpen: state.bottomPanelOpen,
      bottomPanelHeight: state.bottomPanelHeight,
      showHumanFigure: state.showHumanFigure,
      figureMode: state.figureMode,
      renderMode: state.renderMode,
      heatmapEnabled: state.heatmapEnabled,
      gameRailRendering: state.gameRailRendering,
      patternDifficultyVisible: state.patternDifficultyVisible,
      fovPanelOpen: state.fovPanelOpen,
      activeView: state.activeView,
      customCameraPosition: state.customCameraPosition ?? undefined,
      customCameraRotation: state.customCameraRotation ?? undefined,
      vrCams: state.vrCams,
      defaultCameraPosition: state.defaultCameraPosition ?? undefined,
      defaultCameraRotation: state.defaultCameraRotation ?? undefined,
      leftCameraPosition: state.leftCameraPosition ?? undefined,
      leftCameraRotation: state.leftCameraRotation ?? undefined,
      rightCameraPosition: state.rightCameraPosition ?? undefined,
      rightCameraRotation: state.rightCameraRotation ?? undefined,
      mannequinSettings: state.mannequinSettings,
      gameplaySettings: state.gameplaySettings,
      interfaceSettings: state.interfaceSettings,
      particleSettings: state.particleSettings,
      performanceSettings: state.performanceSettings,
      lastBeatmapId: state.lastBeatmapId ?? undefined,
      lastBeatmapIdByEditor: state.lastBeatmapIdByEditor,
      gridVisible: state.gridVisible,
      gridSize: state.gridSize,
      snapToGrid: state.snapToGrid,
      railSnapping: state.railSnapping,
      gridGuideType: state.gridGuideType,
      gridGuideVisible: state.gridGuideVisible,
      highlightLevel: state.highlightLevel,
      gridWidth: state.gridWidth,
      gridHeight: state.gridHeight,
      gridDivisionsH: state.gridDivisionsH,
      gridDivisionsV: state.gridDivisionsV,
      gridColor: state.gridColor,
      gridOpacity: state.gridOpacity,
      notesGridYOffset: state.notesGridYOffset,
      gridGeometryVersion: GRID_GEOMETRY_VERSION,
    });
  }
});

// ── Autosave edited notes/difficulties to a per-map draft ─────────────────────
// Note edits are otherwise in-memory only, so a page refresh (which reloads the
// backend original) would discard them. Debounced so a burst of edits doesn't
// thrash localStorage. Only fires on a real edit to the *same* map (a load or
// map-switch changes the beatmap id — we flush the previous map's pending draft
// then, but never treat the freshly-loaded difficulties as a new edit).
let _diffDraftTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingDiffDraft: { id: string; difficulties: Record<string, Difficulty> } | null = null;
function flushDiffDraft() {
  if (_diffDraftTimer) { clearTimeout(_diffDraftTimer); _diffDraftTimer = null; }
  if (_pendingDiffDraft) {
    saveDifficultyDraft(_pendingDiffDraft.id, _pendingDiffDraft.difficulties);
    _pendingDiffDraft = null;
  }
}
useEditorStore.subscribe((state, prevState) => {
  const bm = state.beatmap;
  const prevBm = prevState.beatmap;
  // Map load / switch / close: commit any pending edits for the previous map,
  // and don't record the just-loaded difficulties as an edit.
  if (bm?.id !== prevBm?.id) { flushDiffDraft(); return; }
  if (!bm || bm.difficulties === prevBm?.difficulties) return;
  _pendingDiffDraft = { id: bm.id, difficulties: bm.difficulties };
  if (_diffDraftTimer) clearTimeout(_diffDraftTimer);
  _diffDraftTimer = setTimeout(flushDiffDraft, 500);
});
// Flush synchronously on refresh / tab close so the latest edits aren't lost to
// the 500ms debounce (localStorage writes are synchronous).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushDiffDraft);
  window.addEventListener('pagehide', flushDiffDraft);
}

// Console debug handle, same family as __flowStore/__synapseScene/__synapseBudget: lets a bug
// report be reproduced against the real store from devtools, and lets the end-to-end harness set
// up and assert editor state without driving twenty clicks first.
if (typeof window !== 'undefined') {
  (window as unknown as { __editorStore?: unknown }).__editorStore = useEditorStore;
}

// A Tools box preview belongs to the beat, difficulty and map it was opened on, and to its own undo
// entry. Moving the playhead or changing difficulty or map puts it back (the desktop's Rail Smoothing
// cancels on a beat change); any other edit, or an undo, pushes past its entry and simply ends it,
// keeping what is drawn (SRVR-0354 AC6).
useEditorStore.subscribe((state) => {
  const session = _toolSession;
  if (!session) return;
  const moved = state.playback.currentBeat !== session.beat
    || state.activeDifficulty !== session.diffName
    || state.beatmap?.id !== session.beatmapId;
  if (moved) { state.cancelToolPreview(); return; }
  if (session.entry && state.undoStack[state.undoStack.length - 1] !== session.entry) {
    _toolSession = null;
    useEditorStore.setState({ toolPreview: null });
  }
});
